import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Highlight, themes } from 'prism-react-renderer';
import { Copy, Sun, Moon, Menu, X } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import Cookies from 'js-cookie';
import { supabase } from './supabaseClient';
import {
    COMPANIES_API_ITEMS,
    PIPELINES_API_ITEMS,
    COMPANIES_API_PATHS,
    PIPELINES_API_PATHS,
    buildCompaniesPipelinesPageContent,
    getCompaniesPipelinesMethodPill,
    getCompaniesPipelinesUrlSlug,
    getCompaniesPipelinesPageFromUrl,
} from './apiDocs/companiesAndPipelinesApiDocs';

// Available themes:
// themes.vsDark - VS Code Dark (current)
// themes.vsLight - VS Code Light
// themes.dracula - Dracula theme
// themes.duotoneDark - Duotone Dark
// themes.duotoneLight - Duotone Light
// themes.github - GitHub theme
// themes.nightOwl - Night Owl theme
// themes.nightOwlLight - Night Owl Light
// themes.oceanicNext - Oceanic Next
// themes.palenight - Palenight
// themes.shadesOfPurple - Shades of Purple
// themes.ultramin - Ultramin

// Clean theme system using CSS variables
const applyTheme = (isDark) => {
    if (isDark) {
        document.body.classList.remove('light-mode');
    } else {
        document.body.classList.add('light-mode');
    }
};

// API base URL for documentation
const getApiBaseUrl = () => {
    return 'https://www.ninjapipe.app/api';
};

// Get API path for each endpoint
const getApiPathForPage = (pageName) => {
    const paths = {
        'List Contacts': '/contacts',
        'Create Contact': '/contacts',
        'Get Contact': '/contacts/{contact_id}',
        'Update Contact': '/contacts/{contact_id}',
        'Delete Contact': '/contacts/{contact_id}',
        'List Contact Activities': '/contacts/{contact_id}/activities',
        'Create Contact Activity': '/contacts/{contact_id}/activities',
        'Get Contact Activity': '/contacts/{contact_id}/activities/{activity_id}',
        'Update Contact Activity': '/contacts/{contact_id}/activities/{activity_id}',
        'Delete Contact Activity': '/contacts/{contact_id}/activities/{activity_id}',
        'Enable Client Portal': '/client-portal-accounts/{contact_id}/enable',
        'Disable Client Portal': '/client-portal-accounts/{contact_id}/disable',
        'List Deals': '/deals',
        'Create Deal': '/deals',
        'Get Deal': '/deals/{deal_id}',
        'Update Deal': '/deals/{deal_id}',
        'Delete Deal': '/deals/{deal_id}',
        'List Products': '/products',
        'Create Product': '/products',
        'Get Product': '/products/{product_id}',
        'Update Product': '/products/{product_id}',
        'Delete Product': '/products/{product_id}',
        'List Budgets': '/budgets',
        'Create Budget': '/budgets',
        'Get Budget': '/budgets/{budget_id}',
        'Update Budget': '/budgets/{budget_id}',
        'Delete Budget': '/budgets/{budget_id}',
        'List Budget Expenses': '/budgets/{budget_id}/expenses',
        'Create Budget Expense': '/budgets/{budget_id}/expenses',
        'Get Budget Expense': '/budgets/{budget_id}/expenses/{expense_id}',
        'Update Budget Expense': '/budgets/{budget_id}/expenses/{expense_id}',
        'Delete Budget Expense': '/budgets/{budget_id}/expenses/{expense_id}',
        'List Lists': '/lists',
        'Create List': '/lists',
        'Get List': '/lists/{list_id}',
        'Update List': '/lists/{list_id}',
        'Delete List': '/lists/{list_id}',
        'Get List Members': '/lists/{list_id}/members',
        'Update List Member': '/lists/{list_id}/members/{member_id}',
        'Remove List Member': '/lists/{list_id}/members/{member_id}',
        'Add to List': '/lists/{list_id}/members',
        'List Projects': '/projects',
        'Create Project': '/projects',
        'Get Project': '/projects/{project_id}',
        'Update Project': '/projects/{project_id}',
        'Delete Project': '/projects/{project_id}',
        'List General Tasks': '/tasks',
        'Create General Task': '/tasks',
        'Get General Task': '/tasks/{task_id}',
        'Update General Task': '/tasks/{task_id}',
        'Delete General Task': '/tasks/{task_id}',
        'Reorder General Tasks': '/tasks/reorder',
        'List Tasks': '/projects/{project_id}/tasks',
        'Create Task': '/projects/{project_id}/tasks',
        'Get Task': '/projects/{project_id}/tasks/{task_id}',
        'Update Task': '/projects/{project_id}/tasks/{task_id}',
        'Delete Task': '/projects/{project_id}/tasks/{task_id}',
        'Reorder Tasks': '/projects/{project_id}/tasks/reorder',
        'List Orders': '/orders',
        'Create Order': '/orders',
        'Get Order': '/orders/{order_id}',
        'Update Order': '/orders/{order_id}',
        'Delete Order': '/orders/{order_id}',
        ...COMPANIES_API_PATHS,
        ...PIPELINES_API_PATHS,
    };
    return paths[pageName] || '';
};

const rowBase = {
    margin: '2px 8px',
    borderRadius: '8px',
    fontSize: '15px',
    lineHeight: 1.8,
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease',
};

const ApiDocsPage = () => {
    const { section, page } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const [selectedItem, setSelectedItem] = useState(null);
    const [contactsExpanded, setContactsExpanded] = useState(true);
    const [workspaceExpanded, setWorkspaceExpanded] = useState(false);
    const [dealsExpanded, setDealsExpanded] = useState(false);
    const [productsExpanded, setProductsExpanded] = useState(false);
    const [budgetsExpanded, setBudgetsExpanded] = useState(false);
    const [projectsExpanded, setProjectsExpanded] = useState(false);
    const [ordersExpanded, setOrdersExpanded] = useState(false);
    const [listsExpanded, setListsExpanded] = useState(false);
    const [companiesExpanded, setCompaniesExpanded] = useState(false);
    const [pipelinesExpanded, setPipelinesExpanded] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [user, setUser] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [account, setAccount] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [userAvatar, setUserAvatar] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [userName, setUserName] = useState('User');
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef(null);
    const [showShortcutAnimation, setShowShortcutAnimation] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => {
        // Initialize theme from localStorage or default to dark mode
        const savedTheme = localStorage.getItem('ninjapipe-theme');
        if (savedTheme === 'light') return false;
        if (savedTheme === 'dark') return true;
        return true; // Default to dark mode
    });
    const [isToggling, setIsToggling] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const mainContentRef = useRef(null);
    const activeSidebarItemRef = useRef(null);
    const lastScrolledPathRef = useRef(null);

    const closeSidebar = useCallback(() => setSidebarOpen(false), []);

    const toggleTheme = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isToggling) return; // Prevent double clicks
        
        setIsToggling(true);
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        
        // Save theme preference to localStorage
        localStorage.setItem('ninjapipe-theme', newDarkMode ? 'dark' : 'light');
        
        // Apply theme using CSS variables
        applyTheme(newDarkMode);
        
        // Reset toggling state after animation completes
        setTimeout(() => {
            setIsToggling(false);
        }, 300);
    };

    // Initialize theme on mount
    useEffect(() => {
        applyTheme(isDarkMode);
    }, [isDarkMode]);

    // Scroll main content to top when navigating to another doc page
    useEffect(() => {
        const el = mainContentRef.current;
        if (el) el.scrollTo(0, 0);
    }, [location.pathname]);


    // Load user data and workspace - matching App.js pattern
    useEffect(() => {
        const loadUserData = async () => {
            try {
                const userCookie = Cookies.get('sb-user');
                if (userCookie) {
                    const userData = JSON.parse(userCookie);
                    setUser(userData);
                    
                    // Fetch account data from database (like App.js does)
                    const { data: accountData } = await supabase
                        .from('accounts')
                        .select('*')
                        .eq('id', userData.id)
                        .single();
                    
                    if (accountData) {
                        setAccount(accountData);
                        
                        // Extract user name and avatar from account data
                        const firstName = accountData.first_name || userData?.user_metadata?.first_name || userData?.user_metadata?.full_name || userData?.email?.split('@')[0] || 'User';
                        const lastName = accountData.last_name || '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        
                        setUserName(fullName || firstName);
                        setUserAvatar(accountData.picture_url || userData?.user_metadata?.picture_url || null);
                    } else {
                        // Fallback to cookie data if no account found
                        const firstName = userData?.user_metadata?.first_name || userData?.user_metadata?.full_name?.split(' ')[0] || userData?.email?.split('@')[0] || 'User';
                        setUserName(firstName);
                        setUserAvatar(userData?.user_metadata?.picture_url || null);
                    }
                }
            } catch (error) {
                console.error('Error loading user data:', error);
            }
        };

        loadUserData();
    }, []);


    // Handle URL changes and update selected item
    useEffect(() => {
        const pageFromUrl = getPageFromUrl(section, page);
        setSelectedItem(pageFromUrl);
        
        // Expand workspace section if we're on a workspace page
        if (section === 'workspace') {
            setWorkspaceExpanded(true);
        }
        
        // Expand contacts section if we're on a contacts page
        if (section === 'contacts') {
            setContactsExpanded(true);
        }
        
        // Expand deals section if we're on a deals page
        if (section === 'deals') {
            setDealsExpanded(true);
        }
        
        // Expand products section if we're on a products page
        if (section === 'products') {
            setProductsExpanded(true);
        }
        
        // Expand budgets section if we're on a budgets page
        if (section === 'budgets') {
            setBudgetsExpanded(true);
        }

        if (section === 'projects') {
            setProjectsExpanded(true);
        }

        if (section === 'orders') {
            setOrdersExpanded(true);
        }

        if (section === 'lists') {
            setListsExpanded(true);
        }

        if (section === 'companies') {
            setCompaniesExpanded(true);
        }

        if (section === 'pipelines') {
            setPipelinesExpanded(true);
        }
        
        // Redirect to default page if no specific page is selected
        if (!section && !page && location.pathname === '/api-docs') {
            navigate('/api-docs/introduction/authentication', { replace: true });
        }
    }, [section, page, location.pathname, navigate]);

    // Scroll sidebar to bring active item into view only once per page (on load/navigation), not when user expands other sections
    useEffect(() => {
        if (!selectedItem) return;
        const pathKey = location.pathname;
        if (lastScrolledPathRef.current === pathKey) return;
        lastScrolledPathRef.current = pathKey;
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                activeSidebarItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            });
        });
        return () => cancelAnimationFrame(id);
    }, [selectedItem, location.pathname, contactsExpanded, dealsExpanded, productsExpanded, projectsExpanded, ordersExpanded, budgetsExpanded, listsExpanded, companiesExpanded, pipelinesExpanded]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportDropdownOpen && !event.target.closest('[data-export-dropdown]')) {
                setExportDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [exportDropdownOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') setSidebarOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Focus search when "/" is pressed
            if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey) {
                // Don't trigger if user is typing in an input field
                if (event.target.tagName !== 'INPUT' && event.target.tagName !== 'TEXTAREA') {
                    event.preventDefault();
                    searchInputRef.current?.focus();
                    
                    // Trigger animation
                    setShowShortcutAnimation(true);
                    setTimeout(() => setShowShortcutAnimation(false), 600);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const introItems = [
        'Introduction',
        'Quick Start',
        'Authentication',
        'Pagination'
    ];

    const contactsItems = [
        'List Contacts',
        'Create Contact', 
        'Get Contact',
        'Update Contact',
        'Delete Contact',
        'List Contact Activities',
        'Create Contact Activity',
        'Get Contact Activity',
        'Update Contact Activity',
        'Delete Contact Activity',
        'Enable Client Portal',
        'Disable Client Portal'
    ];

    const workspaceItems = ['List Workspace Members'];

    const dealsItems = [
        'List Deals',
        'Create Deal',
        'Get Deal',
        'Update Deal',
        'Delete Deal'
    ];

    const productsItems = [
        'List Products',
        'Create Product',
        'Get Product',
        'Update Product',
        'Delete Product'
    ];

    const projectsItems = [
        'List Projects',
        'Create Project',
        'Get Project',
        'Update Project',
        'Delete Project',
        'List General Tasks',
        'Create General Task',
        'Get General Task',
        'Update General Task',
        'Delete General Task',
        'Reorder General Tasks',
        'List Tasks',
        'Create Task',
        'Get Task',
        'Update Task',
        'Delete Task',
        'Reorder Tasks'
    ];

    const ordersItems = [
        'List Orders',
        'Create Order',
        'Get Order',
        'Update Order',
        'Delete Order'
    ];

    const budgetsItems = [
        'List Budgets',
        'Create Budget',
        'Get Budget',
        'Update Budget',
        'Delete Budget',
        'List Budget Expenses',
        'Create Budget Expense',
        'Get Budget Expense',
        'Update Budget Expense',
        'Delete Budget Expense'
    ];

    const listsItems = [
        'List Lists',
        'Create List',
        'Get List',
        'Update List',
        'Delete List',
        'Get List Members',
        'Add to List',
        'Update List Member',
        'Remove List Member'
    ];

    const companiesItems = COMPANIES_API_ITEMS;
    const pipelinesItems = PIPELINES_API_ITEMS;

    // URL mapping functions
    const getPageFromUrl = (section, page) => {
        if (!section && !page) return 'Authentication'; // Default page
        
        if (section === 'introduction') {
            if (page === 'quick-start') return 'Quick Start';
            if (page === 'authentication') return 'Authentication';
            if (page === 'pagination') return 'Pagination';
            return 'Introduction';
        }
        
        if (section === 'workspace') {
            if (page === 'list-members') return 'List Workspace Members';
            return 'List Workspace Members';
        }
        
        if (section === 'contacts') {
            if (page === 'list-contacts') return 'List Contacts';
            if (page === 'create-contact') return 'Create Contact';
            if (page === 'get-contact') return 'Get Contact';
            if (page === 'update-contact') return 'Update Contact';
            if (page === 'delete-contact') return 'Delete Contact';
            if (page === 'list-contact-activities') return 'List Contact Activities';
            if (page === 'create-contact-activity') return 'Create Contact Activity';
            if (page === 'get-contact-activity') return 'Get Contact Activity';
            if (page === 'update-contact-activity') return 'Update Contact Activity';
            if (page === 'delete-contact-activity') return 'Delete Contact Activity';
            if (page === 'enable-client-portal') return 'Enable Client Portal';
            if (page === 'disable-client-portal') return 'Disable Client Portal';
            return 'List Contacts'; // Default for contacts section
        }
        
        if (section === 'deals') {
            if (page === 'list-deals') return 'List Deals';
            if (page === 'create-deal') return 'Create Deal';
            if (page === 'get-deal') return 'Get Deal';
            if (page === 'update-deal') return 'Update Deal';
            if (page === 'delete-deal') return 'Delete Deal';
            return 'List Deals'; // Default for deals section
        }
        
        if (section === 'products') {
            if (page === 'list-products') return 'List Products';
            if (page === 'create-product') return 'Create Product';
            if (page === 'get-product') return 'Get Product';
            if (page === 'update-product') return 'Update Product';
            if (page === 'delete-product') return 'Delete Product';
            return 'List Products'; // Default for products section
        }
        
        if (section === 'projects') {
            if (page === 'list-projects') return 'List Projects';
            if (page === 'create-project') return 'Create Project';
            if (page === 'get-project') return 'Get Project';
            if (page === 'update-project') return 'Update Project';
            if (page === 'delete-project') return 'Delete Project';
            if (page === 'list-general-tasks') return 'List General Tasks';
            if (page === 'create-general-task') return 'Create General Task';
            if (page === 'get-general-task') return 'Get General Task';
            if (page === 'update-general-task') return 'Update General Task';
            if (page === 'delete-general-task') return 'Delete General Task';
            if (page === 'reorder-general-tasks') return 'Reorder General Tasks';
            if (page === 'list-tasks') return 'List Tasks';
            if (page === 'create-task') return 'Create Task';
            if (page === 'get-task') return 'Get Task';
            if (page === 'update-task') return 'Update Task';
            if (page === 'delete-task') return 'Delete Task';
            if (page === 'reorder-tasks') return 'Reorder Tasks';
            return 'List Projects';
        }

        if (section === 'orders') {
            if (page === 'list-orders') return 'List Orders';
            if (page === 'create-order') return 'Create Order';
            if (page === 'get-order') return 'Get Order';
            if (page === 'update-order') return 'Update Order';
            if (page === 'delete-order') return 'Delete Order';
            return 'List Orders';
        }

        if (section === 'budgets') {
            if (page === 'list-budgets') return 'List Budgets';
            if (page === 'create-budget') return 'Create Budget';
            if (page === 'get-budget') return 'Get Budget';
            if (page === 'update-budget') return 'Update Budget';
            if (page === 'delete-budget') return 'Delete Budget';
            if (page === 'list-budget-expenses') return 'List Budget Expenses';
            if (page === 'create-budget-expense') return 'Create Budget Expense';
            if (page === 'get-budget-expense') return 'Get Budget Expense';
            if (page === 'update-budget-expense') return 'Update Budget Expense';
            if (page === 'delete-budget-expense') return 'Delete Budget Expense';
            return 'List Budgets'; // Default for budgets section
        }

        if (section === 'lists') {
            if (page === 'list-lists') return 'List Lists';
            if (page === 'create-list') return 'Create List';
            if (page === 'get-list') return 'Get List';
            if (page === 'update-list') return 'Update List';
            if (page === 'delete-list') return 'Delete List';
            if (page === 'get-list-members') return 'Get List Members';
            if (page === 'add-to-list') return 'Add to List';
            if (page === 'update-list-member') return 'Update List Member';
            if (page === 'remove-list-member') return 'Remove List Member';
            return 'List Lists';
        }

        const companiesPipelinesPage = getCompaniesPipelinesPageFromUrl(section, page);
        if (companiesPipelinesPage) return companiesPipelinesPage;
        
        return 'Authentication'; // Fallback
    };

    const getUrlFromPage = useCallback((pageName) => {
        if (pageName === 'Introduction') return '/api-docs/introduction';
        if (pageName === 'Quick Start') return '/api-docs/introduction/quick-start';
        if (pageName === 'Authentication') return '/api-docs/introduction/authentication';
        if (pageName === 'Pagination') return '/api-docs/introduction/pagination';
        if (pageName === 'List Workspace Members') return '/api-docs/workspace/list-members';
        if (pageName === 'List Contacts') return '/api-docs/contacts/list-contacts';
        if (pageName === 'Create Contact') return '/api-docs/contacts/create-contact';
        if (pageName === 'Get Contact') return '/api-docs/contacts/get-contact';
        if (pageName === 'Update Contact') return '/api-docs/contacts/update-contact';
        if (pageName === 'Delete Contact') return '/api-docs/contacts/delete-contact';
        if (pageName === 'List Contact Activities') return '/api-docs/contacts/list-contact-activities';
        if (pageName === 'Create Contact Activity') return '/api-docs/contacts/create-contact-activity';
        if (pageName === 'Get Contact Activity') return '/api-docs/contacts/get-contact-activity';
        if (pageName === 'Update Contact Activity') return '/api-docs/contacts/update-contact-activity';
        if (pageName === 'Delete Contact Activity') return '/api-docs/contacts/delete-contact-activity';
        if (pageName === 'Enable Client Portal') return '/api-docs/contacts/enable-client-portal';
        if (pageName === 'Disable Client Portal') return '/api-docs/contacts/disable-client-portal';
        if (pageName === 'List Deals') return '/api-docs/deals/list-deals';
        if (pageName === 'Create Deal') return '/api-docs/deals/create-deal';
        if (pageName === 'Get Deal') return '/api-docs/deals/get-deal';
        if (pageName === 'Update Deal') return '/api-docs/deals/update-deal';
        if (pageName === 'Delete Deal') return '/api-docs/deals/delete-deal';
        if (pageName === 'List Products') return '/api-docs/products/list-products';
        if (pageName === 'Create Product') return '/api-docs/products/create-product';
        if (pageName === 'Get Product') return '/api-docs/products/get-product';
        if (pageName === 'Update Product') return '/api-docs/products/update-product';
        if (pageName === 'Delete Product') return '/api-docs/products/delete-product';
        if (pageName === 'List Projects') return '/api-docs/projects/list-projects';
        if (pageName === 'Create Project') return '/api-docs/projects/create-project';
        if (pageName === 'Get Project') return '/api-docs/projects/get-project';
        if (pageName === 'Update Project') return '/api-docs/projects/update-project';
        if (pageName === 'Delete Project') return '/api-docs/projects/delete-project';
        if (pageName === 'List General Tasks') return '/api-docs/projects/list-general-tasks';
        if (pageName === 'Create General Task') return '/api-docs/projects/create-general-task';
        if (pageName === 'Get General Task') return '/api-docs/projects/get-general-task';
        if (pageName === 'Update General Task') return '/api-docs/projects/update-general-task';
        if (pageName === 'Delete General Task') return '/api-docs/projects/delete-general-task';
        if (pageName === 'Reorder General Tasks') return '/api-docs/projects/reorder-general-tasks';
        if (pageName === 'List Tasks') return '/api-docs/projects/list-tasks';
        if (pageName === 'Create Task') return '/api-docs/projects/create-task';
        if (pageName === 'Get Task') return '/api-docs/projects/get-task';
        if (pageName === 'Update Task') return '/api-docs/projects/update-task';
        if (pageName === 'Delete Task') return '/api-docs/projects/delete-task';
        if (pageName === 'Reorder Tasks') return '/api-docs/projects/reorder-tasks';
        if (pageName === 'List Orders') return '/api-docs/orders/list-orders';
        if (pageName === 'Create Order') return '/api-docs/orders/create-order';
        if (pageName === 'Get Order') return '/api-docs/orders/get-order';
        if (pageName === 'Update Order') return '/api-docs/orders/update-order';
        if (pageName === 'Delete Order') return '/api-docs/orders/delete-order';
        if (pageName === 'List Budgets') return '/api-docs/budgets/list-budgets';
        if (pageName === 'Create Budget') return '/api-docs/budgets/create-budget';
        if (pageName === 'Get Budget') return '/api-docs/budgets/get-budget';
        if (pageName === 'Update Budget') return '/api-docs/budgets/update-budget';
        if (pageName === 'Delete Budget') return '/api-docs/budgets/delete-budget';
        if (pageName === 'List Budget Expenses') return '/api-docs/budgets/list-budget-expenses';
        if (pageName === 'Create Budget Expense') return '/api-docs/budgets/create-budget-expense';
        if (pageName === 'Get Budget Expense') return '/api-docs/budgets/get-budget-expense';
        if (pageName === 'Update Budget Expense') return '/api-docs/budgets/update-budget-expense';
        if (pageName === 'Delete Budget Expense') return '/api-docs/budgets/delete-budget-expense';
        if (pageName === 'List Lists') return '/api-docs/lists/list-lists';
        if (pageName === 'Create List') return '/api-docs/lists/create-list';
        if (pageName === 'Get List') return '/api-docs/lists/get-list';
        if (pageName === 'Update List') return '/api-docs/lists/update-list';
        if (pageName === 'Delete List') return '/api-docs/lists/delete-list';
        if (pageName === 'Get List Members') return '/api-docs/lists/get-list-members';
        if (pageName === 'Add to List') return '/api-docs/lists/add-to-list';
        if (pageName === 'Update List Member') return '/api-docs/lists/update-list-member';
        if (pageName === 'Remove List Member') return '/api-docs/lists/remove-list-member';
        const companiesPipelinesUrl = getCompaniesPipelinesUrlSlug(pageName);
        if (companiesPipelinesUrl) return companiesPipelinesUrl;
        return '/api-docs/introduction/authentication'; // Fallback
    }, []);

    const navigateToPage = (pageName) => {
        const url = getUrlFromPage(pageName);
        navigate(url);
        setSelectedItem(pageName);
        setSidebarOpen(false);
    };

    // Generate SEO metadata for each page
    const getPageMetadata = useCallback((pageName) => {
        const baseUrl = 'https://docs.ninjapipe.app';
        const currentUrl = `${baseUrl}${getUrlFromPage(pageName)}`;
        
        const metadata = {
            'Introduction': {
                title: 'API Documentation - Complete Integration Guide | NinjaPipe API',
                description: 'Master the NinjaPipe CRM API with our comprehensive documentation. Learn authentication, endpoints, and build powerful CRM integrations. Complete guide for developers.',
                keywords: 'NinjaPipe CRM API, CRM integration, API documentation, customer relationship management, business automation, developer guide, REST API, CRM software, business tools, API integration'
            },
            'Quick Start': {
                title: 'API Quick Start - Get Started in 5 Minutes | NinjaPipe API',
                description: 'Get your NinjaPipe API integration running in minutes. Step-by-step guide: create API key, make your first request, and start building.',
                keywords: 'NinjaPipe API quick start, API getting started, first API call, API key setup, NinjaPipe integration tutorial'
            },
            'Authentication': {
                title: 'API Authentication - Secure API Key Setup | NinjaPipe API',
                description: 'Secure your NinjaPipe CRM API integration with proper authentication. Complete guide to API key setup, Bearer tokens, and security best practices for CRM developers.',
                keywords: 'NinjaPipe CRM API authentication, API key setup, Bearer token, CRM security, API authentication, developer authentication, secure API access, CRM integration security, business API security'
            },
            'Pagination': {
                title: 'API Pagination - Handle Large CRM Datasets | NinjaPipe API',
                description: 'Optimize your NinjaPipe CRM API calls with efficient pagination. Learn to handle large contact lists, deal pipelines, and product catalogs with our comprehensive pagination guide.',
                keywords: 'NinjaPipe CRM API pagination, large CRM datasets, contact pagination, deal pagination, CRM data retrieval, API pagination, business data management, CRM performance optimization'
            },
            'List Workspace Members': {
                title: 'List Workspace Members API - Team Member UUIDs | NinjaPipe API',
                description: 'List active workspace members and account UUIDs for contact assigned_to, deal owners, and CRM integrations.',
                keywords: 'NinjaPipe workspace members API, team members, account UUID, assigned_to, CRM API integration'
            },
            'List Contacts': {
                title: 'List Contacts API - Retrieve Customer Database | NinjaPipe API',
                description: 'Access your complete customer database with the NinjaPipe CRM List Contacts API. Advanced filtering, sorting, and pagination for large contact lists. Perfect for CRM integrations.',
                keywords: 'NinjaPipe CRM list contacts API, customer database, contact management, CRM contacts, customer data retrieval, business contact API, CRM integration, customer relationship management, contact filtering, business contacts'
            },
            'Create Contact': {
                title: 'Create Contact API - Add New Customers | NinjaPipe API',
                description: 'Add new customers to your CRM with the NinjaPipe Create Contact API. Complete guide with examples for customer onboarding, lead capture, and CRM data entry automation.',
                keywords: 'NinjaPipe CRM create contact API, add customer, new contact, customer onboarding, lead capture, CRM data entry, business contact creation, customer management, CRM automation, contact import'
            },
            'Get Contact': {
                title: 'Get Contact API - Retrieve Customer Details | NinjaPipe API',
                description: 'Retrieve detailed customer information with the NinjaPipe Get Contact API. Perfect for customer lookup, profile management, and CRM integrations.',
                keywords: 'NinjaPipe CRM get contact API, customer details, contact lookup, customer profile, CRM contact retrieval, business contact API, customer information, contact management, CRM data access'
            },
            'Update Contact': {
                title: 'Update Contact API - Modify Customer Information | NinjaPipe API',
                description: 'Update customer information in your CRM with the NinjaPipe Update Contact API. Complete guide for customer data management, profile updates, and CRM maintenance.',
                keywords: 'NinjaPipe CRM update contact API, modify customer, edit contact, customer data update, CRM contact management, business contact editing, customer profile update, CRM data modification, contact maintenance'
            },
            'Delete Contact': {
                title: 'Delete Contact API - Remove Customer Records | NinjaPipe API',
                description: 'Remove customer records from your CRM with the NinjaPipe Delete Contact API. Complete guide for data cleanup, GDPR compliance, and CRM maintenance.',
                keywords: 'NinjaPipe CRM delete contact API, remove customer, delete contact, customer data removal, CRM contact deletion, business contact management, GDPR compliance, CRM data cleanup, contact maintenance'
            },
            'Enable Client Portal': {
                title: 'Enable Client Portal API - Activate Client Portal Access | NinjaPipe API',
                description: 'Enable client portal access for contacts with the NinjaPipe Enable Client Portal API. Create or activate client portal accounts for customer self-service access.',
                keywords: 'NinjaPipe CRM enable client portal API, activate client portal, customer portal access, client portal management, CRM client portal, customer self-service, portal account creation, client access management'
            },
            'Disable Client Portal': {
                title: 'Disable Client Portal API - Deactivate Client Portal Access | NinjaPipe API',
                description: 'Disable client portal access for contacts with the NinjaPipe Disable Client Portal API. Deactivate customer portal accounts when client access is no longer needed.',
                keywords: 'NinjaPipe CRM disable client portal API, deactivate client portal, customer portal access, client portal management, CRM client portal, customer self-service, portal account deactivation, client access management'
            },
            'List Contact Activities': {
                title: 'List Contact Activities API - Contact Timeline | NinjaPipe API',
                description: 'List notes and tasks on a contact timeline via the NinjaPipe API. Filter by type, paginate results, workspace-scoped.',
                keywords: 'NinjaPipe contact activities API, contact timeline, CRM notes API, tasks on contact, CRM API'
            },
            'Create Contact Activity': {
                title: 'Create Contact Activity API - Notes and Tasks | NinjaPipe API',
                description: 'Create notes or tasks on a contact timeline. Tasks link to project tasks and stay in sync with the tasks API.',
                keywords: 'NinjaPipe create contact activity, contact note API, contact task API, CRM timeline'
            },
            'Get Contact Activity': {
                title: 'Get Contact Activity API - Single Timeline Item | NinjaPipe API',
                description: 'Retrieve one contact activity by ID for notes or tasks on the contact timeline.',
                keywords: 'NinjaPipe get contact activity, contact timeline item, CRM API'
            },
            'Update Contact Activity': {
                title: 'Update Contact Activity API - Notes and Tasks | NinjaPipe API',
                description: 'Update notes or task-linked activities. Task updates propagate to the underlying task row.',
                keywords: 'NinjaPipe update contact activity, patch contact note, patch contact task, CRM API'
            },
            'Delete Contact Activity': {
                title: 'Delete Contact Activity API - Remove Timeline Items | NinjaPipe API',
                description: 'Delete contact timeline activities. Task activities remove the linked task when applicable.',
                keywords: 'NinjaPipe delete contact activity, remove contact note, remove contact task, CRM API'
            },
            'List Deals': {
                title: 'List Deals API - Access Sales Pipeline | NinjaPipe API',
                description: 'Access your complete sales pipeline with the NinjaPipe CRM List Deals API. Advanced filtering, sorting, and pagination for deal management. Perfect for sales CRM integrations.',
                keywords: 'NinjaPipe CRM list deals API, sales pipeline, deal management, CRM deals, sales opportunities, business deals, sales tracking, CRM sales data, deal filtering, sales automation'
            },
            'Create Deal': {
                title: 'Create Deal API - Add Sales Opportunities | NinjaPipe API',
                description: 'Create new sales opportunities with the NinjaPipe CRM Create Deal API. Complete guide for sales pipeline management, lead conversion, and CRM deal tracking.',
                keywords: 'NinjaPipe CRM create deal API, add sales opportunity, new deal, sales pipeline, lead conversion, CRM deal creation, business sales, sales tracking, deal management, sales automation'
            },
            'Get Deal': {
                title: 'Get Deal API - Retrieve Sales Opportunity | NinjaPipe API',
                description: 'Retrieve detailed sales opportunity information with the NinjaPipe Get Deal API. Perfect for deal lookup, sales tracking, and CRM integrations.',
                keywords: 'NinjaPipe CRM get deal API, sales opportunity details, deal lookup, sales tracking, CRM deal retrieval, business deal API, sales information, deal management, CRM data access, sales pipeline'
            },
            'Update Deal': {
                title: 'Update Deal API - Modify Sales Opportunities | NinjaPipe API',
                description: 'Update sales opportunities in your CRM with the NinjaPipe Update Deal API. Complete guide for deal progression, sales tracking, and CRM maintenance.',
                keywords: 'NinjaPipe CRM update deal API, modify sales opportunity, edit deal, sales progression, CRM deal management, business deal editing, sales tracking, deal updates, CRM data modification'
            },
            'Delete Deal': {
                title: 'Delete Deal API - Remove Sales Opportunities | NinjaPipe API',
                description: 'Remove sales opportunities from your CRM with the NinjaPipe Delete Deal API. Complete guide for deal cleanup, sales pipeline management, and CRM maintenance.',
                keywords: 'NinjaPipe CRM delete deal API, remove sales opportunity, delete deal, sales pipeline cleanup, CRM deal deletion, business deal management, sales data cleanup, deal maintenance'
            },
            'List Products': {
                title: 'List Products API - Access Product Catalog | NinjaPipe API',
                description: 'Access your complete product catalog with the NinjaPipe CRM List Products API. Advanced filtering, sorting, and pagination for product management. Perfect for e-commerce CRM integrations.',
                keywords: 'NinjaPipe CRM list products API, product catalog, product management, CRM products, business products, product inventory, e-commerce CRM, product data, catalog management, business automation'
            },
            'Create Product': {
                title: 'Create Product API - Add New Products | NinjaPipe API',
                description: 'Add new products to your CRM catalog with the NinjaPipe Create Product API. Complete guide for product onboarding, inventory management, and e-commerce CRM integration.',
                keywords: 'NinjaPipe CRM create product API, add product, new product, product catalog, inventory management, e-commerce CRM, product onboarding, business product creation, catalog automation, product data entry'
            },
            'Get Product': {
                title: 'Get Product API - Retrieve Product Details | NinjaPipe API',
                description: 'Retrieve detailed product information with the NinjaPipe Get Product API. Perfect for product lookup, catalog management, and e-commerce CRM integrations.',
                keywords: 'NinjaPipe CRM get product API, product details, product lookup, product catalog, CRM product retrieval, business product API, product information, catalog management, e-commerce data access'
            },
            'Update Product': {
                title: 'Update Product API - Modify Product Information | NinjaPipe API',
                description: 'Update product information in your CRM with the NinjaPipe Update Product API. Complete guide for product management, catalog updates, and e-commerce CRM maintenance.',
                keywords: 'NinjaPipe CRM update product API, modify product, edit product, product catalog update, CRM product management, business product editing, catalog maintenance, product data modification'
            },
            'Delete Product': {
                title: 'Delete Product API - Remove Products | NinjaPipe API',
                description: 'Remove products from your CRM catalog with the NinjaPipe Delete Product API. Complete guide for product cleanup, catalog management, and e-commerce CRM maintenance.',
                keywords: 'NinjaPipe CRM delete product API, remove product, delete product, product catalog cleanup, CRM product deletion, business product management, catalog maintenance, product data cleanup'
            },
            'List Budgets': {
                title: 'List Budgets API - Access Financial Planning | NinjaPipe API',
                description: 'Access your complete budget management system with the NinjaPipe CRM List Budgets API. Advanced filtering, sorting, and pagination for financial planning. Perfect for business CRM integrations.',
                keywords: 'NinjaPipe CRM list budgets API, budget management, financial planning, CRM budgets, business budgets, budget tracking, financial CRM, budget data, financial automation, business planning'
            },
            'Create Budget': {
                title: 'Create Budget API - Add Financial Plans | NinjaPipe API',
                description: 'Create new budgets and financial plans with the NinjaPipe CRM Create Budget API. Complete guide for financial planning, budget management, and business CRM integration.',
                keywords: 'NinjaPipe CRM create budget API, add budget, financial planning, budget creation, financial planning, business budget management, budget automation, financial CRM, business planning'
            },
            'Get Budget': {
                title: 'Get Budget API - Retrieve Financial Plan | NinjaPipe API',
                description: 'Retrieve detailed budget information with the NinjaPipe Get Budget API. Perfect for financial planning, budget tracking, and business CRM integrations.',
                keywords: 'NinjaPipe CRM get budget API, budget details, financial plan lookup, budget tracking, CRM budget retrieval, business budget API, financial information, budget management, financial data access'
            },
            'Update Budget': {
                title: 'Update Budget API - Modify Financial Plans | NinjaPipe API',
                description: 'Update budget information in your CRM with the NinjaPipe Update Budget API. Complete guide for financial planning, budget management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM update budget API, modify budget, edit budget, financial plan update, CRM budget management, business budget editing, financial tracking, budget updates, financial data modification'
            },
            'Delete Budget': {
                title: 'Delete Budget API - Remove Financial Plans | NinjaPipe API',
                description: 'Remove budgets from your CRM with the NinjaPipe Delete Budget API. Complete guide for financial cleanup, budget management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM delete budget API, remove budget, delete budget, financial plan cleanup, CRM budget deletion, business budget management, financial data cleanup, budget maintenance'
            },
            'List Budget Expenses': {
                title: 'List Budget Expenses API - Track Financial Spending | NinjaPipe API',
                description: 'Track and manage budget expenses with the NinjaPipe CRM List Budget Expenses API. Advanced filtering, sorting, and pagination for expense management. Perfect for financial CRM integrations.',
                keywords: 'NinjaPipe CRM list budget expenses API, expense tracking, budget expenses, financial spending, expense management, budget tracking, financial CRM, expense data, business expenses, financial automation'
            },
            'Create Budget Expense': {
                title: 'Create Budget Expense API - Add Financial Expenses | NinjaPipe API',
                description: 'Add new budget expenses with the NinjaPipe CRM Create Budget Expense API. Complete guide for expense tracking, financial planning, and business CRM integration.',
                keywords: 'NinjaPipe CRM create budget expense API, add expense, expense tracking, budget expense creation, financial planning, business expense management, expense automation, financial CRM, business spending'
            },
            'Get Budget Expense': {
                title: 'Get Budget Expense API - Retrieve Expense Details | NinjaPipe API',
                description: 'Retrieve detailed budget expense information with the NinjaPipe Get Budget Expense API. Perfect for expense lookup, financial tracking, and business CRM integrations.',
                keywords: 'NinjaPipe CRM get budget expense API, expense details, expense lookup, budget expense retrieval, financial tracking, business expense API, expense information, financial data access, expense management'
            },
            'Update Budget Expense': {
                title: 'Update Budget Expense API - Modify Financial Expenses | NinjaPipe API',
                description: 'Update budget expenses in your CRM with the NinjaPipe Update Budget Expense API. Complete guide for expense management, financial tracking, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM update budget expense API, modify expense, edit expense, budget expense update, financial tracking, business expense management, expense updates, financial data modification, expense maintenance'
            },
            'Delete Budget Expense': {
                title: 'Delete Budget Expense API - Remove Financial Expenses | NinjaPipe API',
                description: 'Remove budget expenses from your CRM with the NinjaPipe Delete Budget Expense API. Complete guide for expense cleanup, financial management, and business CRM maintenance.',
                keywords: 'NinjaPipe CRM delete budget expense API, remove expense, delete expense, budget expense cleanup, financial expense deletion, business expense management, expense data cleanup, financial maintenance'
            },
            'List Lists': {
                title: 'List Lists API - CRM Lists | NinjaPipe API',
                description: 'List workspace CRM lists (Contact or Company) with pagination, search, and filters. Requires lists:read (session) or API key read.',
                keywords: 'NinjaPipe API lists, CRM lists, segmentation, list_members'
            },
            'Create List': {
                title: 'Create List API - CRM Lists | NinjaPipe API',
                description: 'Create a Contact or Company list in your workspace.',
                keywords: 'NinjaPipe API create list, CRM lists REST'
            },
            'Get List': {
                title: 'Get List API - CRM Lists | NinjaPipe API',
                description: 'Retrieve one list by ID including member_count.',
                keywords: 'NinjaPipe API get list'
            },
            'Update List': {
                title: 'Update List API - CRM Lists | NinjaPipe API',
                description: 'Update list fields. Changing list_type is blocked while the list has members.',
                keywords: 'NinjaPipe API update list'
            },
            'Delete List': {
                title: 'Delete List API - CRM Lists | NinjaPipe API',
                description: 'Delete a list; list_members rows cascade.',
                keywords: 'NinjaPipe API delete list'
            },
            'Get List Members': {
                title: 'Get List Members API - CRM Lists | NinjaPipe API',
                description: 'Paginate list members; use expand=true to include contact or company summaries.',
                keywords: 'NinjaPipe API list members, list_members'
            },
            'Update List Member': {
                title: 'Update List Member API - CRM Lists | NinjaPipe API',
                description: 'PATCH a list member (e.g. is_important).',
                keywords: 'NinjaPipe API list member patch'
            },
            'Remove List Member': {
                title: 'Remove List Member API - CRM Lists | NinjaPipe API',
                description: 'Remove a contact or company from a list by list_member id.',
                keywords: 'NinjaPipe API remove list member'
            },
            'Add to List': {
                title: 'Add to List API - Contacts & Companies | NinjaPipe API',
                description: 'Add a contact or company to a CRM list via the NinjaPipe REST API. Same behavior as the Add to List workflow action. Requires write permission.',
                keywords: 'NinjaPipe API add to list, CRM list members, contact list API, company list API, list_members, segmentation'
            },
            'List Projects': {
                title: 'List Projects API - Projects & Tasks | NinjaPipe API',
                description: 'List workspace projects with pagination, search, and filters. Aligns with the Projects & Tasks feature and workspace tasks permissions.',
                keywords: 'NinjaPipe API projects, list projects, task management API, workspace projects REST'
            },
            'Create Project': {
                title: 'Create Project API - Projects & Tasks | NinjaPipe API',
                description: 'Create a project in your workspace via the NinjaPipe REST API.',
                keywords: 'NinjaPipe API create project, projects REST, task management'
            },
            'Get Project': {
                title: 'Get Project API - Projects & Tasks | NinjaPipe API',
                description: 'Retrieve a single project by ID.',
                keywords: 'NinjaPipe API get project, project by id'
            },
            'Update Project': {
                title: 'Update Project API - Projects & Tasks | NinjaPipe API',
                description: 'Update project fields including optional settings_json (e.g. contact link).',
                keywords: 'NinjaPipe API update project, project settings_json'
            },
            'Delete Project': {
                title: 'Delete Project API - Projects & Tasks | NinjaPipe API',
                description: 'Delete a project and its tasks (cascade).',
                keywords: 'NinjaPipe API delete project'
            },
            'List General Tasks': {
                title: 'List General Tasks API - Workspace Tasks | NinjaPipe API',
                description: 'List workspace-level general tasks (project_id is null). Same fields and filters as project tasks.',
                keywords: 'NinjaPipe API general tasks, list tasks, workspace tasks REST'
            },
            'Create General Task': {
                title: 'Create General Task API - Workspace Tasks | NinjaPipe API',
                description: 'Create a workspace-level general task not tied to a project.',
                keywords: 'NinjaPipe API create general task, workspace tasks REST'
            },
            'Get General Task': {
                title: 'Get General Task API - Workspace Tasks | NinjaPipe API',
                description: 'Retrieve a single general task by ID.',
                keywords: 'NinjaPipe API get general task, workspace tasks REST'
            },
            'Update General Task': {
                title: 'Update General Task API - Workspace Tasks | NinjaPipe API',
                description: 'Update a general task. Parent must be another general task in the same workspace.',
                keywords: 'NinjaPipe API update general task, workspace tasks REST'
            },
            'Delete General Task': {
                title: 'Delete General Task API - Workspace Tasks | NinjaPipe API',
                description: 'Delete a general task; child tasks cascade per database rules.',
                keywords: 'NinjaPipe API delete general task, workspace tasks REST'
            },
            'Reorder General Tasks': {
                title: 'Reorder General Tasks API - Workspace Tasks | NinjaPipe API',
                description: 'Batch update order_index for sibling general tasks.',
                keywords: 'NinjaPipe API reorder general tasks, order_index batch'
            },
            'List Tasks': {
                title: 'List Tasks API - Projects & Tasks | NinjaPipe API',
                description: 'List tasks for a project (flat list with parent_id for hierarchy). For workspace-level tasks, use List General Tasks.',
                keywords: 'NinjaPipe API list tasks, project tasks REST'
            },
            'Create Task': {
                title: 'Create Task API - Projects & Tasks | NinjaPipe API',
                description: 'Create a task or subtask in a project.',
                keywords: 'NinjaPipe API create task, subtask API'
            },
            'Get Task': {
                title: 'Get Task API - Projects & Tasks | NinjaPipe API',
                description: 'Retrieve a single task in a project.',
                keywords: 'NinjaPipe API get task'
            },
            'Update Task': {
                title: 'Update Task API - Projects & Tasks | NinjaPipe API',
                description: 'Update task fields; invalid parent cycles return 409.',
                keywords: 'NinjaPipe API update task, TASK_PARENT_CYCLE'
            },
            'Delete Task': {
                title: 'Delete Task API - Projects & Tasks | NinjaPipe API',
                description: 'Delete a task; child tasks cascade per database rules.',
                keywords: 'NinjaPipe API delete task'
            },
            'Reorder Tasks': {
                title: 'Reorder Tasks API - Projects & Tasks | NinjaPipe API',
                description: 'Batch update order_index for sibling tasks in a project.',
                keywords: 'NinjaPipe API reorder tasks, order_index batch'
            },
            'List Orders': {
                title: 'List Orders API - Orders | NinjaPipe API',
                description: 'Paginated list of orders in the workspace (same data as the Orders page).',
                keywords: 'NinjaPipe API orders, list orders REST'
            },
            'Create Order': {
                title: 'Create Order API - Orders | NinjaPipe API',
                description: 'Create an order with customer fields, line items in settings_json, optional contact_id.',
                keywords: 'NinjaPipe API create order'
            },
            'Get Order': {
                title: 'Get Order API - Orders | NinjaPipe API',
                description: 'Retrieve one order by UUID.',
                keywords: 'NinjaPipe API get order'
            },
            'Update Order': {
                title: 'Update Order API - Orders | NinjaPipe API',
                description: 'Update order fields including settings_json (items, currency, custom fields).',
                keywords: 'NinjaPipe API update order'
            },
            'Delete Order': {
                title: 'Delete Order API - Orders | NinjaPipe API',
                description: 'Delete an order from the workspace.',
                keywords: 'NinjaPipe API delete order'
            }
        };

        const pageMeta = metadata[pageName] || {
            title: 'API Documentation - Complete Business Integration Guide | NinjaPipe API',
            description: 'Master the NinjaPipe CRM API with comprehensive documentation for customer relationship management, sales pipeline, product catalog, and financial planning. Complete developer guide for business automation.',
            keywords: 'NinjaPipe CRM API, CRM integration, customer relationship management, business automation, sales pipeline, product catalog, financial planning, business tools, API documentation, developer guide, REST API, business CRM, enterprise software'
        };

        return {
            title: pageMeta.title,
            description: pageMeta.description,
            keywords: pageMeta.keywords,
            url: currentUrl,
            canonical: currentUrl
        };
    }, [getUrlFromPage]);


    // Search functionality - filter navigation items
    const handleSearch = (query) => {
        setSearchQuery(query);
        
        // Auto-expand sections that have matching results
        if (query.trim()) {
            const lowerQuery = query.toLowerCase();
            
            // Check if each section has matching results
            const hasWorkspaceResults = (workspaceItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            const hasContactsResults = (contactsItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            const hasDealsResults = (dealsItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            const hasProductsResults = (productsItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            const hasBudgetsResults = (budgetsItems || []).some(item => 
                item.toLowerCase().includes(lowerQuery)
            );
            const hasProjectsResults = (projectsItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            const hasOrdersResults = (ordersItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            const hasListsResults = (listsItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            const hasCompaniesResults = (companiesItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            const hasPipelinesResults = (pipelinesItems || []).some(item =>
                item.toLowerCase().includes(lowerQuery)
            );
            
            if (hasWorkspaceResults) setWorkspaceExpanded(true);
            if (hasContactsResults) setContactsExpanded(true);
            if (hasDealsResults) setDealsExpanded(true);
            if (hasProductsResults) setProductsExpanded(true);
            if (hasProjectsResults) setProjectsExpanded(true);
            if (hasOrdersResults) setOrdersExpanded(true);
            if (hasBudgetsResults) setBudgetsExpanded(true);
            if (hasListsResults) setListsExpanded(true);
            if (hasCompaniesResults) setCompaniesExpanded(true);
            if (hasPipelinesResults) setPipelinesExpanded(true);
        }
    };

    // Generate Postman collection
    const generatePostmanCollection = () => {
        const baseUrl = getApiBaseUrl();
        const apiKey = '[YOUR_API_KEY]';

        const collection = {
            info: {
                name: `NinjaPipe API - ${'NinjaPipe API'}`,
                description: 'Complete NinjaPipe API collection with all endpoints',
                schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
                version: '1.0.0'
            },
            auth: {
                type: 'bearer',
                bearer: [
                    {
                        key: 'token',
                        value: apiKey,
                        type: 'string'
                    }
                ]
            },
            variable: [
                {
                    key: 'baseUrl',
                    value: baseUrl,
                    type: 'string'
                },
                {
                    key: 'apiKey',
                    value: apiKey,
                    type: 'string'
                }
            ],
            item: [
                // Introduction & Authentication
                {
                    name: 'Introduction',
                    item: [
                        {
                            name: 'Authentication',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/contacts?page=1&limit=1',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '1' }
                                    ]
                                },
                                description: 'Test your API key authentication'
                            }
                        }
                    ]
                },
                // Workspace
                {
                    name: 'Workspace',
                    item: [
                        {
                            name: 'List Workspace Members',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/workspace/members',
                                    host: ['{{baseUrl}}'],
                                    path: ['workspace', 'members']
                                }
                            }
                        }
                    ]
                },
                // Contacts
                {
                    name: 'Contacts',
                    item: [
                        {
                            name: 'List Contacts',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/contacts?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Contact',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        first_name: 'John',
                                        last_name: 'Doe',
                                        email: 'john.doe@example.com',
                                        phone: '+1234567890',
                                        company: 'Example Corp'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/contacts',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts']
                                }
                            }
                        },
                        {
                            name: 'Get Contact',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/contacts/:contactId',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts', ':contactId'],
                                    variable: [
                                        {
                                            key: 'contactId',
                                            value: 'CONTACT_ID_HERE',
                                            description: 'The contact ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Contact',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        first_name: 'Jane',
                                        last_name: 'Doe',
                                        email: 'jane.doe@example.com'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/contacts/:contactId',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts', ':contactId'],
                                    variable: [
                                        {
                                            key: 'contactId',
                                            value: 'CONTACT_ID_HERE',
                                            description: 'The contact ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Contact',
                            request: {
                                method: 'DELETE',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/contacts/:contactId',
                                    host: ['{{baseUrl}}'],
                                    path: ['contacts', ':contactId'],
                                    variable: [
                                        {
                                            key: 'contactId',
                                            value: 'CONTACT_ID_HERE',
                                            description: 'The contact ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Enable Client Portal',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        email: 'client@example.com',
                                        auto_generate_password: true
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/client-portal-accounts/:contactId/enable',
                                    host: ['{{baseUrl}}'],
                                    path: ['client-portal-accounts', ':contactId', 'enable'],
                                    variable: [
                                        {
                                            key: 'contactId',
                                            value: 'CONTACT_ID_HERE',
                                            description: 'The contact ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Disable Client Portal',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/client-portal-accounts/:contactId/disable',
                                    host: ['{{baseUrl}}'],
                                    path: ['client-portal-accounts', ':contactId', 'disable'],
                                    variable: [
                                        {
                                            key: 'contactId',
                                            value: 'CONTACT_ID_HERE',
                                            description: 'The contact ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        }
                    ]
                },
                // Deals
                {
                    name: 'Deals',
                    item: [
                        {
                            name: 'List Deals',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/deals?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['deals'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Deal',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'New Deal',
                                        amount: 50000,
                                        stage: 'Prospecting',
                                        close_date: '2024-12-31',
                                        contact_id: 'CONTACT_ID_HERE'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/deals',
                                    host: ['{{baseUrl}}'],
                                    path: ['deals']
                                }
                            }
                        },
                        {
                            name: 'Get Deal',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/deals/:dealId',
                                    host: ['{{baseUrl}}'],
                                    path: ['deals', ':dealId'],
                                    variable: [
                                        {
                                            key: 'dealId',
                                            value: 'DEAL_ID_HERE',
                                            description: 'The deal ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Deal',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'Updated Deal',
                                        amount: 75000,
                                        stage: 'Negotiation'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/deals/:dealId',
                                    host: ['{{baseUrl}}'],
                                    path: ['deals', ':dealId'],
                                    variable: [
                                        {
                                            key: 'dealId',
                                            value: 'DEAL_ID_HERE',
                                            description: 'The deal ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Deal',
                            request: {
                                method: 'DELETE',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/deals/:dealId',
                                    host: ['{{baseUrl}}'],
                                    path: ['deals', ':dealId'],
                                    variable: [
                                        {
                                            key: 'dealId',
                                            value: 'DEAL_ID_HERE',
                                            description: 'The deal ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        }
                    ]
                },
                // Products
                {
                    name: 'Products',
                    item: [
                        {
                            name: 'List Products',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/products?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['products'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Product',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'New Product',
                                        description: 'Product description',
                                        price: 99.99,
                                        category: 'Software',
                                        sku: 'PROD-001'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/products',
                                    host: ['{{baseUrl}}'],
                                    path: ['products']
                                }
                            }
                        },
                        {
                            name: 'Get Product',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/products/:productId',
                                    host: ['{{baseUrl}}'],
                                    path: ['products', ':productId'],
                                    variable: [
                                        {
                                            key: 'productId',
                                            value: 'PRODUCT_ID_HERE',
                                            description: 'The product ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Product',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'Updated Product',
                                        price: 149.99,
                                        description: 'Updated description'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/products/:productId',
                                    host: ['{{baseUrl}}'],
                                    path: ['products', ':productId'],
                                    variable: [
                                        {
                                            key: 'productId',
                                            value: 'PRODUCT_ID_HERE',
                                            description: 'The product ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Product',
                            request: {
                                method: 'DELETE',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/products/:productId',
                                    host: ['{{baseUrl}}'],
                                    path: ['products', ':productId'],
                                    variable: [
                                        {
                                            key: 'productId',
                                            value: 'PRODUCT_ID_HERE',
                                            description: 'The product ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        }
                    ]
                },
                // Projects & Tasks
                {
                    name: 'Projects & Tasks',
                    item: [
                        {
                            name: 'List Projects',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/projects?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['projects'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Project',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ name: 'New project', status: 'Planning', priority: 'Medium' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/projects',
                                    host: ['{{baseUrl}}'],
                                    path: ['projects']
                                }
                            }
                        },
                        {
                            name: 'List Tasks',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/projects/:projectId/tasks',
                                    host: ['{{baseUrl}}'],
                                    path: ['projects', ':projectId', 'tasks'],
                                    variable: [
                                        { key: 'projectId', value: 'PROJECT_ID_HERE', description: 'Project UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Task',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ title: 'New task', status: 'To Do', priority: 'Medium' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/projects/:projectId/tasks',
                                    host: ['{{baseUrl}}'],
                                    path: ['projects', ':projectId', 'tasks'],
                                    variable: [
                                        { key: 'projectId', value: 'PROJECT_ID_HERE', description: 'Project UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Reorder Tasks',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        updates: [
                                            { id: 'TASK_ID_1', order_index: 0 },
                                            { id: 'TASK_ID_2', order_index: 10 }
                                        ]
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/projects/:projectId/tasks/reorder',
                                    host: ['{{baseUrl}}'],
                                    path: ['projects', ':projectId', 'tasks', 'reorder'],
                                    variable: [
                                        { key: 'projectId', value: 'PROJECT_ID_HERE', description: 'Project UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'List General Tasks',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/tasks?sort_by=order_index&sort_order=asc',
                                    host: ['{{baseUrl}}'],
                                    path: ['tasks'],
                                    query: [
                                        { key: 'sort_by', value: 'order_index' },
                                        { key: 'sort_order', value: 'asc' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create General Task',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ title: 'General task', status: 'To Do', priority: 'Medium' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/tasks',
                                    host: ['{{baseUrl}}'],
                                    path: ['tasks']
                                }
                            }
                        },
                        {
                            name: 'Reorder General Tasks',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        updates: [
                                            { id: 'TASK_ID_1', order_index: 0 },
                                            { id: 'TASK_ID_2', order_index: 10 }
                                        ]
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/tasks/reorder',
                                    host: ['{{baseUrl}}'],
                                    path: ['tasks', 'reorder']
                                }
                            }
                        }
                    ]
                },
                // Orders
                {
                    name: 'Orders',
                    item: [
                        {
                            name: 'List Orders',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/orders?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['orders'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Order',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        customer_name: 'Acme Buyer',
                                        customer_email: 'buyer@acme.com',
                                        total_amount: 99.5,
                                        status: 'Processing',
                                        payment_status: 'Pending',
                                        settings_json: { currency: 'USD', items: [] }
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/orders',
                                    host: ['{{baseUrl}}'],
                                    path: ['orders']
                                }
                            }
                        },
                        {
                            name: 'Get Order',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/orders/:orderId',
                                    host: ['{{baseUrl}}'],
                                    path: ['orders', ':orderId'],
                                    variable: [
                                        { key: 'orderId', value: 'ORDER_ID_HERE', description: 'Order UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Order',
                            request: {
                                method: 'PUT',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ status: 'Shipped', tracking_number: 'TRK-123' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/orders/:orderId',
                                    host: ['{{baseUrl}}'],
                                    path: ['orders', ':orderId'],
                                    variable: [
                                        { key: 'orderId', value: 'ORDER_ID_HERE', description: 'Order UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Order',
                            request: {
                                method: 'DELETE',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/orders/:orderId',
                                    host: ['{{baseUrl}}'],
                                    path: ['orders', ':orderId'],
                                    variable: [
                                        { key: 'orderId', value: 'ORDER_ID_HERE', description: 'Order UUID' }
                                    ]
                                }
                            }
                        }
                    ]
                },
                // Budgets
                {
                    name: 'Budgets',
                    item: [
                        {
                            name: 'List Budgets',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Budget',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'Marketing Budget Q1 2024',
                                        description: 'Q1 marketing budget for campaigns',
                                        category: 'Marketing',
                                        period: 'Quarterly',
                                        allocated: 50000,
                                        currency: 'USD',
                                        start_date: '2024-01-01',
                                        end_date: '2024-03-31'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/budgets',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets']
                                }
                            }
                        },
                        {
                            name: 'Get Budget',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Budget',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        name: 'Updated Marketing Budget',
                                        allocated: 75000,
                                        description: 'Updated budget description'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Budget',
                            request: {
                                method: 'DELETE',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        }
                    ]
                },
                // Budget Expenses
                {
                    name: 'Budget Expenses',
                    item: [
                        {
                            name: 'List Budget Expenses',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId/expenses?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId', 'expenses'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create Budget Expense',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        description: 'Google Ads Campaign',
                                        amount: 15000,
                                        category: 'Marketing',
                                        date: '2024-01-15'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId/expenses',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId', 'expenses'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Get Budget Expense',
                            request: {
                                method: 'GET',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId/expenses/:expenseId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId', 'expenses', ':expenseId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        },
                                        {
                                            key: 'expenseId',
                                            value: 'EXPENSE_ID_HERE',
                                            description: 'The expense ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update Budget Expense',
                            request: {
                                method: 'PUT',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        description: 'Updated Google Ads Campaign',
                                        amount: 18000,
                                        category: 'Digital Marketing'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId/expenses/:expenseId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId', 'expenses', ':expenseId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        },
                                        {
                                            key: 'expenseId',
                                            value: 'EXPENSE_ID_HERE',
                                            description: 'The expense ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete Budget Expense',
                            request: {
                                method: 'DELETE',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/budgets/:budgetId/expenses/:expenseId',
                                    host: ['{{baseUrl}}'],
                                    path: ['budgets', ':budgetId', 'expenses', ':expenseId'],
                                    variable: [
                                        {
                                            key: 'budgetId',
                                            value: 'BUDGET_ID_HERE',
                                            description: 'The budget ID (UUID format)'
                                        },
                                        {
                                            key: 'expenseId',
                                            value: 'EXPENSE_ID_HERE',
                                            description: 'The expense ID (UUID format)'
                                        }
                                    ]
                                }
                            }
                        }
                    ]
                },
                {
                    name: 'Lists',
                    item: [
                        {
                            name: 'List Lists',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/lists?page=1&limit=20',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists'],
                                    query: [
                                        { key: 'page', value: '1' },
                                        { key: 'limit', value: '20' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Create List',
                            request: {
                                method: 'POST',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ name: 'My List', list_type: 'Contact', status: 'Active' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/lists',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists']
                                }
                            }
                        },
                        {
                            name: 'Get List',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/lists/:listId',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists', ':listId'],
                                    variable: [
                                        { key: 'listId', value: 'LIST_ID_HERE', description: 'List UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Update List',
                            request: {
                                method: 'PUT',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' },
                                    { key: 'Content-Type', value: 'application/json', type: 'text' }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({ status: 'Inactive' }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/lists/:listId',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists', ':listId'],
                                    variable: [
                                        { key: 'listId', value: 'LIST_ID_HERE', description: 'List UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Delete List',
                            request: {
                                method: 'DELETE',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/lists/:listId',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists', ':listId'],
                                    variable: [
                                        { key: 'listId', value: 'LIST_ID_HERE', description: 'List UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Get List Members',
                            request: {
                                method: 'GET',
                                header: [
                                    { key: 'Authorization', value: 'Bearer {{apiKey}}', type: 'text' }
                                ],
                                url: {
                                    raw: '{{baseUrl}}/lists/:listId/members?limit=25&expand=true',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists', ':listId', 'members'],
                                    query: [
                                        { key: 'limit', value: '25' },
                                        { key: 'expand', value: 'true' }
                                    ],
                                    variable: [
                                        { key: 'listId', value: 'LIST_ID_HERE', description: 'List UUID' }
                                    ]
                                }
                            }
                        },
                        {
                            name: 'Add to List',
                            request: {
                                method: 'POST',
                                header: [
                                    {
                                        key: 'Authorization',
                                        value: 'Bearer {{apiKey}}',
                                        type: 'text'
                                    },
                                    {
                                        key: 'Content-Type',
                                        value: 'application/json',
                                        type: 'text'
                                    }
                                ],
                                body: {
                                    mode: 'raw',
                                    raw: JSON.stringify({
                                        contact_id: 'CONTACT_ID_HERE'
                                    }, null, 2)
                                },
                                url: {
                                    raw: '{{baseUrl}}/lists/:listId/members',
                                    host: ['{{baseUrl}}'],
                                    path: ['lists', ':listId', 'members'],
                                    variable: [
                                        {
                                            key: 'listId',
                                            value: 'LIST_ID_HERE',
                                            description: 'The list ID (UUID format)'
                                        }
                                    ]
                                },
                                description: 'Add a contact or company to a list. Use contact_id for Contact lists or company_id for Company lists (exactly one).'
                            }
                        }
                    ]
                }
            ]
        };

        return collection;
    };

    // Handle export to Postman
    const handleExportToPostman = () => {
        try {
            const collection = generatePostmanCollection();
            const collectionJson = JSON.stringify(collection, null, 2);
            
            // Create blob and download
            const blob = new Blob([collectionJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ninjapipe-api-collection-${'ninjapipe-api'}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            // Show success message
            if (window.showToast) {
                window.showToast('Postman collection exported successfully!', 'success');
            }
        } catch (error) {
            console.error('Error exporting Postman collection:', error);
            if (window.showToast) {
                window.showToast('Error exporting collection. Please try again.', 'error');
            }
        }
    };

    // Generate OpenAPI/Swagger specification
    const generateSwaggerSpec = () => {
        const baseUrl = getApiBaseUrl();

        const spec = {
            openapi: '3.0.0',
            info: {
                title: `NinjaPipe API - ${'NinjaPipe API'}`,
                description: 'Complete NinjaPipe API specification with all endpoints',
                version: '1.0.0',
                contact: {
                    name: 'NinjaPipe API Support',
                    url: 'https://www.ninjapipe.app'
                }
            },
            servers: [
                {
                    url: baseUrl,
                    description: 'NinjaPipe API Server'
                }
            ],
            security: [
                {
                    bearerAuth: []
                }
            ],
            components: {
                securitySchemes: {
                    bearerAuth: {
                        type: 'http',
                        scheme: 'bearer',
                        bearerFormat: 'JWT'
                    }
                },
                schemas: {
                    Contact: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            first_name: { type: 'string' },
                            last_name: { type: 'string' },
                            email: { type: 'string', format: 'email' },
                            phone: { type: 'string' },
                            company: { type: 'string' },
                            status: { type: 'string', enum: ['Active', 'Inactive'] },
                            owner_id: { type: 'string', format: 'uuid' },
                            assigned_to: { type: 'string', format: 'uuid', nullable: true },
                            company_id: { type: 'string', format: 'uuid' },
                            fields: { type: 'object' },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' }
                        }
                    },
                    WorkspaceMember: {
                        type: 'object',
                        properties: {
                            account_id: { type: 'string', format: 'uuid' },
                            email: { type: 'string', format: 'email' },
                            first_name: { type: 'string', nullable: true },
                            last_name: { type: 'string', nullable: true },
                            full_name: { type: 'string', nullable: true },
                            role: { type: 'string', nullable: true },
                            joined_at: { type: 'string', format: 'date-time', nullable: true }
                        }
                    },
                    Deal: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            name: { type: 'string' },
                            amount: { type: 'number' },
                            stage: { type: 'string' },
                            close_date: { type: 'string', format: 'date' },
                            contact_id: { type: 'string', format: 'uuid' },
                            owner_id: { type: 'string', format: 'uuid' },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' }
                        }
                    },
                    Product: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            name: { type: 'string' },
                            description: { type: 'string' },
                            price: { type: 'number' },
                            category: { type: 'string' },
                            sku: { type: 'string' },
                            status: { type: 'string', enum: ['Active', 'Inactive'] },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' }
                        }
                    },
                    Budget: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            name: { type: 'string' },
                            description: { type: 'string' },
                            category: { type: 'string' },
                            period: { type: 'string' },
                            allocated: { type: 'number' },
                            spent: { type: 'number' },
                            remaining: { type: 'number' },
                            currency: { type: 'string' },
                            status: { type: 'string', enum: ['Active', 'Inactive'] },
                            start_date: { type: 'string', format: 'date' },
                            end_date: { type: 'string', format: 'date' },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' }
                        }
                    },
                    BudgetExpense: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            budget_id: { type: 'string', format: 'uuid' },
                            description: { type: 'string' },
                            amount: { type: 'number' },
                            category: { type: 'string' },
                            date: { type: 'string', format: 'date' },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' }
                        }
                    },
                    ClientPortalAccount: {
                        type: 'object',
                        properties: {
                            id: { type: 'string', format: 'uuid' },
                            workspace_id: { type: 'string', format: 'uuid' },
                            contact_id: { type: 'string', format: 'uuid' },
                            email: { type: 'string', format: 'email' },
                            is_enabled: { type: 'boolean' },
                            created_by: { type: 'string', format: 'uuid' },
                            created_at: { type: 'string', format: 'date-time' },
                            updated_at: { type: 'string', format: 'date-time' },
                            last_login_at: { type: 'string', format: 'date-time', nullable: true },
                            settings_json: { type: 'object', nullable: true }
                        }
                    }
                }
            },
            paths: {
                '/contacts': {
                    get: {
                        summary: 'List Contacts',
                        description: 'Retrieve a paginated list of contacts with optional filtering and search capabilities.',
                        parameters: [
                            { name: 'page', in: 'query', schema: { type: 'integer', default: 1 }, description: 'Page number for pagination' },
                            { name: 'limit', in: 'query', schema: { type: 'integer', default: 20 }, description: 'Number of contacts per page (max 100)' },
                            { name: 'search', in: 'query', schema: { type: 'string' }, description: 'Search in name, email, and phone fields' },
                            { name: 'status', in: 'query', schema: { type: 'string' }, description: 'Filter by contact status' },
                            { name: 'owner_id', in: 'query', schema: { type: 'string' }, description: 'Filter by record owner (distinct from Assign to)' },
                            { name: 'assigned_to', in: 'query', schema: { type: 'string' }, description: 'Filter by assigned team member account UUID' }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '200': {
                                description: 'Successful response',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { type: 'array', items: { $ref: '#/components/schemas/Contact' } },
                                                pagination: { type: 'object' }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    },
                    post: {
                        summary: 'Create Contact',
                        description: 'Create a new contact in your workspace.',
                        security: [{ bearerAuth: [] }],
                        requestBody: {
                            required: true,
                            content: {
                                'application/json': {
                                    schema: {
                                        type: 'object',
                                        properties: {
                                            first_name: { type: 'string' },
                                            last_name: { type: 'string' },
                                            email: { type: 'string', format: 'email' },
                                            phone: { type: 'string' },
                                            company: { type: 'string' },
                                            status: { type: 'string', default: 'Active' },
                                            owner_id: { type: 'string' },
                                            assigned_to: { type: 'string', description: 'Team member account UUID, email, or unique display name' },
                                            company_id: { type: 'string' },
                                            fields: {
                                                type: 'object',
                                                description: 'Custom field data object containing additional contact information',
                                                properties: {
                                                    mrr: { type: 'string', description: 'Monthly recurring revenue' },
                                                    city: { type: 'string', description: 'Contact city' },
                                                    tags: { type: 'string', description: 'Contact tags (comma-separated)' },
                                                    churn: { type: 'string', description: 'Churn status' },
                                                    state: { type: 'string', description: 'Contact state or province' },
                                                    tiktok: { type: 'string', description: 'TikTok handle' },
                                                    address: { type: 'string', description: 'Physical address' },
                                                    by_text: { type: 'boolean', description: 'Contact preference: allow text messages' },
                                                    country: { type: 'string', description: 'Contact country' },
                                                    revenue: { type: 'string', description: 'Revenue amount' },
                                                    twitter: { type: 'string', description: 'Twitter handle' },
                                                    website: { type: 'string', description: 'Website URL' },
                                                    by_email: { type: 'boolean', description: 'Contact preference: allow emails' },
                                                    by_phone: { type: 'boolean', description: 'Contact preference: allow phone calls' },
                                                    category: { type: 'string', description: 'Contact category' },
                                                    currency: { type: 'string', description: 'Currency code (e.g., USD, EUR)' },
                                                    facebook: { type: 'string', description: 'Facebook page URL' },
                                                    industry: { type: 'string', description: 'Industry type' },
                                                    linkedin: { type: 'string', description: 'LinkedIn profile URL' },
                                                    position: { type: 'string', description: 'Job position or title' },
                                                    timezone: { type: 'string', description: 'Timezone (e.g., Europe/Madrid)' },
                                                    whatsapp: { type: 'string', description: 'WhatsApp number' },
                                                    zip_code: { type: 'string', description: 'Postal/ZIP code' },
                                                    by_social: { type: 'boolean', description: 'Contact preference: allow social media outreach' },
                                                    leadSource: { type: 'string', description: 'Lead source information' },
                                                    companySize: { type: 'string', description: 'Company size range' },
                                                    jobFunction: { type: 'string', description: 'Job function or department' },
                                                    social_tiktok: { type: 'string', description: 'TikTok profile URL' },
                                                    social_twitter: { type: 'string', description: 'Twitter profile URL' },
                                                    social_facebook: { type: 'string', description: 'Facebook profile URL' },
                                                    social_linkedin: { type: 'string', description: 'LinkedIn profile URL' },
                                                    social_whatsapp: { type: 'string', description: 'WhatsApp profile URL' },
                                                    social_instagram: { type: 'string', description: 'Instagram profile URL' },
                                                    name_value_pairs: {
                                                        type: 'array',
                                                        description: 'Array of custom name-value pairs',
                                                        items: {
                                                            type: 'object',
                                                            properties: {
                                                                name: { type: 'string' },
                                                                value: { type: 'string' }
                                                            }
                                                        }
                                                    },
                                                    additional_details: { type: 'string', description: 'Additional notes or details about the contact' }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        },
                        responses: {
                            '201': {
                                description: 'Contact created successfully',
                                content: {
                                    'application/json': {
                                        schema: { $ref: '#/components/schemas/Contact' }
                                    }
                                }
                            }
                        }
                    }
                },
                '/contacts/{contactId}': {
                    get: {
                        summary: 'Get Contact',
                        description: 'Retrieve a specific contact by ID.',
                        parameters: [
                            { name: 'contactId', in: 'path', required: true, schema: { type: 'string' }, description: 'The contact ID (UUID format)' }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '200': {
                                description: 'Successful response',
                                content: {
                                    'application/json': {
                                        schema: { $ref: '#/components/schemas/Contact' }
                                    }
                                }
                            }
                        }
                    },
                    put: {
                        summary: 'Update Contact',
                        description: 'Update an existing contact.',
                        parameters: [
                            { name: 'contactId', in: 'path', required: true, schema: { type: 'string' }, description: 'The contact ID (UUID format)' }
                        ],
                        security: [{ bearerAuth: [] }],
                        requestBody: {
                            required: true,
                            content: {
                                'application/json': {
                                    schema: {
                                        type: 'object',
                                        properties: {
                                            first_name: { type: 'string' },
                                            last_name: { type: 'string' },
                                            email: { type: 'string', format: 'email' },
                                            phone: { type: 'string' },
                                            company: { type: 'string' },
                                            status: { type: 'string' },
                                            owner_id: { type: 'string' },
                                            assigned_to: { type: 'string', nullable: true, description: 'Team member account UUID, email, unique display name, or null to unassign' },
                                            company_id: { type: 'string' },
                                            fields: { type: 'object' }
                                        }
                                    }
                                }
                            }
                        },
                        responses: {
                            '200': {
                                description: 'Contact updated successfully',
                                content: {
                                    'application/json': {
                                        schema: { $ref: '#/components/schemas/Contact' }
                                    }
                                }
                            }
                        }
                    },
                    delete: {
                        summary: 'Delete Contact',
                        description: 'Delete a contact from your workspace.',
                        parameters: [
                            { name: 'contactId', in: 'path', required: true, schema: { type: 'string' }, description: 'The contact ID (UUID format)' }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '204': {
                                description: 'Contact deleted successfully'
                            }
                        }
                    }
                },
                '/workspace/members': {
                    get: {
                        summary: 'List Workspace Members',
                        description: 'List active workspace members and account UUIDs (for contact assigned_to and integrations).',
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '200': {
                                description: 'Successful response',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { type: 'array', items: { $ref: '#/components/schemas/WorkspaceMember' } },
                                                meta: { type: 'object' }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                '/client-portal-accounts/{contactId}/enable': {
                    put: {
                        summary: 'Enable Client Portal',
                        description: 'Enable client portal access for a contact. Creates a new portal account if one doesn\'t exist, or re-enables an existing disabled portal account.',
                        parameters: [
                            { name: 'contactId', in: 'path', required: true, schema: { type: 'string' }, description: 'The contact ID (UUID format)' }
                        ],
                        security: [{ bearerAuth: [] }],
                        requestBody: {
                            required: false,
                            content: {
                                'application/json': {
                                    schema: {
                                        type: 'object',
                                        properties: {
                                            email: { type: 'string', format: 'email', description: 'Email address for the portal account. If not provided, the contact\'s email will be used.' },
                                            password: { type: 'string', description: 'Password for the portal account. Must be at least 8 characters. If not provided, a secure password will be auto-generated.' },
                                            auto_generate_password: { type: 'boolean', description: 'Set to true to auto-generate a password, or false to require a password in the request body. Defaults to true if password is not provided.' }
                                        }
                                    }
                                }
                            }
                        },
                        responses: {
                            '200': {
                                description: 'Client portal enabled successfully',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { $ref: '#/components/schemas/ClientPortalAccount' },
                                                message: { type: 'string' },
                                                generated_password: { type: 'string', description: 'Auto-generated password (only included if password was auto-generated)' }
                                            }
                                        }
                                    }
                                }
                            },
                            '400': {
                                description: 'Bad Request - validation error or missing required field',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                error: { type: 'string' },
                                                message: { type: 'string' },
                                                code: { type: 'string' },
                                                details: { type: 'object' }
                                            }
                                        }
                                    }
                                }
                            },
                            '404': {
                                description: 'Contact not found',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                error: { type: 'string' },
                                                message: { type: 'string' },
                                                code: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                '/client-portal-accounts/{contactId}/disable': {
                    put: {
                        summary: 'Disable Client Portal',
                        description: 'Disable client portal access for a contact. Deactivates the portal account, preventing the contact from accessing the client portal.',
                        parameters: [
                            { name: 'contactId', in: 'path', required: true, schema: { type: 'string' }, description: 'The contact ID (UUID format)' }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '200': {
                                description: 'Client portal disabled successfully',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { $ref: '#/components/schemas/ClientPortalAccount' },
                                                message: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            },
                            '404': {
                                description: 'Contact or portal account not found',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                error: { type: 'string' },
                                                message: { type: 'string' },
                                                code: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                '/lists': {
                    get: {
                        summary: 'List lists',
                        description: 'Paginated CRM lists in the workspace.',
                        parameters: [
                            { name: 'page', in: 'query', schema: { type: 'integer' } },
                            { name: 'limit', in: 'query', schema: { type: 'integer' } },
                            { name: 'search', in: 'query', schema: { type: 'string' } },
                            { name: 'list_type', in: 'query', schema: { type: 'string', enum: ['Contact', 'Company'] } },
                            { name: 'status', in: 'query', schema: { type: 'string', enum: ['Active', 'Inactive', 'Archived'] } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '200': { description: 'OK' }
                        }
                    },
                    post: {
                        summary: 'Create list',
                        security: [{ bearerAuth: [] }],
                        responses: {
                            '201': { description: 'Created' }
                        }
                    }
                },
                '/lists/{id}': {
                    get: {
                        summary: 'Get list',
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' }, '404': { description: 'Not found' } }
                    },
                    put: {
                        summary: 'Update list',
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' } }
                    },
                    delete: {
                        summary: 'Delete list',
                        parameters: [
                            { name: 'id', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' } }
                    }
                },
                '/lists/{listId}/members': {
                    get: {
                        summary: 'List list members',
                        parameters: [
                            { name: 'listId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
                            { name: 'page', in: 'query', schema: { type: 'integer' } },
                            { name: 'limit', in: 'query', schema: { type: 'integer' } },
                            { name: 'expand', in: 'query', schema: { type: 'string' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' } }
                    },
                    post: {
                        summary: 'Add to list',
                        description: 'Add a contact or company to a CRM list. Send exactly one of contact_id (for Contact lists) or company_id (for Company lists). The list type must match the member. Duplicate membership returns 200 with already_in_list: true.',
                        parameters: [
                            { name: 'listId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' }, description: 'The list ID' }
                        ],
                        security: [{ bearerAuth: [] }],
                        requestBody: {
                            required: true,
                            content: {
                                'application/json': {
                                    schema: {
                                        type: 'object',
                                        properties: {
                                            contact_id: { type: 'string', format: 'uuid', description: 'Contact to add (use for Contact-type lists only)' },
                                            company_id: { type: 'string', format: 'uuid', description: 'Company to add (use for Company-type lists only)' }
                                        },
                                        description: 'Exactly one of contact_id or company_id is required.'
                                    }
                                }
                            }
                        },
                        responses: {
                            '201': {
                                description: 'Member added',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { type: 'object' },
                                                message: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            },
                            '200': {
                                description: 'Already in list (idempotent)',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                data: { type: 'object' },
                                                message: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            },
                            '400': {
                                description: 'Validation error (e.g. list type mismatch, both IDs sent)',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                error: { type: 'string' },
                                                message: { type: 'string' },
                                                code: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            },
                            '404': {
                                description: 'List, contact, or company not found in workspace',
                                content: {
                                    'application/json': {
                                        schema: {
                                            type: 'object',
                                            properties: {
                                                error: { type: 'string' },
                                                message: { type: 'string' },
                                                code: { type: 'string' }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                },
                '/lists/{listId}/members/{memberId}': {
                    patch: {
                        summary: 'Update list member',
                        parameters: [
                            { name: 'listId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
                            { name: 'memberId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' }, '404': { description: 'Not found' } }
                    },
                    delete: {
                        summary: 'Remove list member',
                        parameters: [
                            { name: 'listId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } },
                            { name: 'memberId', in: 'path', required: true, schema: { type: 'string', format: 'uuid' } }
                        ],
                        security: [{ bearerAuth: [] }],
                        responses: { '200': { description: 'OK' }, '404': { description: 'Not found' } }
                    }
                }
            }
        };

        return spec;
    };

    // Handle export to Swagger
    const handleExportToSwagger = () => {
        try {
            const spec = generateSwaggerSpec();
            const specJson = JSON.stringify(spec, null, 2);
            
            // Create blob and download
            const blob = new Blob([specJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ninjapipe-api-spec-${'ninjapipe-api'}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            // Show success message
            if (window.showToast) {
                window.showToast('OpenAPI/Swagger specification exported successfully!', 'success');
            }
        } catch (error) {
            console.error('Error exporting Swagger specification:', error);
            if (window.showToast) {
                window.showToast('Error exporting specification. Please try again.', 'error');
            }
        }
    };

    // Generate Insomnia collection
    const generateInsomniaCollection = () => {
        const baseUrl = getApiBaseUrl();
        const apiKey = '[YOUR_API_KEY]';

        const collection = {
            _type: 'export',
            __export_format: 4,
            __export_date: new Date().toISOString(),
            __export_source: 'ninjapipe.app',
            resources: [
                {
                    _id: 'wrk_ninjapipe_api',
                    _type: 'workspace',
                    name: `NinjaPipe API - ${'NinjaPipe API'}`,
                    description: 'Complete NinjaPipe API collection with all endpoints',
                    scope: 'collection'
                },
                {
                    _id: 'env_ninjapipe_api',
                    _type: 'environment',
                    name: 'Base Environment',
                    data: {
                        base_url: baseUrl,
                        api_key: apiKey
                    },
                    dataPropertyOrder: {
                        '&': ['base_url', 'api_key']
                    },
                    color: null,
                    isPrivate: false,
                    parentId: 'wrk_ninjapipe_api'
                },
                // Contacts
                {
                    _id: 'fld_contacts',
                    _type: 'request_group',
                    name: 'Contacts',
                    description: 'Contact management endpoints',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -1000000000000
                },
                {
                    _id: 'req_list_contacts',
                    _type: 'request',
                    name: 'List Contacts',
                    description: 'Retrieve a paginated list of contacts with optional filtering and search capabilities.',
                    url: '{{ _.base_url }}/contacts?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page number for pagination' },
                        { name: 'limit', value: '20', description: 'Number of contacts per page (max 100)' },
                        { name: 'search', value: '', description: 'Search in name, email, and phone fields' },
                        { name: 'status', value: '', description: 'Filter by contact status' },
                        { name: 'owner_id', value: '', description: 'Filter by record owner (distinct from Assign to)' },
                        { name: 'assigned_to', value: '', description: 'Filter by assigned team member account UUID' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000000
                },
                {
                    _id: 'req_create_contact',
                    _type: 'request',
                    name: 'Create Contact',
                    description: 'Create a new contact in your workspace.',
                    url: '{{ _.base_url }}/contacts',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            first_name: 'John',
                            last_name: 'Doe',
                            email: 'john.doe@example.com',
                            phone: '+1234567890',
                            company: 'Example Corp',
                            status: 'Active'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000001
                },
                {
                    _id: 'req_get_contact',
                    _type: 'request',
                    name: 'Get Contact',
                    description: 'Retrieve a specific contact by ID.',
                    url: '{{ _.base_url }}/contacts/{{ _.contact_id }}',
                    method: 'GET',
                    body: {},
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000002
                },
                {
                    _id: 'req_update_contact',
                    _type: 'request',
                    name: 'Update Contact',
                    description: 'Update an existing contact.',
                    url: '{{ _.base_url }}/contacts/{{ _.contact_id }}',
                    method: 'PUT',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            first_name: 'Jane',
                            last_name: 'Doe',
                            email: 'jane.doe@example.com'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000003
                },
                {
                    _id: 'req_delete_contact',
                    _type: 'request',
                    name: 'Delete Contact',
                    description: 'Delete a contact from your workspace.',
                    url: '{{ _.base_url }}/contacts/{{ _.contact_id }}',
                    method: 'DELETE',
                    body: {},
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000004
                },
                {
                    _id: 'req_enable_client_portal',
                    _type: 'request',
                    name: 'Enable Client Portal',
                    description: 'Enable client portal access for a contact. Creates a new portal account if one doesn\'t exist, or re-enables an existing disabled portal account.',
                    url: '{{ _.base_url }}/client-portal-accounts/{{ _.contact_id }}/enable',
                    method: 'PUT',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            email: 'client@example.com',
                            auto_generate_password: true
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000005
                },
                {
                    _id: 'req_disable_client_portal',
                    _type: 'request',
                    name: 'Disable Client Portal',
                    description: 'Disable client portal access for a contact. Deactivates the portal account, preventing the contact from accessing the client portal.',
                    url: '{{ _.base_url }}/client-portal-accounts/{{ _.contact_id }}/disable',
                    method: 'PUT',
                    body: {},
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_contacts',
                    metaSortKey: -1000000000006
                },
                // Deals
                {
                    _id: 'fld_deals',
                    _type: 'request_group',
                    name: 'Deals',
                    description: 'Deal management endpoints',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -2000000000000
                },
                {
                    _id: 'req_list_deals',
                    _type: 'request',
                    name: 'List Deals',
                    description: 'Retrieve a paginated list of deals with optional filtering and search capabilities.',
                    url: '{{ _.base_url }}/deals?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page number for pagination' },
                        { name: 'limit', value: '20', description: 'Number of deals per page (max 100)' },
                        { name: 'search', value: '', description: 'Search in name and description fields' },
                        { name: 'status', value: '', description: 'Filter by deal status' },
                        { name: 'owner_id', value: '', description: 'Filter by deal owner' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_deals',
                    metaSortKey: -2000000000000
                },
                {
                    _id: 'req_create_deal',
                    _type: 'request',
                    name: 'Create Deal',
                    description: 'Create a new deal in your workspace.',
                    url: '{{ _.base_url }}/deals',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            name: 'New Deal',
                            amount: 50000,
                            stage: 'Prospecting',
                            close_date: '2024-12-31',
                            contact_id: '{{ _.contact_id }}'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_deals',
                    metaSortKey: -2000000000001
                },
                // Products
                {
                    _id: 'fld_products',
                    _type: 'request_group',
                    name: 'Products',
                    description: 'Product management endpoints',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -3000000000000
                },
                {
                    _id: 'req_list_products',
                    _type: 'request',
                    name: 'List Products',
                    description: 'Retrieve a paginated list of products with optional filtering and search capabilities.',
                    url: '{{ _.base_url }}/products?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page number for pagination' },
                        { name: 'limit', value: '20', description: 'Number of products per page (max 100)' },
                        { name: 'search', value: '', description: 'Search in name, description, and SKU fields' },
                        { name: 'status', value: '', description: 'Filter by product status' },
                        { name: 'category', value: '', description: 'Filter by product category' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_products',
                    metaSortKey: -3000000000000
                },
                {
                    _id: 'req_create_product',
                    _type: 'request',
                    name: 'Create Product',
                    description: 'Create a new product in your workspace.',
                    url: '{{ _.base_url }}/products',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            name: 'New Product',
                            description: 'Product description',
                            price: 99.99,
                            category: 'Software',
                            sku: 'PROD-001'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_products',
                    metaSortKey: -3000000000001
                },
                {
                    _id: 'fld_projects_tasks',
                    _type: 'request_group',
                    name: 'Projects & Tasks',
                    description: 'Projects and tasks (same as Projects & Tasks in the app)',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -3500000000000
                },
                {
                    _id: 'req_list_projects',
                    _type: 'request',
                    name: 'List Projects',
                    description: 'Paginated list of workspace projects.',
                    url: '{{ _.base_url }}/projects?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page' },
                        { name: 'limit', value: '20', description: 'Page size (max 100)' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_projects_tasks',
                    metaSortKey: -3500000000000
                },
                {
                    _id: 'req_create_project',
                    _type: 'request',
                    name: 'Create Project',
                    description: 'Create a project.',
                    url: '{{ _.base_url }}/projects',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({ name: 'New project', status: 'Planning', priority: 'Medium' }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_projects_tasks',
                    metaSortKey: -3500000000001
                },
                {
                    _id: 'fld_orders',
                    _type: 'request_group',
                    name: 'Orders',
                    description: 'Orders (same as Orders page in the app)',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -3750000000000
                },
                {
                    _id: 'req_list_orders',
                    _type: 'request',
                    name: 'List Orders',
                    description: 'Paginated list of workspace orders.',
                    url: '{{ _.base_url }}/orders?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page' },
                        { name: 'limit', value: '20', description: 'Page size (max 100)' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_orders',
                    metaSortKey: -3750000000000
                },
                // Budgets
                {
                    _id: 'fld_budgets',
                    _type: 'request_group',
                    name: 'Budgets',
                    description: 'Budget management endpoints',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -4000000000000
                },
                {
                    _id: 'req_list_budgets',
                    _type: 'request',
                    name: 'List Budgets',
                    description: 'Retrieve a paginated list of budgets with optional filtering and search capabilities.',
                    url: '{{ _.base_url }}/budgets?page=1&limit=20',
                    method: 'GET',
                    body: {},
                    parameters: [
                        { name: 'page', value: '1', description: 'Page number for pagination' },
                        { name: 'limit', value: '20', description: 'Number of budgets per page (max 100)' },
                        { name: 'search', value: '', description: 'Search in name and description fields' },
                        { name: 'status', value: '', description: 'Filter by budget status' },
                        { name: 'category', value: '', description: 'Filter by budget category' }
                    ],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_budgets',
                    metaSortKey: -4000000000000
                },
                {
                    _id: 'req_create_budget',
                    _type: 'request',
                    name: 'Create Budget',
                    description: 'Create a new budget in your workspace.',
                    url: '{{ _.base_url }}/budgets',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            name: 'Marketing Budget Q1 2024',
                            description: 'Q1 marketing budget for campaigns',
                            category: 'Marketing',
                            period: 'Quarterly',
                            allocated: 50000,
                            currency: 'USD',
                            start_date: '2024-01-01',
                            end_date: '2024-03-31'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_budgets',
                    metaSortKey: -4000000000001
                },
                {
                    _id: 'fld_lists',
                    _type: 'request_group',
                    name: 'Lists',
                    description: 'CRM list membership',
                    parentId: 'wrk_ninjapipe_api',
                    environment: {},
                    environmentPropertyOrder: null,
                    metaSortKey: -4500000000000
                },
                {
                    _id: 'req_list_lists',
                    _type: 'request',
                    name: 'List Lists',
                    description: 'GET /api/lists — paginated workspace lists.',
                    url: '{{ _.base_url }}/lists?page=1&limit=20',
                    method: 'GET',
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_lists',
                    metaSortKey: -4500000000001
                },
                {
                    _id: 'req_add_to_list',
                    _type: 'request',
                    name: 'Add to List',
                    description: 'Add a contact or company to a list (contact_id or company_id, not both).',
                    url: '{{ _.base_url }}/lists/{{ _.list_id }}/members',
                    method: 'POST',
                    body: {
                        mimeType: 'application/json',
                        text: JSON.stringify({
                            contact_id: '{{ _.contact_id }}'
                        }, null, 2)
                    },
                    parameters: [],
                    headers: [
                        { name: 'Authorization', value: 'Bearer {{ _.api_key }}' },
                        { name: 'Content-Type', value: 'application/json' }
                    ],
                    authentication: {},
                    isPrivate: false,
                    settingStoreCookies: true,
                    settingSendCookies: true,
                    settingDisableRenderRequestBody: false,
                    settingEncodeUrl: true,
                    settingRebuildPath: true,
                    settingFollowRedirects: 'global',
                    parentId: 'fld_lists',
                    metaSortKey: -4500000000000
                }
            ]
        };

        return collection;
    };

    // Handle export to Insomnia
    const handleExportToInsomnia = () => {
        try {
            const collection = generateInsomniaCollection();
            const collectionJson = JSON.stringify(collection, null, 2);
            
            // Create blob and download
            const blob = new Blob([collectionJson], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ninjapipe-insomnia-collection-${'ninjapipe-api'}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            // Show success message
            if (window.showToast) {
                window.showToast('Insomnia collection exported successfully!', 'success');
            }
        } catch (error) {
            console.error('Error exporting Insomnia collection:', error);
            if (window.showToast) {
                window.showToast('Error exporting collection. Please try again.', 'error');
            }
        }
    };

    // Filter navigation items based on search query
    const getFilteredIntroItems = () => {
        if (!searchQuery.trim()) return introItems || [];
        return (introItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredWorkspaceItems = () => {
        if (!searchQuery.trim()) return workspaceItems || [];
        return (workspaceItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredContactsItems = () => {
        if (!searchQuery.trim()) return contactsItems || [];
        return (contactsItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredDealsItems = () => {
        if (!searchQuery.trim()) return dealsItems || [];
        return (dealsItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredProductsItems = () => {
        if (!searchQuery.trim()) return productsItems || [];
        return (productsItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredBudgetsItems = () => {
        if (!searchQuery.trim()) return budgetsItems || [];
        return (budgetsItems || []).filter(item => 
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredProjectsItems = () => {
        if (!searchQuery.trim()) return projectsItems || [];
        return (projectsItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredOrdersItems = () => {
        if (!searchQuery.trim()) return ordersItems || [];
        return (ordersItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredListsItems = () => {
        if (!searchQuery.trim()) return listsItems || [];
        return (listsItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredCompaniesItems = () => {
        if (!searchQuery.trim()) return companiesItems || [];
        return (companiesItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const getFilteredPipelinesItems = () => {
        if (!searchQuery.trim()) return pipelinesItems || [];
        return (pipelinesItems || []).filter(item =>
            item.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    // Helper function to highlight matching text
    const highlightText = (text, query) => {
        if (!query.trim()) return text;
        
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => {
            if (regex.test(part)) {
                return (
                    <span key={index} style={{
                        backgroundColor: '#10B981',
                        color: '#ffffff',
                        padding: '1px 2px',
                        borderRadius: '3px',
                        fontWeight: 600
                    }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const renderContent = useMemo(() => {
        const companiesPipelinesContent = buildCompaniesPipelinesPageContent(selectedItem, getApiBaseUrl);
        if (companiesPipelinesContent) return companiesPipelinesContent;

        switch (selectedItem) {
            case 'Introduction':
                return {
                    title: 'Introduction',
                    content: (
                        <div>
                            <div style={{ marginBottom: 32 }}>
                                <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Hey there! 👋
                                </h2>
                                <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                                    Welcome to the NinjaPipe API! We've built this to help you connect your apps with all the cool stuff 
                                    NinjaPipe can do - managing contacts, tracking deals, handling products, and keeping your budgets in check. 
                                    Think of it as your bridge to automate the boring stuff so you can focus on what matters.
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    What can you actually build with this?
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
                                    <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>Sync your contacts everywhere</h4>
                                        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
                                            Keep your customer data in sync across all your tools and platforms
                                        </p>
                                    </div>
                                    <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>Track deals like a pro</h4>
                                        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
                                            Monitor your sales pipeline and never miss an opportunity
                                        </p>
                                    </div>
                                    <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>Manage your products</h4>
                                        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
                                            Keep your product catalog organized and up-to-date across all channels
                                        </p>
                                    </div>
                                    <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>Stay on budget</h4>
                                        <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
                                            Track expenses and manage budgets without the spreadsheet headaches
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Let's get you started
                                </h3>
                                <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 24 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <div style={{ width: 24, height: 24, backgroundColor: 'var(--brand-cyan)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>1</div>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Grab your API key</h4>
                                    </div>
                                    <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: '0 0 16px', paddingLeft: 36 }}>
                                        Head over to your NinjaPipe dashboard and create an API key. It's like your password to access everything.
                                    </p>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <div style={{ width: 24, height: 24, backgroundColor: 'var(--brand-cyan)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>2</div>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Make your first call</h4>
                                    </div>
                                    <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: '0 0 16px', paddingLeft: 36 }}>
                                        Try fetching your contacts or deals to make sure everything's working. We've got examples for you below.
                                    </p>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <div style={{ width: 24, height: 24, backgroundColor: 'var(--brand-cyan)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>3</div>
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Build something awesome</h4>
                                    </div>
                                    <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0, paddingLeft: 36 }}>
                                        Once you're comfortable, start building your integration. We're here to help if you get stuck.
                                    </p>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    A few things to keep in mind
                                </h3>
                                <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>🔐 Keep your keys safe</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                Don't put API keys in your frontend code or commit them to GitHub
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>⚡ We have rate limits</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                You can make 100 requests per minute - should be plenty for most use cases
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>🛡️ HTTPS only</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                All requests need to be over HTTPS for security
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>📝 Handle errors gracefully</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                We'll give you clear error messages when something goes wrong
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    What's next?
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                    Ready to dive in? Check out the <a href={getUrlFromPage('Authentication')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Authentication</a> section to see how to set up your API key, 
                                    then pick any endpoint that looks interesting and start experimenting.
                                </p>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: 0 }}>
                                    If you run into any issues or have questions, don't hesitate to reach out. We're here to help you succeed.
                                </p>
                            </div>
                        </div>
                    ),
                    codeBlocks: []
                };

            case 'Quick Start':
                return {
                    title: 'Quick Start',
                    content: (
                        <div>
                            <div style={{ marginBottom: 32 }}>
                                <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Get started in 5 minutes
                                </h2>
                                <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                                    Follow these steps to make your first API call and verify your integration works.
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Step 1: Create an API key
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                    In NinjaPipe, go to <a href="/docs/features/workspaces-overview" style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Workspace Settings</a> → <a href="/docs/features/ninjapipe-api" style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>NinjaPipe API</a>. Create a new API key, give it a name, and copy it. Keep it secure — you won't be able to see it again.
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Step 2: Make your first request
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                    Use curl to list your contacts. Replace <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>YOUR_API_KEY</code> with your actual key:
                                </p>
                                <pre style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 20, overflow: 'auto', fontSize: 14, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'var(--text-primary)' }}>
{`curl -X GET "https://www.ninjapipe.app/api/contacts?page=1&limit=10" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"`}
                                </pre>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '16px 0 0' }}>
                                    You should get a JSON response with your contacts (or an empty array if you have none yet).
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Step 3: Create a contact (optional)
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                    Try creating a contact with a POST request:
                                </p>
                                <pre style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-primary)', borderRadius: 12, padding: 20, overflow: 'auto', fontSize: 14, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'var(--text-primary)' }}>
{`curl -X POST "https://www.ninjapipe.app/api/contacts" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Jane Doe", "email": "jane@example.com"}'`}
                                </pre>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    What's next?
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: 0 }}>
                                    Read the <a href={getUrlFromPage('Authentication')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Authentication</a> section for API key details and security, then explore the <a href={getUrlFromPage('List Contacts')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Contacts</a>, <a href={getUrlFromPage('List Companies')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Companies</a>, <a href={getUrlFromPage('List Deals')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Deals</a>, <a href={getUrlFromPage('List Pipelines')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Pipelines</a>, <a href={getUrlFromPage('List Products')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Products</a>, <a href={getUrlFromPage('List Projects')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Projects &amp; Tasks</a>, <a href={getUrlFromPage('List Orders')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Orders</a>, <a href={getUrlFromPage('List Budgets')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Budgets</a>, and <a href={getUrlFromPage('List Lists')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>Lists</a> endpoints.
                                </p>
                            </div>
                        </div>
                    ),
                    codeBlocks: []
                };

            case 'Authentication':
                return {
                    title: 'Authentication',
                    content: (
                        <div>
                            <div style={{ marginBottom: 32 }}>
                                <h2 style={{ fontSize: 24, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    API Key Authentication
                                </h2>
                                <p style={{ fontSize: 18, lineHeight: 1.6, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
                                    The NinjaPipe API uses API keys to authenticate requests. All API keys are workspace-specific 
                                    and provide access to your workspace's data and resources.
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 className="text-primary" style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px' }}>
                                    🔑 API Key Format
                                </h3>
                                <div className="panel">
                                    <p className="text-secondary" style={{ fontSize: 16, lineHeight: 1.7, margin: '0 0 16px' }}>
                                        All NinjaPipe API keys follow a consistent format:
                                    </p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                        <code style={{
                                            backgroundColor: 'var(--input-bg)',
                                            border: '1px solid var(--border-primary)',
                                            padding: '8px 12px',
                                            borderRadius: 8,
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            fontSize: 14,
                                            color: 'var(--text-primary)'
                                        }}>np_</code>
                                        <span className="text-secondary" style={{ fontSize: 14 }}>+ 32 character random string</span>
                                    </div>
                                    <p className="text-muted" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                                        Example: <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>np_abc123def456ghi789jkl012mno345pqr678</code>
                                    </p>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 className="text-primary" style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px' }}>
                                    🛠️ Managing API Keys
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                                    <div className="panel">
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>🔧 Generate Keys</h4>
                                        <p className="text-secondary" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                                            Create new API keys in your workspace settings with custom names and permissions
                                        </p>
                                    </div>
                                    <div className="panel">
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>🔒 Permissions</h4>
                                        <p className="text-secondary" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                                            Control read/write access for each API key to maintain security
                                        </p>
                                    </div>
                                    <div className="panel">
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>⏰ Expiration</h4>
                                        <p className="text-secondary" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                                            Set optional expiration dates for enhanced security
                                        </p>
                                    </div>
                                    <div className="panel">
                                        <h4 style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-cyan)', margin: '0 0 8px' }}>📊 Usage Tracking</h4>
                                        <p className="text-secondary" style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
                                            Monitor API key usage and last accessed timestamps
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    Other Bearer tokens
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
                                    The same <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>Authorization: Bearer</code> header also accepts:
                                </p>
                                <ul style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 12px', paddingLeft: 22 }}>
                                    <li style={{ marginBottom: 8 }}><strong style={{ color: 'var(--text-primary)' }}>OAuth integration access tokens</strong> (for example Zapier or other integrations) when the integration has workspace scope.</li>
                                    <li><strong style={{ color: 'var(--text-primary)' }}>Supabase session JWT</strong> (browser or mobile app) for the signed-in user. For this token you must pass <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>workspace_id</code> on each request (query string or JSON body) so the API knows which workspace to use.</li>
                                </ul>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: 0 }}>
                                    <strong>Tasks &amp; Projects permissions:</strong> When using a session JWT, access follows the same <strong>tasks</strong> resource rules as the app (read, create, update, delete). NinjaPipe API keys continue to use each key&apos;s read/write flags.
                                </p>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    ⚡ Rate Limiting
                                </h3>
                                <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                        <div style={{ width: 8, height: 8, backgroundColor: '#F59E0B', borderRadius: '50%' }}></div>
                                        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>100 requests per minute per API key</span>
                                    </div>
                                    <p style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)', margin: 0 }}>
                                        Rate limits are applied per API key to ensure fair usage across all users. 
                                        If you exceed the limit, you'll receive a 429 status code with retry information.
                                    </p>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    🔒 Security Best Practices
                                </h3>
                                <div style={{ backgroundColor: 'var(--input-bg)', border: `1px solid var(--border-primary)`, borderRadius: 12, padding: 20 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', margin: '0 0 8px' }}>🚫 Never Expose Keys</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                Keep API keys secure and never commit them to version control
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', margin: '0 0 8px' }}>🔄 Rotate Regularly</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                Regularly rotate API keys and revoke unused ones
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', margin: '0 0 8px' }}>🎯 Minimal Permissions</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                Use read-only keys when write access isn't needed
                                            </p>
                                        </div>
                                        <div>
                                            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#EF4444', margin: '0 0 8px' }}>📝 Monitor Usage</h4>
                                            <p style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)', margin: 0 }}>
                                                Regularly check API key usage logs for suspicious activity
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: 32 }}>
                                <h3 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                                    🌐 HTTPS Requirement
                                </h3>
                                <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                    All API requests must be made over HTTPS for security. Calls made over plain HTTP will fail 
                                    with a 400 Bad Request error. This ensures your API keys and data remain encrypted in transit.
                                </p>
                            </div>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Authentication Header',
                            type: 'info',
                            content: 'Include your API key in the Authorization header with the Bearer prefix.'
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts" \\
  -H "Authorization: Bearer ${'[YOUR_API_KEY]'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Error Response (Invalid Key)',
                            type: 'response',
                            content: `{
  "error": "Unauthorized",
  "message": "Invalid or inactive API key",
  "code": "INVALID_API_KEY"
}`
                        },
                        {
                            title: 'Error Response (Rate Limited)',
                            type: 'response',
                            content: `{
  "error": "Too Many Requests",
  "message": "Rate limit exceeded. Maximum 100 requests per minute.",
  "code": "RATE_LIMIT_EXCEEDED",
  "retryAfter": 60
}`
                        }
                    ]
                };

            case 'Pagination':
                return {
                    title: 'Pagination',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                All GET endpoints that return collections support pagination to help you efficiently retrieve large datasets. 
                                Pagination is consistent across all list endpoints in the NinjaPipe API.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Use the <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>page</code> and <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>limit</code> query parameters to control which results you receive.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The response includes a <code style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    padding: '2px 8px',
                                    borderRadius: 8,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 14,
                                    color: 'var(--text-primary)'
                                }}>pagination</code> object with metadata about the current page and total results.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Pagination Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number to retrieve (starts from 1)' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of items per page (maximum 100)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts?page=2&limit=10" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com",
      "status": "Active",
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 2,
    "limit": 10,
    "total": 150,
    "pages": 15
  },
  "meta": {
    "workspace_id": "ws_123",
    "filters": {},
    "sort": {
      "field": "created_at",
      "order": "desc"
    }
  }
}`
                        },
                        {
                            title: 'Pagination Response Fields',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: true, description: 'Current page number' },
                                { name: 'limit', type: 'integer', required: true, description: 'Number of items per page' },
                                { name: 'total', type: 'integer', required: true, description: 'Total number of items across all pages' },
                                { name: 'pages', type: 'integer', required: true, description: 'Total number of pages available' }
                            ]
                        }
                    ]
                };

            case 'List Workspace Members':
                return {
                    title: 'List Workspace Members',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Returns active workspace members for the workspace tied to your API key. Use{' '}
                                <code>account_id</code> when setting contact <code>assigned_to</code>, deal{' '}
                                <code>owner_id</code>, or pipeline assignees.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Requires <strong>read</strong> permission on the API key. Results are workspace-scoped only.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/workspace/members" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "account_id": "456e7890-e89b-12d3-a456-426614174001",
      "email": "jane.smith@example.com",
      "first_name": "Jane",
      "last_name": "Smith",
      "full_name": "Jane Smith",
      "role": "TEAM MEMBER",
      "joined_at": "2024-01-15T10:30:00Z"
    }
  ],
  "meta": {
    "workspace_id": "123e4567-e89b-12d3-a456-426614174000",
    "total": 1
  }
}`
                        },
                        {
                            title: 'Response Fields',
                            type: 'params',
                            content: [
                                { name: 'account_id', type: 'uuid', required: true, description: 'Team member account UUID (use for assigned_to / owner_id)' },
                                { name: 'email', type: 'string', required: true, description: 'Member email address' },
                                { name: 'first_name', type: 'string', required: false, description: 'First name' },
                                { name: 'last_name', type: 'string', required: false, description: 'Last name' },
                                { name: 'full_name', type: 'string', required: false, description: 'Combined display name' },
                                { name: 'role', type: 'string', required: false, description: 'Workspace role' },
                                { name: 'joined_at', type: 'datetime', required: false, description: 'When the member joined' },
                            ]
                        }
                    ]
                };

            case 'List Contacts':
                return {
      title: 'List Contacts',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of contacts with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter contacts by status, owner, assignee, and search across name, email, and phone fields. 
                                Results are paginated with a maximum of 100 contacts per page.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of contacts per page (max 100)' },
        { name: 'search', type: 'string', required: false, description: 'Search in name, email, and phone fields' },
        { name: 'status', type: 'string', required: false, description: 'Filter by contact status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Filter by record owner (distinct from Assign to)' },
                                { name: 'assigned_to', type: 'string', required: false, description: 'Filter by assigned team member account UUID (Assign to in the app)' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Field to sort by' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc/desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts?page=1&limit=20&search=john" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "first_name": "John",
      "last_name": "Doe",
      "email": "john.doe@example.com",
      "phone": "+1234567890",
      "status": "Active",
      "assigned_to": "456e7890-e89b-12d3-a456-426614174001",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  }
}`
                        }
                    ]
                };

            case 'Create Contact':
                return {
      title: 'Create Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new contact in your workspace. At least one name field (first_name or last_name) is required.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Email addresses must be unique within your workspace. Use <code>assigned_to</code> for the team member shown as Assign to in the app (account UUID, member email, or unique display name). <code>owner_id</code> is a separate record-ownership field and defaults to the API key owner when omitted.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
        { name: 'first_name', type: 'string', required: false, description: 'Contact first name' },
        { name: 'last_name', type: 'string', required: false, description: 'Contact last name' },
        { name: 'email', type: 'string', required: false, description: 'Contact email address' },
        { name: 'phone', type: 'string', required: false, description: 'Contact phone number' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Contact status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Record owner account ID (distinct from assigned_to)' },
                                { name: 'assigned_to', type: 'string', required: false, description: 'Assigned team member account UUID, email, or unique display name' },
        { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Custom field data object containing additional contact information' },
                                { name: 'fields.mrr', type: 'string', required: false, description: 'Monthly recurring revenue' },
                                { name: 'fields.city', type: 'string', required: false, description: 'Contact city' },
                                { name: 'fields.tags', type: 'string', required: false, description: 'Contact tags (comma-separated)' },
                                { name: 'fields.churn', type: 'string', required: false, description: 'Churn status' },
                                { name: 'fields.state', type: 'string', required: false, description: 'Contact state or province' },
                                { name: 'fields.tiktok', type: 'string', required: false, description: 'TikTok handle' },
                                { name: 'fields.address', type: 'string', required: false, description: 'Physical address' },
                                { name: 'fields.by_text', type: 'boolean', required: false, description: 'Contact preference: allow text messages' },
                                { name: 'fields.country', type: 'string', required: false, description: 'Contact country' },
                                { name: 'fields.revenue', type: 'string', required: false, description: 'Revenue amount' },
                                { name: 'fields.twitter', type: 'string', required: false, description: 'Twitter handle' },
                                { name: 'fields.website', type: 'string', required: false, description: 'Website URL' },
                                { name: 'fields.by_email', type: 'boolean', required: false, description: 'Contact preference: allow emails' },
                                { name: 'fields.by_phone', type: 'boolean', required: false, description: 'Contact preference: allow phone calls' },
                                { name: 'fields.category', type: 'string', required: false, description: 'Contact category' },
                                { name: 'fields.currency', type: 'string', required: false, description: 'Currency code (e.g., USD, EUR)' },
                                { name: 'fields.facebook', type: 'string', required: false, description: 'Facebook page URL' },
                                { name: 'fields.industry', type: 'string', required: false, description: 'Industry type' },
                                { name: 'fields.linkedin', type: 'string', required: false, description: 'LinkedIn profile URL' },
                                { name: 'fields.position', type: 'string', required: false, description: 'Job position or title' },
                                { name: 'fields.timezone', type: 'string', required: false, description: 'Timezone (e.g., Europe/Madrid)' },
                                { name: 'fields.whatsapp', type: 'string', required: false, description: 'WhatsApp number' },
                                { name: 'fields.zip_code', type: 'string', required: false, description: 'Postal/ZIP code' },
                                { name: 'fields.by_social', type: 'boolean', required: false, description: 'Contact preference: allow social media outreach' },
                                { name: 'fields.leadSource', type: 'string', required: false, description: 'Lead source information' },
                                { name: 'fields.companySize', type: 'string', required: false, description: 'Company size range' },
                                { name: 'fields.jobFunction', type: 'string', required: false, description: 'Job function or department' },
                                { name: 'fields.social_tiktok', type: 'string', required: false, description: 'TikTok profile URL' },
                                { name: 'fields.social_twitter', type: 'string', required: false, description: 'Twitter profile URL' },
                                { name: 'fields.social_facebook', type: 'string', required: false, description: 'Facebook profile URL' },
                                { name: 'fields.social_linkedin', type: 'string', required: false, description: 'LinkedIn profile URL' },
                                { name: 'fields.social_whatsapp', type: 'string', required: false, description: 'WhatsApp profile URL' },
                                { name: 'fields.social_instagram', type: 'string', required: false, description: 'Instagram profile URL' },
                                { name: 'fields.name_value_pairs', type: 'array', required: false, description: 'Array of custom name-value pairs' },
                                { name: 'fields.additional_details', type: 'string', required: false, description: 'Additional notes or details about the contact' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/contacts" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
  "first_name": "Lucia",
  "last_name": "Martinez",
  "email": "lucia.martinez@solarluna.co",
  "phone": "+34 612 458 937",
  "status": "Qualified",
  "fields": {
    "mrr": "3200",
    "city": "Barcelona",
    "tags": "renewable, solar, europe",
    "churn": "false",
    "state": "Catalonia",
    "tiktok": "@luciasolarluna",
    "address": "Carrer de Casp 45, 3rd Floor",
    "by_text": true,
    "country": "Spain",
    "revenue": "38500",
    "twitter": "@luciamartinez_es",
    "website": "https://solarluna.co",
    "by_email": true,
    "by_phone": false,
    "category": "Renewable Energy",
    "currency": "EUR",
    "facebook": "facebook.com/solarluna.co",
    "industry": "Clean Technology",
    "linkedin": "linkedin.com/in/luciamartinezsolar",
    "position": "Head of Partnerships",
    "timezone": "Europe/Madrid",
    "whatsapp": "+34 612 458 937",
    "zip_code": "08010",
    "by_social": true,
    "leadSource": "Inbound - Company Website",
    "companySize": "51-200",
    "jobFunction": "Business Development",
    "social_tiktok": "tiktok.com/@luciasolarluna",
    "social_twitter": "twitter.com/luciamartinez_es",
    "social_facebook": "facebook.com/solarluna.co",
    "social_linkedin": "linkedin.com/in/luciamartinezsolar",
    "social_whatsapp": "wa.me/34612458937",
    "name_value_pairs": [
      { "name": "Lead Temperature", "value": "Hot" },
      { "name": "Meeting Scheduled", "value": "2025-11-02" }
    ],
    "social_instagram": "instagram.com/solarluna.energy",
    "additional_details": "Lucia is exploring potential distributors in Southern Europe. Prefers morning calls between 9–11 AM local time."
  }
}'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "Lucia",
    "last_name": "Martinez",
    "email": "lucia.martinez@solarluna.co",
    "phone": "+34 612 458 937",
    "status": "Qualified",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "fields": {
      "mrr": "3200",
      "city": "Barcelona",
      "tags": "renewable, solar, europe",
      "churn": "false",
      "state": "Catalonia",
      "tiktok": "@luciasolarluna",
      "address": "Carrer de Casp 45, 3rd Floor",
      "by_text": true,
      "country": "Spain",
      "revenue": "38500",
      "twitter": "@luciamartinez_es",
      "website": "https://solarluna.co",
      "by_email": true,
      "by_phone": false,
      "category": "Renewable Energy",
      "currency": "EUR",
      "facebook": "facebook.com/solarluna.co",
      "industry": "Clean Technology",
      "linkedin": "linkedin.com/in/luciamartinezsolar",
      "position": "Head of Partnerships",
      "timezone": "Europe/Madrid",
      "whatsapp": "+34 612 458 937",
      "zip_code": "08010",
      "by_social": true,
      "leadSource": "Inbound - Company Website",
      "companySize": "51-200",
      "jobFunction": "Business Development",
      "social_tiktok": "tiktok.com/@luciasolarluna",
      "social_twitter": "twitter.com/luciamartinez_es",
      "social_facebook": "facebook.com/solarluna.co",
      "social_linkedin": "linkedin.com/in/luciamartinezsolar",
      "social_whatsapp": "wa.me/34612458937",
      "name_value_pairs": [
        { "name": "Lead Temperature", "value": "Hot" },
        { "name": "Meeting Scheduled", "value": "2025-11-02" }
      ],
      "social_instagram": "instagram.com/solarluna.energy",
      "additional_details": "Lucia is exploring potential distributors in Southern Europe. Prefers morning calls between 9–11 AM local time."
    }
  },
  "message": "Contact created successfully"
}`
                        }
                    ]
                };

            case 'Get Contact':
                return {
                    title: 'Get Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific contact by its ID. The contact ID must be a valid UUID format.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The response includes the contact's details along with associated owner and company information.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "John",
    "last_name": "Doe",
    "email": "john.doe@example.com",
    "phone": "+1234567890",
    "status": "Active",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "acc_123",
    "first_name": "Jane",
    "last_name": "Smith",
      "email": "jane@company.com"
    },
    "companies": {
      "id": "comp_456",
      "name": "Acme Corp",
      "website": "https://acme.com",
      "industry": "Technology"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Contact':
                return {
                    title: 'Update Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing contact. You can update any field by including it in the request body.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                If updating the email address, it must be unique within your workspace. Only provided fields will be updated. Unrecognized body fields return HTTP 400.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'first_name', type: 'string', required: false, description: 'Updated first name' },
                                { name: 'last_name', type: 'string', required: false, description: 'Updated last name' },
                                { name: 'email', type: 'string', required: false, description: 'Updated email address' },
                                { name: 'phone', type: 'string', required: false, description: 'Updated phone number' },
                                { name: 'status', type: 'string', required: false, description: 'Updated status' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Updated record owner account ID' },
                                { name: 'assigned_to', type: 'string', required: false, description: 'Updated assignee account UUID, email, unique display name, or null to unassign' },
                                { name: 'company_id', type: 'string', required: false, description: 'Updated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Updated custom fields' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "first_name": "Jane",
    "last_name": "Smith",
    "email": "jane.smith@example.com",
    "status": "Active"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "first_name": "Jane",
    "last_name": "Smith",
    "email": "jane.smith@example.com",
    "phone": "+1234567890",
    "status": "Active",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Contact updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Contact':
                return {
                    title: 'Delete Contact',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a contact from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The contact must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Contact deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "John Doe"
  }
}`
                        }
                    ]
                };

            case 'Enable Client Portal':
                return {
                    title: 'Enable Client Portal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Enable client portal access for a contact. This endpoint creates a new portal account if one doesn't exist, 
                                or re-enables an existing disabled portal account. If the portal is already enabled, the request will succeed without changes.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can optionally provide an email and password for the portal account. If email is not provided, the contact's 
                                email will be used. If password is not provided, a secure password will be auto-generated and returned in the response.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> The contact must exist and belong to your workspace. Email addresses must be valid, 
                                and passwords must be at least 8 characters long.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'contact_id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'email', type: 'string', required: false, description: 'Email address for the portal account. If not provided, the contact\'s email will be used.' },
                                { name: 'password', type: 'string', required: false, description: 'Password for the portal account. Must be at least 8 characters. If not provided, a secure password will be auto-generated.' },
                                { name: 'auto_generate_password', type: 'boolean', required: false, description: 'Set to true to auto-generate a password, or false to require a password in the request body. Defaults to true if password is not provided.' }
                            ]
                        },
                        {
                            title: 'Example Request - Create New Portal (Auto-generate Password)',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/client-portal-accounts/123e4567-e89b-12d3-a456-426614174000/enable" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "client@example.com",
    "auto_generate_password": true
  }'`
                        },
                        {
                            title: 'Example Request - Create New Portal (Custom Password)',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/client-portal-accounts/123e4567-e89b-12d3-a456-426614174000/enable" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "client@example.com",
    "password": "SecurePassword123!",
    "auto_generate_password": false
  }'`
                        },
                        {
                            title: 'Example Request - Re-enable Disabled Portal',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/client-portal-accounts/123e4567-e89b-12d3-a456-426614174000/enable" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "auto_generate_password": true
  }'`
                        },
                        {
                            title: 'Example Response - New Portal Created (Password Generated)',
                            type: 'response',
                            content: `{
  "data": {
    "id": "789e0123-e89b-12d3-a456-426614174005",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "contact_id": "123e4567-e89b-12d3-a456-426614174000",
    "email": "client@example.com",
    "is_enabled": true,
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "last_login_at": null,
    "settings_json": null
  },
  "message": "Client portal enabled successfully Password has been auto-generated.",
  "generated_password": "Xk9#mP2$vL7w"
}`
                        },
                        {
                            title: 'Example Response - Portal Already Enabled',
                            type: 'response',
                            content: `{
  "data": {
    "id": "789e0123-e89b-12d3-a456-426614174005",
    "contact_id": "123e4567-e89b-12d3-a456-426614174000",
    "email": "client@example.com",
    "is_enabled": true
  },
  "message": "Client portal is already enabled for this contact"
}`
                        },
                        {
                            title: 'Error Responses',
                            type: 'response',
                            content: `// Contact not found
{
  "error": "Not Found",
  "message": "Contact not found",
  "code": "CONTACT_NOT_FOUND"
}

// Missing email when contact has no email
{
  "error": "Bad Request",
  "message": "Email is required. Contact does not have an email, please provide one in the request body.",
  "code": "MISSING_EMAIL"
}

// Invalid email format
{
  "error": "Validation Error",
  "message": "Invalid email format",
  "code": "VALIDATION_ERROR",
  "details": {
    "field": "email",
    "message": "Email must be a valid email address"
  }
}

// Password too short
{
  "error": "Validation Error",
  "message": "Password must be at least 8 characters long",
  "code": "VALIDATION_ERROR",
  "details": {
    "field": "password",
    "message": "Password must be at least 8 characters long"
  }
}

// Missing password when auto_generate_password is false
{
  "error": "Bad Request",
  "message": "Password is required when auto_generate_password is set to false",
  "code": "MISSING_PASSWORD"
}`
                        }
                    ]
                };

            case 'Disable Client Portal':
                return {
                    title: 'Disable Client Portal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Disable client portal access for a contact. This endpoint deactivates the portal account, preventing the 
                                contact from accessing the client portal. The portal account remains in the system and can be re-enabled later.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                If the portal is already disabled, the request will succeed without making changes. The contact must exist 
                                and have a portal account associated with it.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> Disabling a portal does not delete the portal account or any associated data. 
                                You can re-enable it at any time using the Enable Client Portal endpoint.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'contact_id', type: 'string', required: true, description: 'The contact ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/client-portal-accounts/123e4567-e89b-12d3-a456-426614174000/disable" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response - Portal Disabled',
                            type: 'response',
                            content: `{
  "data": {
    "id": "789e0123-e89b-12d3-a456-426614174005",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "contact_id": "123e4567-e89b-12d3-a456-426614174000",
    "email": "client@example.com",
    "is_enabled": false,
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z",
    "last_login_at": "2024-01-15T10:35:00Z",
    "settings_json": null
  },
  "message": "Client portal disabled successfully"
}`
                        },
                        {
                            title: 'Example Response - Portal Already Disabled',
                            type: 'response',
                            content: `{
  "data": {
    "id": "789e0123-e89b-12d3-a456-426614174005",
    "contact_id": "123e4567-e89b-12d3-a456-426614174000",
    "is_enabled": false
  },
  "message": "Client portal is already disabled for this contact"
}`
                        },
                        {
                            title: 'Error Responses',
                            type: 'response',
                            content: `// Contact not found
{
  "error": "Not Found",
  "message": "Contact not found",
  "code": "CONTACT_NOT_FOUND"
}

// Portal account not found for contact
{
  "error": "Not Found",
  "message": "No portal account found for this contact",
  "code": "PORTAL_ACCOUNT_NOT_FOUND"
}

// Invalid contact ID format
{
  "error": "Bad Request",
  "message": "Invalid contact ID format",
  "code": "INVALID_CONTACT_ID"
}`
                        }
                    ]
                };

            case 'List Contact Activities':
                return {
                    title: 'List Contact Activities',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                List timeline activities for a contact (notes and tasks). Optional query <code style={{ color: 'var(--text-primary)' }}>type=note,task</code> filters results. Requires read permission.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path & query parameters',
                            type: 'params',
                            content: [
                                { name: 'contactId', type: 'string', required: true, description: 'Contact UUID (path)' },
                                { name: 'page', type: 'integer', required: false, default: '1' },
                                { name: 'limit', type: 'integer', required: false, default: '50' },
                                { name: 'type', type: 'string', required: false, description: 'Comma-separated: note, task' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities?page=1&limit=50&type=note,task" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        }
                    ]
                };

            case 'Create Contact Activity':
                return {
                    title: 'Create Contact Activity',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Set <code style={{ color: 'var(--text-primary)' }}>type</code> to <code style={{ color: 'var(--text-primary)' }}>note</code> or <code style={{ color: 'var(--text-primary)' }}>task</code>. Tasks create a project task, a contact activity, then set <code style={{ color: 'var(--text-primary)' }}>settings_json.contact_activity_id</code> on the task. Updates via <code style={{ color: 'var(--text-primary)' }}>PUT /api/projects/:projectId/tasks/:taskId</code> sync the linked activity.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example: create note',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"note","title":"Follow-up","content":"Called the customer."}'`
                        },
                        {
                            title: 'Example: create task',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"task","title":"Send proposal","status":"To Do","priority":"High","due_date":"2026-04-25","assignees":[]}'`
                        }
                    ]
                };

            case 'Get Contact Activity':
                return {
                    title: 'Get Contact Activity',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Fetch one activity by ID; it must belong to the contact and workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        }
                    ]
                };

            case 'Update Contact Activity':
                return {
                    title: 'Update Contact Activity',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                PATCH a note or task-linked activity. For tasks, the underlying task row is updated and timeline data stays in sync. Optional <code style={{ color: 'var(--text-primary)' }}>date</code> sets the activity timeline date (separate from task <code style={{ color: 'var(--text-primary)' }}>due_date</code>).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PATCH "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Updated","status":"In Progress"}'`
                        }
                    ]
                };

            case 'Delete Contact Activity':
                return {
                    title: 'Delete Contact Activity',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Deletes a timeline activity. For tasks, the linked task is removed when present. Deleting a task via the projects API also removes the linked activity when <code style={{ color: 'var(--text-primary)' }}>contact_activity_id</code> is set on the task.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/contacts/123e4567-e89b-12d3-a456-426614174000/activities/aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        }
                    ]
                };

            case 'List Deals':
                return {
                    title: 'List Deals',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of deals with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter deals by status, owner, and search across name fields. 
                                Results are paginated with a maximum of 100 deals per page.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of deals per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search deals by name' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by deal status (Open, Won, Lost, In Progress)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Filter by deal owner ID' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, value, status)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/deals?page=1&limit=20&status=Open&sort_by=value&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Enterprise Software License",
      "value": 50000,
      "status": "Open",
      "owner_id": "456e7890-e89b-12d3-a456-426614174001",
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "contact_id": "789e0123-e89b-12d3-a456-426614174002",
      "company_id": "012e3456-e89b-12d3-a456-426614174003",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "fields": {},
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Open",
      "owner_id": null
    },
    "sort": {
      "field": "value",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Deal':
                return {
                    title: 'Create Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new deal in your workspace. Deals represent sales opportunities with associated contacts and companies.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal name is required. You can optionally set the value, status, and associate it with contacts and companies.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Deal name' },
                                { name: 'value', type: 'number', required: false, default: '0', description: 'Deal value' },
                                { name: 'status', type: 'string', required: false, default: 'Open', description: 'Deal status (Open, Won, Lost, In Progress)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Deal owner ID (defaults to API key owner)' },
                                { name: 'contact_id', type: 'string', required: false, description: 'Associated contact ID' },
                                { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, default: '{}', description: 'Custom fields as key-value pairs' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/deals" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "priority": "High",
    "description": "Annual enterprise software license renewal",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Deal created successfully"
}`
                        }
                    ]
                };

            case 'Get Deal':
                return {
                    title: 'Get Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific deal by its ID. Returns complete deal information including associated contact and company details.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal must exist and belong to your workspace. Returns 404 if the deal is not found.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 50000,
    "status": "Open",
    "priority": "High",
    "description": "Annual enterprise software license renewal",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "first_name": "John",
      "last_name": "Smith",
      "email": "john.smith@company.com"
    },
    "contacts": {
      "id": "789e0123-e89b-12d3-a456-426614174002",
      "first_name": "Jane",
      "last_name": "Doe",
      "email": "jane.doe@company.com",
      "phone": "+1234567890"
    },
    "companies": {
      "id": "012e3456-e89b-12d3-a456-426614174003",
      "name": "Acme Corporation",
      "website": "https://acme.com",
      "industry": "Technology"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Deal':
                return {
                    title: 'Update Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing deal. You can modify any deal field including name, value, status, priority, and associated contacts or companies.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The deal must exist and belong to your workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Deal name' },
                                { name: 'value', type: 'number', required: false, description: 'Deal value in cents' },
                                { name: 'status', type: 'string', required: false, description: 'Deal status (Open, Won, Lost, In Progress)' },
                                { name: 'priority', type: 'string', required: false, description: 'Deal priority (Low, Medium, High)' },
                                { name: 'description', type: 'string', required: false, description: 'Deal description' },
                                { name: 'expected_close_date', type: 'string', required: false, description: 'Expected close date (YYYY-MM-DD format)' },
                                { name: 'owner_id', type: 'string', required: false, description: 'Deal owner ID' },
                                { name: 'contact_id', type: 'string', required: false, description: 'Associated contact ID' },
                                { name: 'company_id', type: 'string', required: false, description: 'Associated company ID' },
                                { name: 'fields', type: 'object', required: false, description: 'Custom fields as key-value pairs' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "status": "Won",
    "value": 55000,
    "description": "Deal closed successfully with additional services"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License",
    "value": 55000,
    "status": "Won",
    "priority": "High",
    "description": "Deal closed successfully with additional services",
    "expected_close_date": "2024-12-31",
    "owner_id": "456e7890-e89b-12d3-a456-426614174001",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "contact_id": "789e0123-e89b-12d3-a456-426614174002",
    "company_id": "012e3456-e89b-12d3-a456-426614174003",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "fields": {},
    "slug": "enterprise-software-license",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Deal updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Deal':
                return {
                    title: 'Delete Deal',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a deal from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The deal must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The deal ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/deals/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Deal deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Enterprise Software License"
  }
}`
                        }
                    ]
                };

            case 'List Products':
                return {
                    title: 'List Products',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of products with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter products by status, category, product type, and search across name, description, and SKU fields. 
                                Results are paginated with a maximum of 100 products per page.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of products per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search products by name, description, or SKU' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by product category' },
                                { name: 'product_type', type: 'string', required: false, description: 'Filter by product type (Product, Service, Digital)' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, price, status, category, product_type)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/products?page=1&limit=20&status=Active&sort_by=price&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Premium CRM License",
      "description": "Full-featured CRM solution for enterprise businesses",
      "category": "Software",
      "subcategory": "CRM",
      "sku": "CRM-PREM-001",
      "price": 299.00,
      "cost": 150.00,
      "currency": "USD",
      "status": "Active",
      "product_type": "Service",
      "image_url": "https://example.com/product-image.jpg",
      "settings_json": {
        "inventory": {
          "trackInventory": true,
          "quantity": 150,
          "lowStockThreshold": 10,
          "location": "Digital"
        },
        "tags": ["CRM", "Enterprise", "Analytics"],
        "features": ["Unlimited contacts", "Advanced reporting"],
        "featured": true,
        "salesCount": 45,
        "revenue": 13455.00
      },
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z"
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Active",
      "category": null,
      "product_type": null
    },
    "sort": {
      "field": "price",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Product':
                return {
                    title: 'Create Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new product in your workspace. Products represent items or services in your catalog with pricing and inventory information.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product name and SKU are required. You can optionally set the price, cost, category, status, and additional settings.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Product name' },
                                { name: 'description', type: 'string', required: false, description: 'Product description' },
                                { name: 'category', type: 'string', required: false, description: 'Product category' },
                                { name: 'subcategory', type: 'string', required: false, description: 'Product subcategory' },
                                { name: 'sku', type: 'string', required: true, description: 'Product SKU (must be unique in workspace)' },
                                { name: 'price', type: 'number', required: true, description: 'Product price' },
                                { name: 'cost', type: 'number', required: false, default: '0', description: 'Product cost' },
                                { name: 'currency', type: 'string', required: false, default: 'USD', description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'product_type', type: 'string', required: false, default: 'Product', description: 'Product type (Product, Service, Digital)' },
                                { name: 'image_url', type: 'string', required: false, description: 'Product image URL' },
                                { name: 'settings_json', type: 'object', required: false, default: '{}', description: 'Additional product settings (inventory, tags, features, etc.)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/products" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"]
    }
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": null,
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"]
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Product created successfully"
}`
                        }
                    ]
                };

            case 'Get Product':
                return {
                    title: 'Get Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific product by its ID. Returns complete product information including settings and metadata.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product must exist and belong to your workspace. Returns 404 if the product is not found.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 299.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": "https://example.com/product-image.jpg",
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"],
      "featured": true,
      "salesCount": 45,
      "revenue": 13455.00
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  }
}`
                        }
                    ]
                };

            case 'Update Product':
                return {
                    title: 'Update Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing product. You can modify any product field including name, price, status, category, and settings.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The product must exist and belong to your workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Product name' },
                                { name: 'description', type: 'string', required: false, description: 'Product description' },
                                { name: 'category', type: 'string', required: false, description: 'Product category' },
                                { name: 'subcategory', type: 'string', required: false, description: 'Product subcategory' },
                                { name: 'sku', type: 'string', required: false, description: 'Product SKU (must be unique in workspace)' },
                                { name: 'price', type: 'number', required: false, description: 'Product price' },
                                { name: 'cost', type: 'number', required: false, description: 'Product cost' },
                                { name: 'currency', type: 'string', required: false, description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, description: 'Product status (Active, Draft, Archived, Discontinued)' },
                                { name: 'product_type', type: 'string', required: false, description: 'Product type (Product, Service, Digital)' },
                                { name: 'image_url', type: 'string', required: false, description: 'Product image URL' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Additional product settings' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "price": 349.00,
    "status": "Active",
    "settings_json": {
      "featured": true,
      "salesCount": 50
    }
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License",
    "description": "Full-featured CRM solution for enterprise businesses",
    "category": "Software",
    "subcategory": "CRM",
    "sku": "CRM-PREM-001",
    "price": 349.00,
    "cost": 150.00,
    "currency": "USD",
    "status": "Active",
    "product_type": "Service",
    "image_url": null,
    "settings_json": {
      "inventory": {
        "trackInventory": true,
        "quantity": 150,
        "lowStockThreshold": 10,
        "location": "Digital"
      },
      "tags": ["CRM", "Enterprise", "Analytics"],
      "features": ["Unlimited contacts", "Advanced reporting"],
      "featured": true,
      "salesCount": 50,
      "revenue": 13455.00
    },
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Product updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Product':
                return {
                    title: 'Delete Product',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a product from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The product must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The product ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/products/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Product deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Premium CRM License"
  }
}`
                        }
                    ]
                };

            case 'List Budgets':
                return {
                    title: 'List Budgets',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of budgets with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter budgets by status, category, period, and search across name and description fields. 
                                Results are paginated with a maximum of 100 budgets per page.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of budgets per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search budgets by name or description' },
                                { name: 'status', type: 'string', required: false, description: 'Filter by budget status (Active, Inactive, Completed)' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Filter by budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'Sort field (created_at, updated_at, name, allocated_amount, spent_amount, status, category, period)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets?page=1&limit=20&status=Active&sort_by=allocated_amount&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "description": "Q1 marketing budget for digital campaigns, events, and content creation",
      "category": "Marketing",
      "period": "Quarterly",
      "allocated_amount": 50000,
      "spent_amount": 32450,
      "remaining_amount": 17550,
      "currency": "USD",
      "status": "Active",
      "start_date": "2024-01-01",
      "end_date": "2024-03-31",
      "created_by": "456e7890-e89b-12d3-a456-426614174001",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z",
      "accounts": {
        "id": "456e7890-e89b-12d3-a456-426614174001",
        "first_name": "John",
        "last_name": "Smith",
        "email": "john.smith@company.com"
      }
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "status": "Active",
      "category": null,
      "period": null
    },
    "sort": {
      "field": "allocated",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Budget':
                return {
                    title: 'Create Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new budget in your workspace. Budgets help track financial planning and spending across different categories and periods.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget name and allocated amount are required. You can optionally set the category, period, status, and date range.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> The <code>remaining_amount</code> field is automatically calculated by the database as <code>allocated_amount - spent_amount</code> and cannot be manually set.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'Budget name' },
                                { name: 'description', type: 'string', required: false, description: 'Budget description' },
                                { name: 'category', type: 'string', required: false, description: 'Budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'allocated', type: 'number', required: true, description: 'Total allocated budget amount (stored as allocated_amount in database)' },
                                { name: 'currency', type: 'string', required: false, default: 'USD', description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Budget status (Active, Inactive, Completed)' },
                                { name: 'start_date', type: 'string', required: false, description: 'Budget start date (YYYY-MM-DD format)' },
                                { name: 'end_date', type: 'string', required: false, description: 'Budget end date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/budgets" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated": 50000,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 50000,
    "spent_amount": 0,
    "remaining_amount": 50000,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z"
  },
  "message": "Budget created successfully"
}`
                        }
                    ]
                };

            case 'Get Budget':
                return {
                    title: 'Get Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific budget by its ID. Returns complete budget information including financial details and metadata.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. Returns 404 if the budget is not found.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Q1 marketing budget for digital campaigns, events, and content creation",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 50000,
    "spent_amount": 32450,
    "remaining_amount": 17550,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "accounts": {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "first_name": "John",
      "last_name": "Smith",
      "email": "john.smith@company.com"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Budget':
                return {
                    title: 'Update Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing budget. You can modify any budget field including name, allocated amount, status, category, and date range.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Only provided fields will be updated. The budget must exist and belong to your workspace.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                <strong>Note:</strong> The <code>remaining_amount</code> field is automatically recalculated when <code>allocated</code> is updated and cannot be manually set.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Budget name' },
                                { name: 'description', type: 'string', required: false, description: 'Budget description' },
                                { name: 'category', type: 'string', required: false, description: 'Budget category (Marketing, Sales, Product, Operations)' },
                                { name: 'period', type: 'string', required: false, description: 'Budget period (Monthly, Quarterly, Yearly)' },
                                { name: 'allocated', type: 'number', required: false, description: 'Total allocated budget amount (stored as allocated_amount in database)' },
                                { name: 'currency', type: 'string', required: false, description: 'Currency code' },
                                { name: 'status', type: 'string', required: false, description: 'Budget status (Active, Inactive, Completed)' },
                                { name: 'start_date', type: 'string', required: false, description: 'Budget start date (YYYY-MM-DD format)' },
                                { name: 'end_date', type: 'string', required: false, description: 'Budget end date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "allocated": 55000,
    "status": "Active",
    "description": "Updated Q1 marketing budget with additional allocation for new campaigns"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024",
    "description": "Updated Q1 marketing budget with additional allocation for new campaigns",
    "category": "Marketing",
    "period": "Quarterly",
    "allocated_amount": 55000,
    "spent_amount": 32450,
    "remaining_amount": 22550,
    "currency": "USD",
    "status": "Active",
    "start_date": "2024-01-01",
    "end_date": "2024-03-31",
    "created_by": "456e7890-e89b-12d3-a456-426614174001",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z"
  },
  "message": "Budget updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Budget':
                return {
                    title: 'Delete Budget',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete a budget from your workspace. This action cannot be undone.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. You will receive confirmation of the deletion.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Budget deleted successfully",
  "data": {
    "id": "123e4567-e89b-12d3-a456-426614174000",
    "name": "Marketing Budget Q1 2024"
  }
}`
                        }
                    ]
                };

            case 'List Budget Expenses':
                return {
                    title: 'List Budget Expenses',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of expenses for a specific budget with optional filtering and search capabilities.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                You can filter expenses by category and search across description and category fields. 
                                Results are paginated with a maximum of 100 expenses per page.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Query Parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number for pagination' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Number of expenses per page (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search expenses by description or category' },
                                { name: 'category', type: 'string', required: false, description: 'Filter by expense category' },
                                { name: 'sort_by', type: 'string', required: false, default: 'date', description: 'Sort field (date, amount, description, category, created_at)' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'Sort order (asc, desc)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses?page=1&limit=20&category=Marketing&sort_by=date&sort_order=desc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "456e7890-e89b-12d3-a456-426614174001",
      "budget_id": "123e4567-e89b-12d3-a456-426614174000",
      "description": "Google Ads Campaign",
      "amount": 15000,
      "category": "Marketing",
      "date": "2024-01-15",
      "created_by": "789e0123-e89b-12d3-a456-426614174002",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
      "created_at": "2024-01-15T10:30:00Z",
      "updated_at": "2024-01-15T10:30:00Z",
      "budgets": {
        "id": "123e4567-e89b-12d3-a456-426614174000",
        "name": "Marketing Budget Q1 2024",
        "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
      }
    }
  ],
  "pagination": {
    "page": 1,
    "limit": 20,
    "total": 1,
    "pages": 1
  },
  "meta": {
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "filters": {
      "search": null,
      "category": "Marketing"
    },
    "sort": {
      "field": "date",
      "order": "desc"
    }
  }
}`
                        }
                    ]
                };

            case 'Create Budget Expense':
                return {
                    title: 'Create Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a new expense for a specific budget. The expense will be automatically added to the budget's spent amount.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The budget must exist and belong to your workspace. The expense amount cannot exceed the remaining budget allocation.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'description', type: 'string', required: true, description: 'Description of the expense' },
                                { name: 'amount', type: 'number', required: true, description: 'Expense amount (positive number)' },
                                { name: 'category', type: 'string', required: true, description: 'Expense category' },
                                { name: 'date', type: 'string', required: true, description: 'Expense date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  },
  "message": "Budget expense created successfully"
}`
                        }
                    ]
                };

            case 'Get Budget Expense':
                return {
                    title: 'Get Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a specific expense for a budget by its ID.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Google Ads Campaign",
    "amount": 15000,
    "category": "Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T10:30:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  }
}`
                        }
                    ]
                };

            case 'Update Budget Expense':
                return {
                    title: 'Update Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update an existing expense for a budget. The budget's spent amount will be automatically recalculated.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace. The updated amount cannot exceed the remaining budget allocation.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'description', type: 'string', required: false, description: 'Description of the expense' },
                                { name: 'amount', type: 'number', required: false, description: 'Expense amount (positive number)' },
                                { name: 'category', type: 'string', required: false, description: 'Expense category' },
                                { name: 'date', type: 'string', required: false, description: 'Expense date (YYYY-MM-DD format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "description": "Updated Google Ads Campaign",
    "amount": 18000,
    "category": "Digital Marketing"
  }'`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "data": {
    "id": "456e7890-e89b-12d3-a456-426614174001",
    "budget_id": "123e4567-e89b-12d3-a456-426614174000",
    "description": "Updated Google Ads Campaign",
    "amount": 18000,
    "category": "Digital Marketing",
    "date": "2024-01-15",
    "created_by": "789e0123-e89b-12d3-a456-426614174002",
    "workspace_id": "345e6789-e89b-12d3-a456-426614174004",
    "created_at": "2024-01-15T10:30:00Z",
    "updated_at": "2024-01-15T11:45:00Z",
    "budgets": {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "name": "Marketing Budget Q1 2024",
      "workspace_id": "345e6789-e89b-12d3-a456-426614174004"
    }
  },
  "message": "Budget expense updated successfully"
}`
                        }
                    ]
                };

            case 'Delete Budget Expense':
                return {
                    title: 'Delete Budget Expense',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete an expense from a budget. The budget's spent amount will be automatically recalculated.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                The expense must exist and belong to the specified budget in your workspace. This action cannot be undone.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'budgetId', type: 'string', required: true, description: 'The budget ID (UUID format)' },
                                { name: 'expenseId', type: 'string', required: true, description: 'The expense ID (UUID format)' }
                            ]
                        },
                        {
                            title: 'Example Request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/budgets/123e4567-e89b-12d3-a456-426614174000/expenses/456e7890-e89b-12d3-a456-426614174001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example Response',
                            type: 'response',
                            content: `{
  "message": "Budget expense deleted successfully"
}`
                        }
                    ]
                };

            case 'List Projects':
                return {
                    title: 'List Projects',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of projects in the workspace. Matches the <strong>Projects &amp; Tasks</strong> feature in the app.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                With a NinjaPipe API key (<code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>np_</code>), the workspace is implied by the key. With a <strong>Supabase session</strong> or <strong>OAuth integration</strong> Bearer token, pass <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>workspace_id</code> (query or body).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Query parameters',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Required for session JWT / multi-workspace OAuth; omitted when using an np_ API key (workspace comes from the key).' },
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Page size (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search name and description' },
                                { name: 'status', type: 'string', required: false, description: 'Planning | In Progress | Completed | On Hold | Cancelled' },
                                { name: 'priority', type: 'string', required: false, description: 'High | Medium | Low' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'created_at | updated_at | name | due_date' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'asc | desc' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/projects?page=1&limit=20" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{
  "data": [
    {
      "id": "dbe92bb3-3239-4710-8216-dccc9ffceaa5",
      "workspace_id": "6d07f58c-15a7-42da-93f8-484b3d5fccb9",
      "name": "Q4 Launch",
      "description": null,
      "status": "Planning",
      "priority": "Medium",
      "progress": 0,
      "total_tasks": 0,
      "completed_tasks": 0,
      "due_date": null,
      "team": [],
      "tags": [],
      "settings_json": {},
      "created_by": "48b2edd4-3f3b-4a78-9d13-e61ebc524334",
      "created_at": "2026-04-20T02:30:11.700998+00:00",
      "updated_at": "2026-04-20T02:30:11.700998+00:00"
    }
  ],
  "pagination": { "page": 1, "limit": 20, "total": 1, "pages": 1 },
  "meta": { "workspace_id": "6d07f58c-15a7-42da-93f8-484b3d5fccb9" }
}`
                        }
                    ]
                };

            case 'Create Project':
                return {
                    title: 'Create Project',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a project. Requires <strong>tasks</strong> create permission (session) or write permission (API key).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Required for session JWT / OAuth when not implied by token.' },
                                { name: 'name', type: 'string', required: true, description: 'Project name' },
                                { name: 'description', type: 'string', required: false, description: 'Description' },
                                { name: 'status', type: 'string', required: false, default: 'Planning', description: 'Planning | In Progress | Completed | On Hold | Cancelled' },
                                { name: 'priority', type: 'string', required: false, default: 'Medium', description: 'High | Medium | Low' },
                                { name: 'due_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'team', type: 'array', required: false, description: 'Team JSON array' },
                                { name: 'tags', type: 'array', required: false, description: 'Tags' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Optional metadata (e.g. contact_id for linked contact)' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/projects" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"New project","status":"Planning","priority":"Medium"}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "name": "New project", "...": "..." }, "message": "Project created successfully" }`
                        }
                    ]
                };

            case 'Get Project':
                return {
                    title: 'Get Project',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Fetch one project by ID. The project must belong to your workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'uuid', required: true, description: 'Project id (in URL path /projects/:id)' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "dbe92bb3-3239-4710-8216-dccc9ffceaa5", "name": "...", "settings_json": {}, "...": "..." } }`
                        }
                    ]
                };

            case 'Update Project':
                return {
                    title: 'Update Project',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Partial update. Send only fields to change. You may update <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>settings_json</code> (for example link a contact).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body (all optional)',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: false, description: 'Project name' },
                                { name: 'description', type: 'string', required: false, description: 'Description' },
                                { name: 'status', type: 'string', required: false, description: 'Status enum' },
                                { name: 'priority', type: 'string', required: false, description: 'Priority enum' },
                                { name: 'due_date', type: 'string', required: false, description: 'YYYY-MM-DD or null' },
                                { name: 'team', type: 'array', required: false, description: 'Team' },
                                { name: 'tags', type: 'array', required: false, description: 'Tags' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Merged/replace per server rules' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Updated name","status":"In Progress"}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "...": "..." }, "message": "Project updated successfully" }`
                        }
                    ]
                };

            case 'Delete Project':
                return {
                    title: 'Delete Project',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Deletes the project and its tasks (database cascade). Requires delete permission on <strong>tasks</strong> for session auth.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Project deleted successfully" }`
                        }
                    ]
                };

            case 'List General Tasks':
                return {
                    title: 'List General Tasks',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Returns a flat list of <strong>workspace-level general tasks</strong> where <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>project_id</code> is null. Use <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> to build a tree in your client. For tasks inside a project, see <a href={getUrlFromPage('List Tasks')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>List Tasks</a>.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Query parameters',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth multi-workspace' },
                                { name: 'status', type: 'string', required: false, description: 'To Do | In Progress | Done | Cancelled' },
                                { name: 'priority', type: 'string', required: false, description: 'High | Medium | Low' },
                                { name: 'parent_id', type: 'uuid', required: false, description: 'Filter by parent; empty string for root tasks' },
                                { name: 'sort_by', type: 'string', required: false, default: 'order_index', description: 'order_index | created_at | updated_at | title | due_date' },
                                { name: 'sort_order', type: 'string', required: false, default: 'asc', description: 'asc | desc' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/tasks?sort_by=order_index&sort_order=asc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": [ { "id": "...", "project_id": null, "title": "...", "parent_id": null, "order_index": 10, "...": "..." } ], "meta": { "workspace_id": "...", "scope": "general" } }`
                        }
                    ]
                };

            case 'Create General Task':
                return {
                    title: 'Create General Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a workspace-level general task or subtask. Optional <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> must reference another general task in the same workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth' },
                                { name: 'title', type: 'string', required: true, description: 'Task title' },
                                { name: 'description', type: 'string', required: false, description: 'Description' },
                                { name: 'status', type: 'string', required: false, default: 'To Do', description: 'Task status enum' },
                                { name: 'priority', type: 'string', required: false, default: 'Medium', description: 'High | Medium | Low' },
                                { name: 'assignee_id', type: 'uuid', required: false, description: 'Account id of assignee' },
                                { name: 'due_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'parent_id', type: 'uuid', required: false, description: 'Parent general task id for subtasks' },
                                { name: 'order_index', type: 'integer', required: false, description: 'Sibling order; auto if omitted' },
                                { name: 'tags', type: 'array', required: false, description: 'Tags' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Extra settings' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/tasks" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Review vendor contract","status":"To Do","priority":"High"}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "project_id": null, "...": "..." }, "message": "Task created successfully" }`
                        }
                    ]
                };

            case 'Get General Task':
                return {
                    title: 'Get General Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Fetch a single general task by ID. Returns <strong>404</strong> if the task belongs to a project.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "project_id": null, "title": "...", "status": "Done", "parent_id": null, "order_index": 10, "...": "..." } }`
                        }
                    ]
                };

            case 'Update General Task':
                return {
                    title: 'Update General Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update any general task fields. Setting <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> to an invalid or cyclic parent returns <strong>400</strong> or <strong>409</strong> (<code>TASK_PARENT_CYCLE</code>).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"Done"}'`
                        },
                        {
                            title: 'Conflict (cycle)',
                            type: 'response',
                            content: `{
  "error": "Conflict",
  "message": "Cannot set parent to a descendant of this task",
  "code": "TASK_PARENT_CYCLE"
}`
                        }
                    ]
                };

            case 'Delete General Task':
                return {
                    title: 'Delete General Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Delete a general task. Child tasks may cascade per database rules.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Task deleted successfully" }`
                        }
                    ]
                };

            case 'Reorder General Tasks':
                return {
                    title: 'Reorder General Tasks',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Batch-update <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>order_index</code> for sibling general tasks (typically root or same parent). Up to 500 updates per request.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth' },
                                { name: 'updates', type: 'array', required: true, description: 'Array of { id: task uuid, order_index: number }' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/tasks/reorder" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"updates":[{"id":"a97381c8-75d3-4773-92d7-80ad97cf06f9","order_index":0},{"id":"f48104b8-8cd9-4a94-ae5b-3652fc05a5fb","order_index":10}]}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Tasks reordered successfully", "meta": { "updated": 2, "workspace_id": "...", "scope": "general" } }`
                        }
                    ]
                };

            case 'List Tasks':
                return {
                    title: 'List Tasks',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Returns a flat list of tasks for the project. Use <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> to build a tree in your client. For workspace-level tasks not tied to a project, see <a href={getUrlFromPage('List General Tasks')} style={{ color: 'var(--brand-cyan)', textDecoration: 'underline' }}>List General Tasks</a>.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Query parameters',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth multi-workspace' },
                                { name: 'status', type: 'string', required: false, description: 'To Do | In Progress | Done | Cancelled' },
                                { name: 'priority', type: 'string', required: false, description: 'High | Medium | Low' },
                                { name: 'parent_id', type: 'uuid', required: false, description: 'Filter by parent; empty string for root tasks' },
                                { name: 'sort_by', type: 'string', required: false, default: 'order_index', description: 'order_index | created_at | updated_at | title | due_date' },
                                { name: 'sort_order', type: 'string', required: false, default: 'asc', description: 'asc | desc' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks?sort_by=order_index&sort_order=asc" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": [ { "id": "...", "title": "...", "parent_id": null, "order_index": 10, "...": "..." } ], "meta": { "project_id": "...", "workspace_id": "..." } }`
                        }
                    ]
                };

            case 'Create Task':
                return {
                    title: 'Create Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a task or subtask. Optional <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> must reference another task in the same project.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth' },
                                { name: 'title', type: 'string', required: true, description: 'Task title' },
                                { name: 'description', type: 'string', required: false, description: 'Description' },
                                { name: 'status', type: 'string', required: false, default: 'To Do', description: 'Task status enum' },
                                { name: 'priority', type: 'string', required: false, default: 'Medium', description: 'High | Medium | Low' },
                                { name: 'assignee_id', type: 'uuid', required: false, description: 'Account id of assignee' },
                                { name: 'due_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'parent_id', type: 'uuid', required: false, description: 'Parent task id for subtasks' },
                                { name: 'order_index', type: 'integer', required: false, description: 'Sibling order; auto if omitted' },
                                { name: 'tags', type: 'array', required: false, description: 'Tags' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Extra settings' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"Ship release notes","status":"To Do","priority":"High"}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "project_id": "...", "...": "..." }, "message": "Task created successfully" }`
                        }
                    ]
                };

            case 'Get Task':
                return {
                    title: 'Get Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Fetch a single task within a project.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "title": "...", "status": "Done", "parent_id": null, "order_index": 10, "...": "..." } }`
                        }
                    ]
                };

            case 'Update Task':
                return {
                    title: 'Update Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update any task fields. Setting <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>parent_id</code> to an invalid or cyclic parent returns <strong>400</strong> or <strong>409</strong> (<code>TASK_PARENT_CYCLE</code>).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"Done"}'`
                        },
                        {
                            title: 'Conflict (cycle)',
                            type: 'response',
                            content: `{
  "error": "Conflict",
  "message": "Cannot set parent to a descendant of this task",
  "code": "TASK_PARENT_CYCLE"
}`
                        }
                    ]
                };

            case 'Delete Task':
                return {
                    title: 'Delete Task',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Delete a task. Child tasks may cascade per database rules.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks/a97381c8-75d3-4773-92d7-80ad97cf06f9" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Task deleted successfully" }`
                        }
                    ]
                };

            case 'Reorder Tasks':
                return {
                    title: 'Reorder Tasks',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Batch-update <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>order_index</code> for tasks in the same project (typically siblings). Up to 500 updates per request.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth' },
                                { name: 'updates', type: 'array', required: true, description: 'Array of { id: task uuid, order_index: number }' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/projects/dbe92bb3-3239-4710-8216-dccc9ffceaa5/tasks/reorder" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"updates":[{"id":"a97381c8-75d3-4773-92d7-80ad97cf06f9","order_index":0},{"id":"f48104b8-8cd9-4a94-ae5b-3652fc05a5fb","order_index":10}]}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Tasks reordered successfully", "meta": { "updated": 2, "project_id": "...", "workspace_id": "..." } }`
                        }
                    ]
                };

            case 'List Orders':
                return {
                    title: 'List Orders',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve a paginated list of orders in the workspace. Matches the <strong>Orders</strong> page in the app (line items and currency live in <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>settings_json</code>).
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Requires <strong>orders:read</strong> (session role) or API key <strong>read</strong>. With a Supabase session or OAuth token, pass <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>workspace_id</code> in query or body when not implied by the key.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Query parameters',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Required for session JWT / multi-workspace OAuth when not using an np_ key.' },
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Page size (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search order_number, customer_name, customer_email, customer_company' },
                                { name: 'status', type: 'string', required: false, description: 'Pending | Processing | Shipped | Delivered | Completed | Cancelled' },
                                { name: 'payment_status', type: 'string', required: false, description: 'Pending | Paid | Failed | Refunded' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'created_at | updated_at | order_date | total_amount | order_number' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'asc | desc' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/orders?page=1&limit=20" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{
  "data": [ { "id": "...", "order_number": "ORD-...", "customer_name": "...", "total_amount": 99.5, "status": "Processing", "settings_json": { "items": [], "currency": "USD" }, "...": "..." } ],
  "pagination": { "page": 1, "limit": 20, "total": 1, "pages": 1 },
  "meta": { "workspace_id": "6d07f58c-15a7-42da-93f8-484b3d5fccb9" }
}`
                        }
                    ]
                };

            case 'Create Order':
                return {
                    title: 'Create Order',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create an order. Requires <strong>orders:create</strong> (session) or API key <strong>write</strong>. Put line items, currency, and custom fields in <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>settings_json</code> (same shape as the app).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'workspace_id', type: 'uuid', required: false, description: 'Session / OAuth when needed.' },
                                { name: 'order_number', type: 'string', required: false, description: 'Defaults to ORD-&lt;timestamp&gt; if omitted.' },
                                { name: 'customer_name', type: 'string', required: true, description: 'Customer display name' },
                                { name: 'customer_email', type: 'string', required: false, description: 'Email' },
                                { name: 'customer_company', type: 'string', required: false, description: 'Company' },
                                { name: 'contact_id', type: 'uuid', required: false, description: 'Must be a contact in this workspace' },
                                { name: 'total_amount', type: 'number', required: false, default: '0', description: 'Order total' },
                                { name: 'status', type: 'string', required: false, default: 'Processing', description: 'Order status enum' },
                                { name: 'payment_status', type: 'string', required: false, default: 'Pending', description: 'Payment status enum' },
                                { name: 'order_date', type: 'string', required: false, description: 'YYYY-MM-DD (defaults to today)' },
                                { name: 'delivery_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'priority', type: 'string', required: false, description: 'High | Medium | Low or omit' },
                                { name: 'tracking_number', type: 'string', required: false, description: 'Tracking' },
                                { name: 'notes', type: 'string', required: false, description: 'Notes' },
                                { name: 'settings_json', type: 'object', required: false, description: 'items[], currency, custom_fields[], customer_avatar, etc.' },
                                { name: 'comments', type: 'object', required: false, description: 'Optional JSON (if column exists)' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/orders" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"customer_name":"Acme","customer_email":"buyer@acme.com","total_amount":120,"status":"Processing","payment_status":"Pending","settings_json":{"currency":"USD","items":[]}}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "order_number": "ORD-...", "...": "..." }, "message": "Order created successfully" }`
                        }
                    ]
                };

            case 'Get Order':
                return {
                    title: 'Get Order',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Fetch one order by ID within your workspace.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path parameters',
                            type: 'params',
                            content: [
                                { name: 'id', type: 'uuid', required: true, description: 'Order id' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/orders/00000000-0000-0000-0000-000000000001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "id": "...", "settings_json": {}, "...": "..." } }`
                        }
                    ]
                };

            case 'Update Order':
                return {
                    title: 'Update Order',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Partial update. Send only fields to change. Use <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>settings_json</code> for line items and currency.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body (optional fields)',
                            type: 'params',
                            content: [
                                { name: 'order_number', type: 'string', required: false, description: 'Order number' },
                                { name: 'customer_name', type: 'string', required: false, description: 'Customer name' },
                                { name: 'customer_email', type: 'string', required: false, description: 'Email' },
                                { name: 'customer_company', type: 'string', required: false, description: 'Company' },
                                { name: 'contact_id', type: 'uuid | null', required: false, description: 'Linked contact or null' },
                                { name: 'total_amount', type: 'number', required: false, description: 'Total' },
                                { name: 'status', type: 'string', required: false, description: 'Status enum' },
                                { name: 'payment_status', type: 'string', required: false, description: 'Payment enum' },
                                { name: 'order_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'delivery_date', type: 'string', required: false, description: 'YYYY-MM-DD' },
                                { name: 'priority', type: 'string', required: false, description: 'Priority or null' },
                                { name: 'tracking_number', type: 'string', required: false, description: 'Tracking' },
                                { name: 'notes', type: 'string', required: false, description: 'Notes' },
                                { name: 'settings_json', type: 'object', required: false, description: 'Replace line items / currency' },
                                { name: 'comments', type: 'object', required: false, description: 'Optional JSON' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/orders/00000000-0000-0000-0000-000000000001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"Shipped","tracking_number":"TRK-123"}'`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "data": { "...": "..." }, "message": "Order updated successfully" }`
                        }
                    ]
                };

            case 'Delete Order':
                return {
                    title: 'Delete Order',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Permanently delete an order. Requires <strong>orders:delete</strong> or API key write.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/orders/00000000-0000-0000-0000-000000000001" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json"`
                        },
                        {
                            title: 'Example response',
                            type: 'response',
                            content: `{ "message": "Order deleted successfully" }`
                        }
                    ]
                };

            case 'List Lists':
                return {
                    title: 'List Lists',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Retrieve CRM lists (Contact or Company) in the workspace with pagination. Requires <strong>lists:read</strong> (session) or API key <strong>read</strong>.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Query parameters',
                            type: 'params',
                            content: [
                                { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number' },
                                { name: 'limit', type: 'integer', required: false, default: '20', description: 'Page size (max 100)' },
                                { name: 'search', type: 'string', required: false, description: 'Search name and description' },
                                { name: 'list_type', type: 'string', required: false, description: 'Contact | Company' },
                                { name: 'status', type: 'string', required: false, description: 'Active | Inactive | Archived' },
                                { name: 'sort_by', type: 'string', required: false, default: 'created_at', description: 'created_at | updated_at | name' },
                                { name: 'sort_order', type: 'string', required: false, default: 'desc', description: 'asc | desc' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/lists?page=1&limit=20" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}"`
                        }
                    ]
                };

            case 'Create List':
                return {
                    title: 'Create List',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Create a list. Requires <strong>lists:create</strong> or API key <strong>write</strong>.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Request body',
                            type: 'params',
                            content: [
                                { name: 'name', type: 'string', required: true, description: 'List name' },
                                { name: 'description', type: 'string', required: false, description: 'Description' },
                                { name: 'list_type', type: 'string', required: false, default: 'Contact', description: 'Contact | Company' },
                                { name: 'status', type: 'string', required: false, default: 'Active', description: 'Active | Inactive | Archived' },
                                { name: 'color', type: 'string', required: false, description: 'Hex color' },
                                { name: 'icon', type: 'string', required: false, description: 'Icon name' },
                                { name: 'tags', type: 'array', required: false, description: 'String tags' },
                                { name: 'settings', type: 'object', required: false, description: 'Optional JSON settings' }
                            ]
                        },
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/lists" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Newsletter","list_type":"Contact","status":"Active"}'`
                        }
                    ]
                };

            case 'Get List':
                return {
                    title: 'Get List',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Get one list by ID. Response includes <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>member_count</code>.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}"`
                        }
                    ]
                };

            case 'Update List':
                return {
                    title: 'Update List',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Update list fields. You cannot change <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>list_type</code> while the list has members.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PUT "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"status":"Inactive","description":"Paused"}'`
                        }
                    ]
                };

            case 'Delete List':
                return {
                    title: 'Delete List',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Delete a list. Members are removed via cascade.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}"`
                        }
                    ]
                };

            case 'Get List Members':
                return {
                    title: 'Get List Members',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Paginate members. Use <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>expand=true</code> to include <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>contact</code> or <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>company</code> objects for each row.
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X GET "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000/members?page=1&limit=25&expand=true" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}"`
                        }
                    ]
                };

            case 'Update List Member':
                return {
                    title: 'Update List Member',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                PATCH a list member row (e.g. <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>is_important</code>).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X PATCH "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000/members/a1b2c3d4-e89b-12d3-a456-426614174099" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"is_important":true}'`
                        }
                    ]
                };

            case 'Remove List Member':
                return {
                    title: 'Remove List Member',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Remove a member by <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>list_members.id</code> (not contact_id or company_id).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Example request',
                            type: 'curl',
                            content: `curl -X DELETE "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000/members/a1b2c3d4-e89b-12d3-a456-426614174099" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}"`
                        }
                    ]
                };

            case 'Add to List':
                return {
                    title: 'Add to List',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Add a contact or company to a CRM list in your workspace. The list must already exist and must be the correct type: use <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>contact_id</code> for Contact lists and <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>company_id</code> for Company lists.
                            </p>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Requires <strong>lists:create</strong> (session) or API key <strong>write</strong>. If the contact or company is already in the list, the API returns HTTP 200 with <code style={{ backgroundColor: 'var(--input-bg)', padding: '2px 6px', borderRadius: 4, fontSize: 14 }}>already_in_list: true</code> (same idempotent behavior as the workflow &quot;Add to List&quot; action).
                            </p>
                        </div>
                    ),
                    cards: [
                        {
                            title: 'Path Parameters',
                            type: 'params',
                            content: [
                                { name: 'listId', type: 'string', required: true, description: 'The list ID (UUID). Must belong to your workspace.' }
                            ]
                        },
                        {
                            title: 'Request Body',
                            type: 'params',
                            content: [
                                { name: 'contact_id', type: 'string (uuid)', required: false, description: 'Contact to add. Use for Contact-type lists only. Mutually exclusive with company_id.' },
                                { name: 'company_id', type: 'string (uuid)', required: false, description: 'Company to add. Use for Company-type lists only. Mutually exclusive with contact_id.' }
                            ]
                        },
                        {
                            title: 'Example Request (contact)',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000/members" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"contact_id":"456e7890-e89b-12d3-a456-426614174001"}'`
                        },
                        {
                            title: 'Example Request (company)',
                            type: 'curl',
                            content: `curl -X POST "${getApiBaseUrl()}/lists/123e4567-e89b-12d3-a456-426614174000/members" \\
  -H "Authorization: Bearer ${'[YOUR API KEY]...Vw00sc4rlg8'}" \\
  -H "Content-Type: application/json" \\
  -d '{"company_id":"789e0123-e89b-12d3-a456-426614174002"}'`
                        },
                        {
                            title: 'Example Response (201 created)',
                            type: 'response',
                            content: `{
  "data": {
    "list_id": "123e4567-e89b-12d3-a456-426614174000",
    "list_member_id": "a1b2c3d4-e89b-12d3-a456-426614174099",
    "member_type": "contact",
    "member_id": "456e7890-e89b-12d3-a456-426614174001",
    "added_at": "2024-01-15T10:30:00.000Z",
    "already_in_list": false
  },
  "message": "Added to list"
}`
                        },
                        {
                            title: 'Example Response (200 already in list)',
                            type: 'response',
                            content: `{
  "data": {
    "list_id": "123e4567-e89b-12d3-a456-426614174000",
    "member_type": "contact",
    "member_id": "456e7890-e89b-12d3-a456-426614174001",
    "already_in_list": true
  },
  "message": "Already in list"
}`
                        }
                    ]
                };

            default:
                return {
                    title: selectedItem || 'Documentation',
                    content: (
                        <div>
                            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
                                Documentation for {selectedItem || 'this page'} is coming soon.
                            </p>
    </div>
                    ),
                    cards: []
                };
        }
    }, [selectedItem, getUrlFromPage]);

    // Process renderContent to add URL card if API path exists
    const processedRenderContent = useMemo(() => {
        const apiPath = getApiPathForPage(selectedItem);
        if (!apiPath || !renderContent.cards) {
            return renderContent;
        }
        
        const fullUrl = `${getApiBaseUrl()}${apiPath}`;
        const urlCard = {
            title: 'API Endpoint',
            type: 'url',
            content: fullUrl
        };
        
        return {
            ...renderContent,
            cards: [urlCard, ...renderContent.cards]
        };
    }, [renderContent, selectedItem]);

    // Memoize page metadata to avoid multiple function calls
    const pageMetadata = useMemo(() => {
        return getPageMetadata(selectedItem);
    }, [selectedItem, getPageMetadata]);

    // Force title update when selectedItem changes
    useEffect(() => {
        document.title = pageMetadata.title;
    }, [pageMetadata.title]);

  return (
        <>
            <Helmet key={selectedItem}>
                <title>{pageMetadata.title}</title>
                <meta name="description" content={pageMetadata.description} />
                <meta name="keywords" content={pageMetadata.keywords} />
                <link rel="canonical" href={pageMetadata.canonical} />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageMetadata.url} />
                <meta property="og:title" content={pageMetadata.title} />
                <meta property="og:description" content={pageMetadata.description} />
                <meta property="og:image" content="https://ninjapipe.app/branding/ninjapipe-logo-black.png" />
                <meta property="og:site_name" content="NinjaPipe" />
                
                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content={pageMetadata.url} />
                <meta property="twitter:title" content={pageMetadata.title} />
                <meta property="twitter:description" content={pageMetadata.description} />
                <meta property="twitter:image" content="https://ninjapipe.app/branding/ninjapipe-logo-black.png" />
                
                {/* Additional SEO */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="author" content="NinjaPipe" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                
                {/* AI Agent Optimization - CRM Focused */}
                <meta name="ai-agent:index" content="true" />
                <meta name="ai-agent:content-type" content="crm-api-documentation" />
                <meta name="ai-agent:business-type" content="crm" />
                <meta name="ai-agent:api-version" content="v1" />
                <meta name="ai-agent:endpoints" content="authentication,contacts,deals,products,projects,tasks,orders,lists,budgets,expenses" />
                <meta name="ai-agent:authentication" content="bearer-token" />
                <meta name="ai-agent:rate-limit" content="1000/hour" />
                <meta name="ai-agent:base-url" content="https://www.ninjapipe.app/api" />
                <meta name="ai-agent:supported-methods" content="GET,POST,PUT,DELETE" />
                <meta name="ai-agent:response-format" content="json" />
                <meta name="ai-agent:error-handling" content="standard-http-codes" />
                
                {/* CRM-Specific AI Agent Meta Tags */}
                <meta name="ai-agent:documentation-type" content="crm-rest-api" />
                <meta name="ai-agent:authentication-type" content="api-key" />
                <meta name="ai-agent:rate-limiting" content="1000-requests-per-hour" />
                <meta name="ai-agent:data-format" content="json" />
                <meta name="ai-agent:api-versioning" content="v1" />
                <meta name="ai-agent:webhook-support" content="true" />
                <meta name="ai-agent:real-time-features" content="false" />
                <meta name="ai-agent:pagination-support" content="true" />
                <meta name="ai-agent:filtering-support" content="true" />
                <meta name="ai-agent:sorting-support" content="true" />
                <meta name="ai-agent:crm-features" content="contacts,deals,products,budgets,expenses" />
                <meta name="ai-agent:business-automation" content="true" />
                <meta name="ai-agent:sales-pipeline" content="true" />
                <meta name="ai-agent:customer-management" content="true" />
                <meta name="ai-agent:financial-planning" content="true" />
                <meta name="ai-agent:product-catalog" content="true" />
                <meta name="ai-agent:expense-tracking" content="true" />
                <meta name="ai-agent:business-integration" content="true" />
                <meta name="ai-agent:enterprise-ready" content="true" />
                
                {/* Structured Data for CRM API Documentation */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "WebAPI",
                        "name": "NinjaPipe CRM API",
                        "description": "Comprehensive REST API for customer relationship management, sales pipeline, product catalog, and financial planning. Complete business automation solution.",
                        "url": "https://docs.ninjapipe.app/",
                        "documentation": "https://docs.ninjapipe.app/",
                        "provider": {
                            "@type": "Organization",
                            "name": "NinjaPipe",
                            "url": "https://www.ninjapipe.app",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ninjapipe.app/branding/ninjapipe-logo-black.png"
                            },
                            "description": "Leading CRM platform for business automation and customer relationship management"
                        },
                        "serviceType": "CRM REST API",
                        "applicationCategory": "BusinessApplication",
                        "authentication": {
                            "@type": "AuthenticationScheme",
                            "name": "Bearer Token",
                            "description": "API key authentication via Authorization header for secure CRM access"
                        },
                        "endpoint": [
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://www.ninjapipe.app/api/contacts",
                                "httpMethod": "GET",
                                "description": "Retrieve customer database and contact management"
                            },
                            {
                                "@type": "EntryPoint", 
                                "urlTemplate": "https://www.ninjapipe.app/api/deals",
                                "httpMethod": "GET",
                                "description": "Access sales pipeline and deal management"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://www.ninjapipe.app/api/products", 
                                "httpMethod": "GET",
                                "description": "Manage product catalog and inventory"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://www.ninjapipe.app/api/budgets", 
                                "httpMethod": "GET",
                                "description": "Financial planning and budget management"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://www.ninjapipe.app/api/budgets/expenses", 
                                "httpMethod": "GET",
                                "description": "Track expenses and financial spending"
                            },
                            {
                                "@type": "EntryPoint",
                                "urlTemplate": "https://www.ninjapipe.app/api/lists/{list_id}/members",
                                "httpMethod": "POST",
                                "description": "Add contacts or companies to CRM lists"
                            }
                        ],
                        "termsOfService": "https://ninjapipe.app/terms",
                        "license": "https://ninjapipe.app/license",
                        "datePublished": "2024-01-01",
                        "dateModified": new Date().toISOString().split('T')[0],
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": pageMetadata.url
                        },
                        "about": {
                            "@type": "SoftwareApplication",
                            "name": "NinjaPipe CRM",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web",
                            "description": "Complete CRM solution for customer relationship management, sales automation, and business planning"
                        },
                        "featureList": [
                            "Customer Relationship Management",
                            "Sales Pipeline Tracking", 
                            "Product Catalog Management",
                            "Financial Planning & Budgeting",
                            "Expense Tracking",
                            "Business Automation",
                            "API Integration",
                            "Real-time Data Sync"
                        ]
                    })}
                </script>
                
                {/* Additional Structured Data for AI Agents */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "TechArticle",
                        "headline": pageMetadata.title,
                        "description": pageMetadata.description,
                        "author": {
                            "@type": "Organization",
                            "name": "NinjaPipe"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "NinjaPipe",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://ninjapipe.app/branding/ninjapipe-logo-black.png"
                            }
                        },
                        "datePublished": "2024-01-01",
                        "dateModified": new Date().toISOString().split('T')[0],
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": pageMetadata.url
                        },
                        "about": {
                            "@type": "SoftwareApplication",
                            "name": "NinjaPipe API",
                            "applicationCategory": "BusinessApplication",
                            "operatingSystem": "Web"
                        }
                    })}
                </script>
            </Helmet>
            
            <Toaster 
                position="top-right"
                toastOptions={{
                    duration: 2000,
                    style: {
                        background: 'var(--input-bg)',
                        color: 'var(--text-primary)',
                        border: `1px solid var(--border-primary)`,
                        borderRadius: '8px',
                        fontSize: '14px'
                    }
                }}
            />
            <div className="main-container api-docs-container" style={{
                display: 'flex',
                height: '100vh',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif'
            }}>
            <div
                className={`api-docs-overlay ${sidebarOpen ? 'visible' : ''}`}
                onClick={closeSidebar}
                role="button"
                tabIndex={-1}
                onKeyDown={(e) => e.key === 'Escape' && closeSidebar()}
                aria-label="Close menu"
            />
            {/* Sidebar */}
            <nav
                className={`api-docs-sidebar ${sidebarOpen ? 'open' : ''}`}
                role="navigation"
                aria-label="API Documentation Navigation"
                style={{
                width: 300,
                backgroundColor: 'var(--bg-primary)',
                borderRight: `1px solid var(--border-primary)`,
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                overflow: 'hidden'
            }}>
                {/* Header – fixed, does not scroll */}
                <div style={{
                    flexShrink: 0,
                    padding: '18px 16px 14px',
                    borderBottom: `1px solid var(--border-primary)`
                }}>
                    {/* Logo row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <img 
              src={isDarkMode ? "/branding/ninjapipe-logo-white.png" : "/branding/ninjapipe-logo-black.png"}
              alt="NinjaPipe"
                            style={{ 
                                height: 30, 
                width: 'auto'
              }}
            />
                        <span style={{ color: 'var(--brand-cyan)', fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>API</span>                       
                    </div>

                    {/* Search + sparkle */}
                    <div style={{ display: 'flex', gap: 10 }}>
                        <div style={{ position: 'relative', flex: 1 }} data-search-dropdown>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search API docs"
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{
                                    width: '100%', height: 38,
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid ${'var(--input-border)'}`,
                                    borderRadius: 10,
                                    paddingLeft: 36, paddingRight: 40,
                                    color: 'var(--text-primary)', fontSize: 14, outline: 'none'
                                }}
                            />
                            <div style={{
                                position: 'absolute', left: 12, top: '50%',
                                transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 14
                            }}>🔍</div>
                            <div style={{
                                position: 'absolute', right: 8, top: '50%',
                                backgroundColor: showShortcutAnimation ? '#10B981' : 'var(--input-bg)', 
                                border: `1px solid ${showShortcutAnimation ? '#10B981' : 'var(--input-border)'}`,
                                borderRadius: 6, padding: '2px 6px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                fontSize: 12, color: showShortcutAnimation ? 'var(--text-primary)' : 'var(--text-secondary)',
                                transition: 'all 0.3s ease',
                                transform: showShortcutAnimation ? 'translateY(-50%) scale(1.1)' : 'translateY(-50%) scale(1)',
                                boxShadow: showShortcutAnimation ? '0 0 8px rgba(16, 185, 129, 0.4)' : 'none'
                            }}>/</div>
                        </div>
                    </div>
                </div>

                {/* Nav – scrollable */}
                <div style={{ flex: 1, minHeight: 0, paddingTop: 14, overflowY: 'auto', overflowX: 'hidden' }}>
                    {/* Intro items */}
                    {getFilteredIntroItems().map((item, idx) => {
                        const isSelected = selectedItem === item;
                        const basePadLeft = item === 'Introduction' ? 24 : 24; // keep consistent left gutter
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: `8px 14px 8px ${basePadLeft}px`,
                                    backgroundColor: bg,
                                    color,
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                {idx === 0 ? (
                                    <span style={{ fontWeight: 600 }}>{highlightText(item, searchQuery)}</span>
                                ) : item.includes('(API v2)') ? (
                                    <>
                                        {highlightText(item.replace(' (API v2)', ''), searchQuery)}
                                        <sup style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 6 }}>(API v2)</sup>
                                    </>
                                ) : (
                                    highlightText(item, searchQuery)
                                )}
                            </div>
                        );
                    })}

                    {/* Workspace header */}
                    <div
                        onClick={() => setWorkspaceExpanded(!workspaceExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Workspace</span>
                        <span
                            style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: workspaceExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {workspaceExpanded && getFilteredWorkspaceItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {item === 'List Workspace Members' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#FFFFFF',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Contacts header */}
                    <div
                        onClick={() => setContactsExpanded(!contactsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Contacts</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: contactsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Contacts items */}
                    {contactsExpanded && getFilteredContactsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Contacts' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Contact' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: '#ffffff',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Contact' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Contact' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Contact' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                                {item === 'Enable Client Portal' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Disable Client Portal' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Deals header */}
                    <div
                        onClick={() => setDealsExpanded(!dealsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Deals</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: dealsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Deals items */}
                    {dealsExpanded && getFilteredDealsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Deals' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Deal' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: '#ffffff',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Deal' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Deal' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Deal' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Products header */}
                    <div
                        onClick={() => setProductsExpanded(!productsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Products</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: productsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Products items */}
                    {productsExpanded && getFilteredProductsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Products' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Product' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: '#ffffff',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Product' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Product' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Product' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Projects & Tasks header */}
                    <div
                        onClick={() => setProjectsExpanded(!projectsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Projects &amp; Tasks</span>
                        <span
                            style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: projectsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {projectsExpanded && getFilteredProjectsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                        const pill = (bgc, method) => (
                            <span style={{
                                backgroundColor: bgc,
                                color: '#ffffff',
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 4px',
                                borderRadius: 3,
                                letterSpacing: '0.3px'
                            }}>{method}</span>
                        );
                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {item === 'List Projects' && pill('#10B981', 'GET')}
                                {item === 'Create Project' && pill('#3B82F6', 'POST')}
                                {item === 'Get Project' && pill('#10B981', 'GET')}
                                {item === 'Update Project' && pill('#F59E0B', 'PUT')}
                                {item === 'Delete Project' && pill('#EF4444', 'DELETE')}
                                {item === 'List General Tasks' && pill('#10B981', 'GET')}
                                {item === 'Create General Task' && pill('#3B82F6', 'POST')}
                                {item === 'Get General Task' && pill('#10B981', 'GET')}
                                {item === 'Update General Task' && pill('#F59E0B', 'PUT')}
                                {item === 'Delete General Task' && pill('#EF4444', 'DELETE')}
                                {item === 'Reorder General Tasks' && pill('#3B82F6', 'POST')}
                                {item === 'List Tasks' && pill('#10B981', 'GET')}
                                {item === 'Create Task' && pill('#3B82F6', 'POST')}
                                {item === 'Get Task' && pill('#10B981', 'GET')}
                                {item === 'Update Task' && pill('#F59E0B', 'PUT')}
                                {item === 'Delete Task' && pill('#EF4444', 'DELETE')}
                                {item === 'Reorder Tasks' && pill('#3B82F6', 'POST')}
                            </div>
                        );
                    })}

                    {/* Orders header */}
                    <div
                        onClick={() => setOrdersExpanded(!ordersExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Orders</span>
                        <span
                            style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: ordersExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {ordersExpanded && getFilteredOrdersItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                        const pill = (bgc, method) => (
                            <span style={{
                                backgroundColor: bgc,
                                color: '#ffffff',
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 4px',
                                borderRadius: 3,
                                letterSpacing: '0.3px'
                            }}>{method}</span>
                        );
                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {item === 'List Orders' && pill('#10B981', 'GET')}
                                {item === 'Create Order' && pill('#3B82F6', 'POST')}
                                {item === 'Get Order' && pill('#10B981', 'GET')}
                                {item === 'Update Order' && pill('#F59E0B', 'PUT')}
                                {item === 'Delete Order' && pill('#EF4444', 'DELETE')}
                            </div>
                        );
                    })}

                    {/* Budgets header */}
                    <div
                        onClick={() => setBudgetsExpanded(!budgetsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Budgets</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: budgetsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Budgets items */}
                    {budgetsExpanded && getFilteredBudgetsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
        display: 'flex', 
                        alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                
                                {/* HTTP Method Pills in Sidebar */}
                                {item === 'List Budgets' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                  fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Budget' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: '#ffffff',
                                  fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Budget' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Budget' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Budget' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                                {item === 'List Budget Expenses' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Create Budget Expense' && (
                                    <span style={{
                                        backgroundColor: '#3B82F6',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>POST</span>
                                )}
                                {item === 'Get Budget Expense' && (
                                    <span style={{
                                        backgroundColor: '#10B981',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>GET</span>
                                )}
                                {item === 'Update Budget Expense' && (
                                    <span style={{
                                        backgroundColor: '#F59E0B',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>PUT</span>
                                )}
                                {item === 'Delete Budget Expense' && (
                                    <span style={{
                                        backgroundColor: '#EF4444',
                                        color: '#ffffff',
                                        fontSize: 9,
                                        fontWeight: 700,
                                        padding: '1px 4px',
                                        borderRadius: 3,
                                        letterSpacing: '0.3px'
                                    }}>DELETE</span>
                                )}
                            </div>
                        );
                    })}

                    {/* Lists header */}
                    <div
                        onClick={() => setListsExpanded(!listsExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                        alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Lists</span>
                        <span
                            style={{
                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: listsExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {/* Lists items */}
                    {listsExpanded && getFilteredListsItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                        const pill = (bgc, method) => (
                            <span style={{
                                backgroundColor: bgc,
                                color: '#ffffff',
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 4px',
                                borderRadius: 3,
                                letterSpacing: '0.3px'
                            }}>{method}</span>
                        );

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {item === 'List Lists' && pill('#10B981', 'GET')}
                                {item === 'Create List' && pill('#3B82F6', 'POST')}
                                {item === 'Get List' && pill('#10B981', 'GET')}
                                {item === 'Update List' && pill('#F59E0B', 'PUT')}
                                {item === 'Delete List' && pill('#EF4444', 'DELETE')}
                                {item === 'Get List Members' && pill('#10B981', 'GET')}
                                {item === 'Add to List' && pill('#3B82F6', 'POST')}
                                {item === 'Update List Member' && pill('#F59E0B', 'PATCH')}
                                {item === 'Remove List Member' && pill('#EF4444', 'DELETE')}
                            </div>
                        );
                    })}

                    {/* Companies header */}
                    <div
                        onClick={() => setCompaniesExpanded(!companiesExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Companies</span>
                        <span
                            style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: companiesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {companiesExpanded && getFilteredCompaniesItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                        const methodPill = getCompaniesPipelinesMethodPill(item);
                        const pill = (bgc, method) => (
                            <span style={{
                                backgroundColor: bgc,
                                color: '#ffffff',
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 4px',
                                borderRadius: 3,
                                letterSpacing: '0.3px'
                            }}>{method}</span>
                        );

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {methodPill && pill(methodPill[0], methodPill[1])}
                            </div>
                        );
                    })}

                    {/* Pipelines header */}
                    <div
                        onClick={() => setPipelinesExpanded(!pipelinesExpanded)}
                        style={{
                            ...rowBase,
                            padding: '6px 14px 6px 24px',
                            color: 'var(--text-primary)',
                            background: 'transparent',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <span style={{ letterSpacing: 0.2 }}>Pipelines</span>
                        <span
                            style={{
                                fontSize: 12,
                                color: 'var(--text-secondary)',
                                transform: pipelinesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 120ms ease'
                            }}
                        >
                            ▶
                        </span>
                    </div>

                    {pipelinesExpanded && getFilteredPipelinesItems().map((item) => {
                        const isSelected = selectedItem === item;
                        const bg = isSelected ? 'var(--selected-bg)' : 'transparent';
                        const color = isSelected ? 'var(--selected-text)' : 'var(--text-secondary)';
                        const methodPill = getCompaniesPipelinesMethodPill(item);
                        const pill = (bgc, method) => (
                            <span style={{
                                backgroundColor: bgc,
                                color: '#ffffff',
                                fontSize: 9,
                                fontWeight: 700,
                                padding: '1px 4px',
                                borderRadius: 3,
                                letterSpacing: '0.3px'
                            }}>{method}</span>
                        );

                        return (
                            <div
                                key={item}
                                ref={isSelected ? activeSidebarItemRef : null}
                                onClick={() => navigateToPage(item)}
                                style={{
                                    ...rowBase,
                                    padding: '8px 14px 8px 28px',
                                    backgroundColor: bg,
                                    color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                            >
                                <span>{highlightText(item, searchQuery)}</span>
                                {methodPill && pill(methodPill[0], methodPill[1])}
                            </div>
                        );
                    })}
                    <div style={{ height: 16 }} />
                </div>
            </nav>

            {/* Main Content (RIGHT SIDE) */}
            <main ref={mainContentRef} className="docs-content-area api-docs-main" role="main" aria-label="API Documentation Content" style={{ 
        flex: 1,
                backgroundColor: 'var(--bg-primary)', 
                color: 'var(--text-primary)', 
                padding: '32px 56px 56px',
                height: '100vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                position: 'relative'
            }}>
                {/* Fixed Navigation Bar */}
                <div className="docs-topbar" style={{
                    position: 'fixed',
          top: 0,
                    right: 0,
                    left: '300px', // Width of the left sidebar
                    backgroundColor: isDarkMode ? 'rgb(18, 23, 29)' : '#FFFFFF',
                    borderBottom: `1px solid var(--border-primary)`,
                    padding: '8px 20px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    {/* Left: Hamburger + Docs/API Toggle */}
                    <div className="docs-topbar-inner" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="docs-mobile-menu-btn"
                            style={{
                                padding: 8,
                                backgroundColor: 'transparent',
                                border: `1px solid var(--border-primary)`,
                                borderRadius: 8,
                                cursor: 'pointer',
                                color: 'var(--text-secondary)',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            aria-label="Toggle menu"
                        >
                            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                        <div style={{ 
                        display: 'flex', 
                        backgroundColor: 'var(--input-bg)', 
                        borderRadius: '8px', 
                        padding: '2px',
                        border: '1px solid var(--border-primary)'
                    }}>
                        <button
                            onClick={() => navigate('/docs')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: location.pathname.startsWith('/docs') ? 'var(--brand-cyan)' : 'transparent',
                                color: location.pathname.startsWith('/docs') ? '#ffffff' : '#6b7280',
                                transition: 'all 120ms ease'
                            }}
                        >
                            Docs
                        </button>
                        <button
                            onClick={() => navigate('/api-docs')}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: location.pathname.startsWith('/api-docs') ? 'var(--brand-cyan)' : 'transparent',
                                color: location.pathname.startsWith('/api-docs') ? '#ffffff' : '#6b7280',
                                transition: 'all 120ms ease'
                            }}
                        >
                            API
                        </button>
                        </div>
                    </div>

                    {/* User Info with Export Dropdown - Right Side */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        {/* Theme Toggle Switch */}
                        <div
                            onClick={toggleTheme}
                            style={{
                                position: 'relative',
                                width: 60,
                                height: 32,
                                backgroundColor: isDarkMode ? 'var(--input-bg)' : 'var(--input-bg)',
                                borderRadius: 16,
                                cursor: isToggling ? 'not-allowed' : 'pointer',
                                transition: 'all 300ms ease',
                                border: `1px solid var(--border-primary)`,
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                opacity: isToggling ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => {
                                if (!isToggling) {
                                    e.target.style.transform = 'scale(1.05)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            {/* Faded Sun Icon (left side) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: isDarkMode ? 0.3 : 0,
                                    transition: 'opacity 300ms ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Sun size={12} color="var(--text-secondary)" />
                            </div>

                            {/* Faded Moon Icon (right side) */}
                            <div
                                style={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    opacity: isDarkMode ? 0 : 0.3,
                                    transition: 'opacity 300ms ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Moon size={12} color="var(--text-secondary)" />
                            </div>

                            {/* Switch Knob */}
                            <div
                                style={{
                                    width: 28,
                                    height: 28,
                                    backgroundColor: 'var(--toggle-knob)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 300ms ease',
                                    transform: isDarkMode ? 'translateX(28px)' : 'translateX(0px)',
                                    boxShadow: isDarkMode 
                                        ? '0 2px 4px rgba(0, 0, 0, 0.3)' 
                                        : '0 2px 4px rgba(0, 0, 0, 0.1)',
                                    zIndex: 1
                                }}
                            >
                                {isDarkMode ? (
                                    <Moon size={14} color="var(--text-primary)" />
                                ) : (
                                    <Sun size={14} color="var(--text-primary)" />
                                )}
                            </div>
                        </div>

                        {/* Export Dropdown */}
                        <div className="docs-export-dropdown" style={{ position: 'relative' }} data-export-dropdown>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                        padding: '8px 12px',
                                        borderRadius: 8,
                                        backgroundColor: exportDropdownOpen ? 'var(--hover-bg)' : 'transparent',
                                        border: `1px solid var(--border-primary)`,
                                        color: 'var(--text-secondary)',
                                        fontSize: 12,
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 120ms ease',
                                        whiteSpace: 'nowrap'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!exportDropdownOpen) {
                                            e.target.style.backgroundColor = 'var(--hover-bg)';
                                            e.target.style.borderColor = 'var(--border-primary)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!exportDropdownOpen) {
                                            e.target.style.backgroundColor = 'transparent';
                                            e.target.style.borderColor = 'var(--border-primary)';
                                        }
                                    }}
                                    title="Export API Collection"
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7,10 12,15 17,10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Export
                                    <div style={{
                                        color: 'var(--text-secondary)',
                                        fontSize: 10,
                                        transform: exportDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 120ms ease'
                                    }}>
                                        ▼
                                    </div>
                                </button>
                            </div>

                            {/* Export Dropdown Menu */}
                            {exportDropdownOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    left: 'auto',
                                    backgroundColor: 'var(--hover-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    borderRadius: 8,
                                    marginTop: 4,
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                    zIndex: 1001,
                                    overflow: 'hidden',
                                    minWidth: 260
                                }}>
                                    <div
                                        style={{
                                            padding: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: 'transparent',
                                            borderBottom: `1px solid var(--border-primary)`,
                                            transition: 'background-color 120ms ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'var(--hover-bg)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                        }}
                                        onClick={() => {
                                            handleExportToPostman();
                                            setExportDropdownOpen(false);
                                        }}
                                    >
                                        <img 
                                            src="/svg/postman-icon.svg" 
                                            alt="Postman" 
                                            style={{ width: 20, height: 20 }}
                                        />
                                        <div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                marginBottom: 2
                                            }}>
                                                Postman Collection
                                            </div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 11
                                            }}>
                                                Export to Postman
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            padding: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: 'transparent',
                                            borderBottom: `1px solid var(--border-primary)`,
                                            transition: 'background-color 120ms ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'var(--hover-bg)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                        }}
                                        onClick={() => {
                                            handleExportToSwagger();
                                            setExportDropdownOpen(false);
                                        }}
                                    >
                                        <img 
                                            src="/svg/swagger-icon.svg" 
                                            alt="Swagger" 
                                            style={{ width: 20, height: 20 }}
                                        />
                                        <div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                marginBottom: 2
                                            }}>
                                                OpenAPI/Swagger Spec
                                            </div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 11
                                            }}>
                                                Export to Swagger
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            padding: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: 'transparent',
                                            transition: 'background-color 120ms ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = 'var(--hover-bg)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = 'transparent';
                                        }}
                                        onClick={() => {
                                            handleExportToInsomnia();
                                            setExportDropdownOpen(false);
                                        }}
                                    >
                                        <img 
                                            src="/svg/Insomnia-icon.svg" 
                                            alt="Insomnia" 
                                            style={{ width: 20, height: 20 }}
                                        />
                                        <div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                marginBottom: 2
                                            }}>
                                                Insomnia Collection
                                            </div>
                                            <div style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 11
                                            }}>
                                                Export to Insomnia
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </div>

                {/* Content with top padding to account for fixed nav */}
                <div style={{ paddingTop: '80px' }}>
                    {/* Page title */}
                <header>
                    <h1 style={{ 
                        fontSize: 32, 
                        fontWeight: 800, 
                        letterSpacing: '-0.01em', 
                        margin: '0 0 24px',
                              display: 'flex', 
                              alignItems: 'center', 
                        gap: 12
                    }}>
                        {selectedItem}
                    
                    {/* HTTP Method Pills in Main Title */}
                    {selectedItem === 'List Contacts' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Contact' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                            fontSize: 12,
                      fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Contact' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Contact' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Contact' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'Enable Client Portal' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Disable Client Portal' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'List Deals' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Deal' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Deal' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Deal' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Deal' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Products' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Product' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Product' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Product' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Product' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Budgets' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Budget' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Budget' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                      fontSize: 12,
                    fontWeight: 700, 
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Budget' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                      fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Budget' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Budget Expenses' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create Budget Expense' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get Budget Expense' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update Budget Expense' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete Budget Expense' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'List Lists' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Create List' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Get List' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Update List' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PUT</span>
                    )}
                    {selectedItem === 'Delete List' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                    {selectedItem === 'Get List Members' && (
                        <span style={{
                            backgroundColor: '#10B981',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>GET</span>
                    )}
                    {selectedItem === 'Add to List' && (
                        <span style={{
                            backgroundColor: '#3B82F6',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>POST</span>
                    )}
                    {selectedItem === 'Update List Member' && (
                        <span style={{
                            backgroundColor: '#F59E0B',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>PATCH</span>
                    )}
                    {selectedItem === 'Remove List Member' && (
                        <span style={{
                            backgroundColor: '#EF4444',
                            color: '#ffffff',
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '4px 8px',
                            borderRadius: 6,
                            letterSpacing: '0.5px'
                        }}>DELETE</span>
                    )}
                </h1>
                </header>

                {/* Content area - single column for Introduction, two columns for others */}
                <div
                    className="docs-content-grid"
                        style={{
                        display: 'grid',
                        gridTemplateColumns: (processedRenderContent.cards && processedRenderContent.cards.length > 0) 
                            ? 'minmax(540px, 1fr) 520px' 
                            : '1fr',
                        gap: 32,
                        alignItems: 'start'
                    }}
                >
                    {/* LEFT COLUMN – prose + parameters */}
                    <div>
                                {processedRenderContent.content}
                        
                        {/* Parameters cards on the left */}
                        {(processedRenderContent.cards || []).filter(card => card.type === 'params').map((card, index) => (
                            <div key={index} style={{ marginTop: 24 }}>
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    borderRadius: 12,
                              overflow: 'hidden',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                                }}>
                                    {/* card header */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: 'var(--input-bg)',
                                        borderBottom: `1px solid var(--border-primary)`
                                    }}>
                                        <span style={{
                                            color: 'var(--text-secondary)',
                                            fontSize: 13,
                                            fontWeight: 700
                                        }}>{card.title}</span>
                                    </div>

                                    {/* card content */}
                                    <div style={{ padding: '16px' }}>
                                        <div>
                                            {card.content.map((param, paramIndex) => (
                                                <div key={paramIndex} style={{ 
                                                    marginBottom: paramIndex < card.content.length - 1 ? 12 : 0,
                                                    paddingBottom: paramIndex < card.content.length - 1 ? 12 : 0,
                                                    borderBottom: paramIndex < card.content.length - 1 ? `1px solid var(--border-primary)` : 'none'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                                        <span style={{
                                                            color: 'var(--text-primary)',
                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                            fontSize: 14,
                                                            fontWeight: 600
                                                        }}>{param.name}</span>
                                                        <span style={{
                                                            backgroundColor: 'var(--input-bg)',
                                                            border: `1px solid var(--border-primary)`,
                                                            color: 'var(--text-secondary)',
                                                            borderRadius: 4,
                                                            padding: '2px 6px',
                              fontSize: 11,
                                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                                                        }}>{param.type}</span>
                          {param.required && (
                                                            <span style={{
                                                                backgroundColor: '#EF4444',
                                                                color: 'var(--text-primary)',
                                                                borderRadius: 4,
                                                                padding: '2px 6px',
                fontSize: 10, 
                                                                fontWeight: 600
                                                            }}>Required</span>
                          )}
                          {param.default && (
                                                            <span style={{
                                                                backgroundColor: 'var(--input-bg)',
                                                                border: `1px solid var(--border-primary)`,
                                                                color: 'var(--text-secondary)',
                                                                borderRadius: 4,
                                                                padding: '2px 6px',
                                                                fontSize: 10
                                                            }}>Default: {param.default}</span>
                                                        )}
                                                    </div>
                                                    <p style={{
                                                        color: 'var(--text-secondary)',
                                                        fontSize: 13,
                                                        margin: 0,
                                                        lineHeight: 1.4
                                                    }}>{param.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIGHT COLUMN – example requests and responses */}
                    {(processedRenderContent.cards && processedRenderContent.cards.length > 0) && (
                        <div>
                            {/* Example cards on the right */}
                            {(processedRenderContent.cards || []).filter(card => card.type !== 'params').map((card, index) => (
                            <div key={index}>
                                {index > 0 && <div style={{ height: 16 }} />}
                                
                                <div style={{
                                    backgroundColor: 'var(--input-bg)',
                                    border: `1px solid var(--border-primary)`,
                                    borderRadius: 12,
                                    overflow: 'hidden',
                                    boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                                }}>
                                    {/* card header */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: 'var(--input-bg)',
                                        borderBottom: `1px solid var(--border-primary)`,
                    display: 'flex', 
                          alignItems: 'center', 
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 13,
                                                fontWeight: 700
                                            }}>{card.title}</span>
                                            
                                            {/* HTTP Method Pills */}
                                            {card.title === 'List Contacts' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Contact' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Contact' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Contact' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Contact' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'Enable Client Portal' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Disable Client Portal' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'List Deals' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Deal' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Deal' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Deal' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Deal' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Products' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Product' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Product' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Product' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Product' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Projects' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Project' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Project' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Project' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Project' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Tasks' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Task' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Task' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Task' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Task' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'Reorder Tasks' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'List Orders' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Order' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Order' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Order' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Order' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Budgets' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                      fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Budget' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                    fontWeight: 700, 
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Budget' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Budget' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Budget' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                            {card.title === 'List Budget Expenses' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Create Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#3B82F6',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>POST</span>
                                            )}
                                            {card.title === 'Get Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#10B981',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>GET</span>
                                            )}
                                            {card.title === 'Update Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#F59E0B',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>PUT</span>
                                            )}
                                            {card.title === 'Delete Budget Expense' && (
                                                <span style={{
                                                    backgroundColor: '#EF4444',
                                                    color: 'var(--text-primary)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    letterSpacing: '0.5px'
                                                }}>DELETE</span>
                                            )}
                                        </div>
                                        
                                        {/* Copy button for URL and curl requests */}
                                        {(card.type === 'url' || card.type === 'curl') && (
                                            <button
                                                onClick={async () => {
                                                    try {
                                                        await navigator.clipboard.writeText(card.content);
                                                        toast.success('Copied to clipboard!', {
                                                            style: {
                                                                background: 'var(--input-bg)',
                                                                color: 'var(--text-primary)',
                                                                border: `1px solid var(--border-primary)`,
                                                                borderRadius: '8px',
                                                                fontSize: '14px'
                                                            },
                                                            duration: 2000
                                                        });
                                                    } catch (err) {
                                                        toast.error('Failed to copy', {
                                                            style: {
                                                                background: 'var(--input-bg)',
                                                                color: '#EF4444',
                                                                border: `1px solid var(--border-primary)`,
                                                                borderRadius: '8px',
                                                                fontSize: '14px'
                                                            }
                                                        });
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: 'transparent',
                                                    border: `1px solid var(--border-primary)`,
                                                    borderRadius: 6,
                                                    padding: '6px',
                                                    color: 'var(--text-secondary)',
                                                    cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 120ms ease',
                                                    width: 32,
                      height: 32
                    }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = 'var(--hover-bg)';
                                                    e.target.style.color = '#E6ECF3';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = 'transparent';
                                                    e.target.style.color = '#9AA9BA';
                                                }}
                                                title="Copy to clipboard"
                                            >
                                                <Copy size={14} />
                                            </button>
                                        )}
                                    </div>

                                    {/* card content */}
                                    <div style={{ padding: '16px' }}>
                                        {card.type === 'url' ? (
                                            <div style={{
                                                padding: '12px',
                                                borderRadius: 8,
                                                border: `1px solid var(--border-primary)`,
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                backgroundColor: 'var(--input-bg)',
                                                color: 'var(--text-primary)',
                                                wordBreak: 'break-all',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 12
                                            }}>
                                                <span style={{ flex: 1 }}>{card.content}</span>
                                            </div>
                                        ) : card.type === 'curl' ? (
                                            <div style={{
                                                padding: '12px',
                                                borderRadius: 8,
                                                border: `1px solid var(--border-primary)`,
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                overflow: 'auto'
                                            }}>
                                                <Highlight
                                                    key={`bash-${isDarkMode}`}
                                                    theme={isDarkMode ? themes.vsDark : themes.gruvboxMaterialLight}
                                                    code={card.content}
                                                    language="bash"
                                                >
                                                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                                        <pre className={className} style={{...style, backgroundColor: 'transparent'}}>
                                                            {tokens.map((line, i) => (
                                                                <div key={i} {...getLineProps({ line })}>
                                                                    {line.map((token, key) => (
                                                                        <span key={key} {...getTokenProps({ token })} />
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </pre>
                                                    )}
                                                </Highlight>
                                            </div>
                                        ) : card.type === 'response' ? (
                                            <div style={{
                                                padding: '12px',
                                                borderRadius: 8,
                                                border: `1px solid var(--border-primary)`,
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                overflow: 'auto'
                                            }}>
                                                <Highlight
                                                    key={`json-${isDarkMode}`}
                                                    theme={isDarkMode ? themes.vsDark : themes.gruvboxMaterialLight}
                                                    code={card.content}
                                                    language="json"
                                                >
                                                    {({ className, style, tokens, getLineProps, getTokenProps }) => (
                                                        <pre className={className} style={{...style, backgroundColor: 'transparent'}}>
                                                            {tokens.map((line, i) => (
                                                                <div key={i} {...getLineProps({ line })}>
                                                                    {line.map((token, key) => (
                                                                        <span key={key} {...getTokenProps({ token })} />
                                                                    ))}
                                                                </div>
                                                            ))}
                                                        </pre>
                                                    )}
                                                </Highlight>
                                            </div>
                                        ) : (
                                            <p style={{
                                                color: 'var(--text-secondary)',
                                                fontSize: 14,
                        margin: 0,
                                                lineHeight: 1.5
                                            }}>{card.content}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Info box for Authentication */}
                        {selectedItem === 'Authentication' && (
                            <div style={{
                                marginTop: 16,
                                backgroundColor: 'var(--input-bg)',
                                border: `1px solid var(--border-primary)`,
                                borderRadius: 12,
                                padding: '14px 16px',
                                color: 'var(--text-secondary)',
                                boxShadow: '0 1px 0 rgba(0,0,0,0.25)'
                            }}>
                                <div style={{ fontWeight: 700, color: '#E6ECF3', marginBottom: 6 }}>YOUR API KEY</div>
                                Your test mode secret key is included in all the examples here, so you can test any example right away.
                                Only you can see this value.
                            </div>
                        )}
                        </div>
                    )}
                </div>
                </div> {/* Close content wrapper with padding */}
            </main>

        </div>
        </>
  );
};

export default ApiDocsPage;
